import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useBackButton } from "../hooks/useBackButton";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { format as formatDate } from "date-fns";
import ExportModal from "@/components/ExportModal";
import PatrolVehicles from "@/components/PatrolVehicles";
import WatermarkPresetsCard from "@/components/WatermarkPresetsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import markerRetina from "leaflet/dist/images/marker-icon-2x.png";

const knownLocMarkerIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerRetina,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Click-to-place handler + auto-recenter when a coord is set programmatically.
function KnownLocPinPicker({ value, onChange }) {
  const map = useMap();
  useMapEvents({
    click(e) {
      onChange({ lat: +e.latlng.lat.toFixed(6), lng: +e.latlng.lng.toFixed(6) });
    },
  });
  useEffect(() => {
    if (value?.lat != null && value?.lng != null) {
      map.flyTo([value.lat, value.lng], Math.max(map.getZoom(), 15), { duration: 0.5 });
    }
  }, [value?.lat, value?.lng]); // eslint-disable-line react-hooks/exhaustive-deps
  if (value?.lat == null || value?.lng == null) return null;
  return <Marker position={[value.lat, value.lng]} icon={knownLocMarkerIcon} />;
}
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { UserDevicesSection } from "@/components/UserDevices";
import {
  Settings as SettingsIcon,
  Plus,
  Users,
  FileText,
  Trash2,
  Edit,
  Pencil,
  Save,
  Database,
  X,
  AlertTriangle,
  Search,
  Car,
  Bell,
  RefreshCw,
  Download,
  Send,
  Clock,
  Check,
  History,
  XCircle,
  Lock,
  Mail,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  MessageSquare,
  ClipboardList,
  PhoneIncoming,
  MapPin,
  CreditCard,
  Award,
  Mic
} from "lucide-react";

// An Admin Activity change list without the non-changes. Saving a user sends
// every setting, and older entries logged a never-saved setting as
// "null → false" though nothing changed; null / off / empty are one state.
const isBlankSetting = (v) =>
  v === null || v === undefined || v === false || v === "" ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && v !== null && !Array.isArray(v) && Object.keys(v).length === 0);
const realChanges = (changes) =>
  Object.entries(changes || {}).filter(
    ([, c]) => !(isBlankSetting(c?.from) && isBlankSetting(c?.to)) &&
      JSON.stringify(c?.from) !== JSON.stringify(c?.to)
  );

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const [users, setUsers] = useState([]);
  const [usersSubTab, setUsersSubTab] = useState("users");
  const [patrolSubTab, setPatrolSubTab] = useState("cars");
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewUserDialog, setShowNewUserDialog] = useState(false);
  const [showNewTypeDialog, setShowNewTypeDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showEditTypeDialog, setShowEditTypeDialog] = useState(false);

  const [newUser, setNewUser] = useState({ email: "", name: "", role: "" });
  const [newType, setNewType] = useState({ name: "" });
  const [editUser, setEditUser] = useState({});
  // Edit User → Notifications: will this user's phone actually get
  // notifications? (GET /admin/users/{id}/push-status — admin switch, their
  // master switch, a live device sign-up, last push delivered.) Same as CCHD.
  const [pushStatus, setPushStatus] = useState(null);
  const loadPushStatus = useCallback((uid) => {
    if (!uid) { setPushStatus(null); return; }
    setPushStatus("loading");
    axios.get(`${API}/admin/users/${uid}/push-status`, { withCredentials: true })
      .then((r) => setPushStatus(r.data))
      .catch(() => setPushStatus("error"));
  }, []);
  useEffect(() => { loadPushStatus(editUser?.user_id); }, [editUser?.user_id, loadPushStatus]);
  const [editType, setEditType] = useState({});

  // Data management states
  const [dataCounts, setDataCounts] = useState(null);
  const [adminIncidents, setAdminIncidents] = useState([]);
  const [adminProfiles, setAdminProfiles] = useState([]);
  const [adminLiveCalls, setAdminLiveCalls] = useState([]);
  const [adminUnits, setAdminUnits] = useState([]);
  const [adminMediaFiles, setAdminMediaFiles] = useState([]);
  const [adminBuildingCodes, setAdminBuildingCodes] = useState([]);
  const [adminRollCalls, setAdminRollCalls] = useState([]);
  const [adminTodos, setAdminTodos] = useState([]);
  const [adminEventOps, setAdminEventOps] = useState([]);
  const [dataSearchQuery, setDataSearchQuery] = useState("");
  const [dataActiveTab, setDataActiveTab] = useState("incidents");

  // Active Locations state
  const [activeLocations, setActiveLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Live Call Options state
  const [liveCallOptions, setLiveCallOptions] = useState(null);
  const [newFlagLabel, setNewFlagLabel] = useState("");
  const [newFlagType, setNewFlagType] = useState("unit_flags");

  // Custom Radio Codes state
  const [radioCodes, setRadioCodes] = useState({ codes: [] });
  const [newRadioCode, setNewRadioCode] = useState("");

  // Call Final Options state
  const [callFinalOptions, setCallFinalOptions] = useState({ options: [] });
  const [newCallFinal, setNewCallFinal] = useState("");
  // Priority Call Types state
  const [priorityCallTypes, setPriorityCallTypes] = useState([]);

  // Permissions state
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});

  // Units state for user-unit linking
  const [units, setUnits] = useState([]);

  // Available pages
  const [availablePages, setAvailablePages] = useState([]);
  const [pageDisplayNames, setPageDisplayNames] = useState({});

  // Patrol cars state
  const [patrolCars, setPatrolCars] = useState([]);
  const [newCarName, setNewCarName] = useState("");
  const [editCar, setEditCar] = useState(null);
  const [showEditCarDialog, setShowEditCarDialog] = useState(false);
  const [linkDroneVehicles, setLinkDroneVehicles] = useState([]);
  const [linkGeotabDevices, setLinkGeotabDevices] = useState([]);

  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilter, setAuditFilter] = useState({
    event_type: "__all__",
    search: "",
    success: "__all__",
    start_date: "",
    end_date: "",
    user_id: ""
  });
  const [selectedUserAudit, setSelectedUserAudit] = useState(null);
  const [userAuditTrail, setUserAuditTrail] = useState(null);
  
  // IP Blocking state
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [failedAttempts, setFailedAttempts] = useState([]);
  const [auditSubTab, setAuditSubTab] = useState("logs"); // "logs", "blocked", "attempts", "alerts", "downloads", "admin-activity"
  const [adminActivityLogs, setAdminActivityLogs] = useState([]);
  const [adminActivityPage, setAdminActivityPage] = useState(1);
  const [adminActivityTotal, setAdminActivityTotal] = useState(0);
  const [adminActivityLoading, setAdminActivityLoading] = useState(false);
  const [adminActivitySearch, setAdminActivitySearch] = useState("");
  const [adminActivityStartDate, setAdminActivityStartDate] = useState("");
  const [adminActivityEndDate, setAdminActivityEndDate] = useState("");
  const [manualBlockIP, setManualBlockIP] = useState("");
  const [manualBlockReason, setManualBlockReason] = useState("");
  const [manualBlockHours, setManualBlockHours] = useState(24);
  const [manualBlockForever, setManualBlockForever] = useState(false);
  const [blockedEmails, setBlockedEmails] = useState([]);
  const [manualBlockEmail, setManualBlockEmail] = useState("");
  const [manualBlockEmailReason, setManualBlockEmailReason] = useState("");
  const [manualBlockEmailHours, setManualBlockEmailHours] = useState(24);

  // WhatsApp Alerts state
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [whatsappFromNumber, setWhatsappFromNumber] = useState("");
  const [testWhatsappNumber, setTestWhatsappNumber] = useState("");
  const [sendingTestWhatsapp, setSendingTestWhatsapp] = useState(false);
  const [savingWhatsappConfig, setSavingWhatsappConfig] = useState(false);

  // Known Locations (admin-managed pre-saved addresses) state
  const [knownLocations, setKnownLocations] = useState([]);
  const [knownLocationForm, setKnownLocationForm] = useState({ id: null, name: "", address: "", cross_streets: "", location_notes: "", latitude: null, longitude: null });
  const [savingKnownLocation, setSavingKnownLocation] = useState(false);

  // Alert Line (dedicated Twilio → WhatsApp group) state
  const [alertLineEnabled, setAlertLineEnabled] = useState(false);
  const [alertLineGroupJid, setAlertLineGroupJid] = useState("");
  const [alertLineGroupName, setAlertLineGroupName] = useState("");
  const [alertLineTwilioNumber, setAlertLineTwilioNumber] = useState("");
  const [savingAlertLine, setSavingAlertLine] = useState(false);

  // Hotline-to-WhatsApp state (existing hotline number also blasts a group)
  const [hotlineWaEnabled, setHotlineWaEnabled] = useState(false);
  const [hotlineWaGroupJid, setHotlineWaGroupJid] = useState("");
  const [hotlineWaGroupName, setHotlineWaGroupName] = useState("");
  const [hotlineWaNumber, setHotlineWaNumber] = useState("");
  const [savingHotlineWa, setSavingHotlineWa] = useState(false);

  // Deepgram auto-transcription on/off (turn off when out of credit)
  const [deepgramEnabled, setDeepgramEnabled] = useState(true);
  const [savingDeepgram, setSavingDeepgram] = useState(false);

  // WhatsApp Bot state
  const [botStatus, setBotStatus] = useState(null);
  const [botGroups, setBotGroups] = useState([]);
  const [botConfiguredGroups, setBotConfiguredGroups] = useState([]);
  const [botEnabled, setBotEnabled] = useState(false);
  const [savingBotConfig, setSavingBotConfig] = useState(false);
  const [sendingBotTest, setSendingBotTest] = useState(false);
  const [loadingBotGroups, setLoadingBotGroups] = useState(false);
  const [botGroupCollections, setBotGroupCollections] = useState([]); // [{name, jids}]
  const [newCollectionName, setNewCollectionName] = useState("");

  // Groups state
  const [groups, setGroups] = useState([]);
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
  const [showEditGroupDialog, setShowEditGroupDialog] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", members: [] });
  const [editGroup, setEditGroup] = useState(null);

  // Email Alerts state
  const [emailAlertsConfig, setEmailAlertsConfig] = useState(null);
  const [alertEmails, setAlertEmails] = useState([]);
  const [newAlertEmail, setNewAlertEmail] = useState("");
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({});
  const [notificationTypes, setNotificationTypes] = useState({});
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [emailProvider, setEmailProvider] = useState("resend"); // "resend" or "smtp"
  const [resendConfig, setResendConfig] = useState({
    api_key: "",
    from_email: "onboarding@resend.dev",
    from_name: "TARU Alerts"
  });
  const [smtpConfig, setSmtpConfig] = useState({
    host: "",
    port: 465,
    user: "",
    password: "",
    from_email: "",
    from_name: "TARU Security Alerts",
    use_ssl: true
  });
  const [savingEmailConfig, setSavingEmailConfig] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingEmailLogs, setLoadingEmailLogs] = useState(false);
  const [expandedRecipients, setExpandedRecipients] = useState({}); // Track which recipients have expanded prefs

  // Notification Master state
  const [showNotifMasterDialog, setShowNotifMasterDialog] = useState(false);
  const [notifMasterTab, setNotifMasterTab] = useState("send"); // "send", "history"
  const [notifSelectedUsers, setNotifSelectedUsers] = useState([]);
  const [notifTitle, setNotifTitle] = useState("");
  const [notifMessage, setNotifMessage] = useState("");
  const [notifScheduleTime, setNotifScheduleTime] = useState("");
  const [notifHistory, setNotifHistory] = useState([]);
  const [notifHistoryLoading, setNotifHistoryLoading] = useState(false);
  const [notifSending, setNotifSending] = useState(false);
  const [notifSendPush, setNotifSendPush] = useState(true);
  const [notifSendWhatsapp, setNotifSendWhatsapp] = useState(false);
  const [notifSendToWaBotGroups, setNotifSendToWaBotGroups] = useState(false);
  const [notifWaBotGroupJids, setNotifWaBotGroupJids] = useState([]); // empty = all

  // User search & sort
  const [userSearch, setUserSearch] = useState("");
  const [userSort, setUserSort] = useState("recent");
  const [notifHistoryPage, setNotifHistoryPage] = useState(1);
  const [notifHistoryTotal, setNotifHistoryTotal] = useState(0);
  const [notifHistoryPages, setNotifHistoryPages] = useState(0);
  const [notifFilterStartDate, setNotifFilterStartDate] = useState("");
  const [notifFilterEndDate, setNotifFilterEndDate] = useState("");
  const [notifFilterSentBy, setNotifFilterSentBy] = useState("");
  const [notifFilterType, setNotifFilterType] = useState(""); // "immediate" or "scheduled"
  const [notifUserSearch, setNotifUserSearch] = useState("");

  // Exports (moved here from Incidents page)
  const [exportShow, setExportShow] = useState(false);
  const [exportCsv, setExportCsv] = useState("");
  const [exportFilename, setExportFilename] = useState("");
  const [exportDownloadUrl, setExportDownloadUrl] = useState("");
  const [exportLoading, setExportLoading] = useState(null); // 'all' | 'arrests' | 'codes' | null
  const handleExportIncidents = async (arrestOnly = false) => {
    const key = arrestOnly ? "arrests" : "all";
    setExportLoading(key);
    try {
      const params = new URLSearchParams();
      if (arrestOnly) params.append("is_arrest", "true");
      toast.info("Preparing export...");
      const response = await axios.get(`${API}/export/incidents?${params.toString()}`, {
        withCredentials: true,
        responseType: "text",
      });
      if (!response.data || response.data.includes('"message"')) {
        toast.info("No data to export");
        return;
      }
      const filename = `incidents_${arrestOnly ? "arrests_" : ""}${formatDate(new Date(), "yyyy-MM-dd")}.csv`;
      setExportCsv(response.data);
      setExportFilename(filename);
      setExportDownloadUrl(`${API}/download/incidents?${params.toString()}`);
      setExportShow(true);
    } catch (error) {
      toast.error("Export failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setExportLoading(null);
    }
  };
  const handleExportBuildingCodesData = async () => {
    setExportLoading("codes");
    try {
      toast.info("Preparing building codes export...");
      const response = await axios.get(`${API}/building-codes`, { withCredentials: true });
      const buildingCodes = response.data;
      if (!buildingCodes || buildingCodes.length === 0) {
        toast.info("No building codes to export");
        return;
      }
      const headers = ["Address", "Building Code", "Notes", "Created By", "Created At", "Last Updated By", "Last Updated At"];
      const rows = buildingCodes.map((code) => [
        code.address,
        code.building_code,
        code.notes || "",
        code.created_by_name || "",
        code.created_at ? new Date(code.created_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : "",
        code.updated_by_name || "",
        code.updated_at ? new Date(code.updated_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : "",
      ]);
      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")),
      ].join("\n");
      setExportCsv(csvContent);
      setExportFilename(`building-codes-${formatDate(new Date(), "yyyy-MM-dd")}.csv`);
      setExportDownloadUrl("");
      setExportShow(true);
    } catch (error) {
      toast.error("Export failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setExportLoading(null);
    }
  };

  // Media Downloads state
  const [mediaDownloads, setMediaDownloads] = useState([]);
  const [mediaDownloadsLoading, setMediaDownloadsLoading] = useState(false);
  const [mediaDownloadsPage, setMediaDownloadsPage] = useState(1);
  const [mediaDownloadsTotal, setMediaDownloadsTotal] = useState(0);
  const [mediaDownloadsPages, setMediaDownloadsPages] = useState(0);
  const [mediaDownloadsStats, setMediaDownloadsStats] = useState(null);
  const [downloadFilterUser, setDownloadFilterUser] = useState("");
  const [downloadFilterFilename, setDownloadFilterFilename] = useState("");
  const [downloadFilterStartDate, setDownloadFilterStartDate] = useState("");
  const [downloadFilterEndDate, setDownloadFilterEndDate] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  // Handle mobile back button for dialogs
  useBackButton(showNewUserDialog, () => setShowNewUserDialog(false));
  useBackButton(showEditUserDialog, () => setShowEditUserDialog(null));
  useBackButton(showNewTypeDialog, () => setShowNewTypeDialog(false));
  useBackButton(showEditTypeDialog, () => setShowEditTypeDialog(null));
  useBackButton(showEditCarDialog, () => setShowEditCarDialog(null));

  // Load DroneMobile vehicles and Geotab devices when the Edit Car dialog
  // opens, to populate the two link dropdowns. Either may be unconfigured —
  // that just leaves its dropdown empty.
  useEffect(() => {
    if (!showEditCarDialog) return;
    axios.get(`${API}/drone/vehicles`, { withCredentials: true })
      .then((r) => setLinkDroneVehicles(r.data?.vehicles || []))
      .catch(() => setLinkDroneVehicles([]));
    axios.get(`${API}/geotab/devices`, { withCredentials: true })
      .then((r) => setLinkGeotabDevices(r.data?.devices || []))
      .catch(() => setLinkGeotabDevices([]));
  }, [showEditCarDialog]);
  useBackButton(showNotifMasterDialog, () => setShowNotifMasterDialog(false));

  // Check for scheduled notifications periodically when dialog is open on history tab
  useEffect(() => {
    if (!showNotifMasterDialog || notifMasterTab !== "history") return;

    const checkScheduled = async () => {
      try {
        await axios.post(`${API}/push/process-scheduled`, {}, { withCredentials: true });
        const res = await axios.get(`${API}/push/notification-history?page=${notifHistoryPage}&limit=20`, { withCredentials: true });
        setNotifHistory(res.data.items);
        setNotifHistoryTotal(res.data.total);
        setNotifHistoryPages(res.data.pages);
      } catch (error) {
        // Silent fail
      }
    };

    const interval = setInterval(checkScheduled, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [showNotifMasterDialog, notifMasterTab, notifHistoryPage]);

  // Load notification history page with filters
  const loadNotifHistoryPage = async (page, startDate = notifFilterStartDate, endDate = notifFilterEndDate, sentBy = notifFilterSentBy, type = notifFilterType) => {
    setNotifHistoryLoading(true);
    try {
      let url = `${API}/push/notification-history?page=${page}&limit=20`;
      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;
      if (sentBy) url += `&sent_by=${sentBy}`;
      if (type) url += `&notif_type=${type}`;
      const res = await axios.get(url, { withCredentials: true });
      setNotifHistory(res.data.items);
      setNotifHistoryTotal(res.data.total);
      setNotifHistoryPages(res.data.pages);
      setNotifHistoryPage(page);
    } catch (error) {
      toast.error("Failed to load history");
    }
    setNotifHistoryLoading(false);
  };

  // Export notification history to CSV
  const exportNotifHistoryCSV = async () => {
    try {
      let url = `${API}/push/notification-history?page=1&limit=10000`;
      if (notifFilterStartDate) url += `&start_date=${notifFilterStartDate}`;
      if (notifFilterEndDate) url += `&end_date=${notifFilterEndDate}`;
      if (notifFilterSentBy) url += `&sent_by=${notifFilterSentBy}`;
      if (notifFilterType) url += `&notif_type=${notifFilterType}`;
      const res = await axios.get(url, { withCredentials: true });

      if (res.data.items.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Create CSV content
      const headers = ["Date", "Type", "Scheduled For", "Title", "Message", "Sent By", "Recipients", "Status", "Sent Count", "Failed Count"];
      const rows = res.data.items.map(n => {
        const recipientNames = (n.user_ids || []).map(uid => {
          const u = users.find(u => u.user_id === uid);
          return u?.name || uid;
        }).join("; ");
        return [
          `"${new Date(n.created_at).toLocaleString('en-US', { timeZone: 'America/New_York' })}"`,
          n.scheduled_time ? "Scheduled" : "Immediate",
          n.scheduled_time ? `"${new Date(n.scheduled_time).toLocaleString('en-US', { timeZone: 'America/New_York' })}"` : "",
          `"${(n.title || "").replace(/"/g, '""')}"`,
          `"${(n.message || "").replace(/"/g, '""').replace(/\n/g, ' ')}"`,
          `"${n.created_by_name || ""}"`,
          `"${recipientNames.replace(/"/g, '""')}"`,
          n.status || "",
          n.sent_count || 0,
          n.failed_count || 0
        ];
      });

      const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `notification_history_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      toast.success("CSV exported");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, typesRes, liveCallOptRes, unitsRes, radioCodesRes, callFinalsRes, permsRes, patrolCarsRes, pagesRes, priorityTypesRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/incident-types`, { withCredentials: true }),
        axios.get(`${API}/app-config/live-call-options`, { withCredentials: true }),
        axios.get(`${API}/units`, { withCredentials: true }),
        axios.get(`${API}/app-config/radio-codes`, { withCredentials: true }),
        axios.get(`${API}/app-config/call-finals`, { withCredentials: true }).catch(() => ({ data: { options: [] } })),
        axios.get(`${API}/permissions/available`, { withCredentials: true }),
        axios.get(`${API}/patrol/cars/all`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/pages/available`, { withCredentials: true }).catch(() => ({ data: { pages: [], display_names: {} } })),
        axios.get(`${API}/app-config/priority-call-types`, { withCredentials: true }).catch(() => ({ data: { type_names: [] } }))
      ]);
      setUsers(usersRes.data);
      setIncidentTypes([...typesRes.data].sort((a, b) => a.name.localeCompare(b.name)));
      setLiveCallOptions(liveCallOptRes.data);
      setUnits(unitsRes.data);
      setRadioCodes(radioCodesRes.data);
      setCallFinalOptions(callFinalsRes.data);
      setAvailablePermissions(permsRes.data.all_permissions || []);
      setRolePermissions(permsRes.data.role_permissions || {});
      setPatrolCars(patrolCarsRes.data);
      setAvailablePages(pagesRes.data.pages || []);
      setPriorityCallTypes(priorityTypesRes.data.type_names || []);
      setPageDisplayNames(pagesRes.data.display_names || {});
    } catch (error) {
      toast.error("Failed to load settings data");
    } finally {
      setLoading(false);
    }

    // Fetch groups
    try {
      const groupsRes = await axios.get(`${API}/groups`, { withCredentials: true });
      setGroups(groupsRes.data);
    } catch (err) {
      console.error("Failed to fetch groups:", err);
    }
  };

  const handleAddRadioCode = async () => {
    if (!newRadioCode.trim()) return;
    try {
      const response = await axios.post(`${API}/app-config/radio-codes`, {
        label: newRadioCode.trim()
      }, { withCredentials: true });
      setRadioCodes(response.data);
      setNewRadioCode("");
      toast.success("Radio code added");
    } catch (error) {
      toast.error("Failed to add radio code");
    }
  };

  const handleDeleteRadioCode = async (codeId) => {
    try {
      const response = await axios.delete(`${API}/app-config/radio-codes/${codeId}`, { withCredentials: true });
      setRadioCodes(response.data.config);
      toast.success("Radio code removed");
    } catch (error) {
      toast.error("Failed to remove radio code");
    }
  };

  const handleAddCallFinal = async () => {
    if (!newCallFinal.trim()) return;
    try {
      const response = await axios.post(`${API}/app-config/call-finals`, { label: newCallFinal.trim() }, { withCredentials: true });
      setCallFinalOptions(response.data);
      setNewCallFinal("");
      toast.success("Call final option added");
    } catch (error) {
      toast.error("Failed to add call final option");
    }
  };

  const handleDeleteCallFinal = async (optionId) => {
    try {
      const response = await axios.delete(`${API}/app-config/call-finals/${optionId}`, { withCredentials: true });
      setCallFinalOptions(response.data.config);
      toast.success("Call final option removed");
    } catch (error) {
      toast.error("Failed to remove call final option");
    }
  };

  // Patrol car handlers
  const handleAddPatrolCar = async () => {
    if (!newCarName.trim()) return;
    try {
      const response = await axios.post(`${API}/patrol/cars`, {
        name: newCarName.trim()
      }, { withCredentials: true });
      setPatrolCars([...patrolCars, response.data]);
      setNewCarName("");
      toast.success("Patrol car added");
    } catch (error) {
      toast.error("Failed to add patrol car");
    }
  };

  const handleUpdatePatrolCar = async () => {
    if (!editCar?.name?.trim()) return;
    try {
      const response = await axios.put(`${API}/patrol/cars/${editCar.car_id}`, {
        name: editCar.name.trim(),
        drone_device_key: editCar.drone_device_key ?? null,
        drone_vehicle_name: editCar.drone_vehicle_name ?? null,
        geotab_device_id: editCar.geotab_device_id ?? null,
        geotab_device_name: editCar.geotab_device_name ?? null,
      }, { withCredentials: true });
      setPatrolCars(patrolCars.map(c => c.car_id === editCar.car_id ? response.data : c));
      setShowEditCarDialog(false);
      setEditCar(null);
      toast.success("Patrol car updated");
    } catch (error) {
      toast.error("Failed to update patrol car");
    }
  };

  const handleDeletePatrolCar = async (carId) => {
    try {
      await axios.delete(`${API}/patrol/cars/${carId}`, { withCredentials: true });
      setPatrolCars(patrolCars.filter(c => c.car_id !== carId));
      toast.success("Patrol car deactivated");
    } catch (error) {
      toast.error("Failed to delete patrol car");
    }
  };

  const fetchDataManagement = async () => {
    try {
      const [countsRes, incidentsRes, profilesRes, callsRes, unitsRes, codesRes, rollCallsRes, todosRes, eventOpsRes] = await Promise.all([
        axios.get(`${API}/admin/data-counts`, { withCredentials: true }),
        axios.get(`${API}/admin/incidents`, { withCredentials: true }),
        axios.get(`${API}/admin/perpetrator-profiles`, { withCredentials: true }),
        axios.get(`${API}/admin/live-calls`, { withCredentials: true }),
        axios.get(`${API}/units`, { withCredentials: true }),
        axios.get(`${API}/building-codes`, { withCredentials: true }),
        axios.get(`${API}/rollcall/history/list?limit=500`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/patrol/todos`, { withCredentials: true }).catch(() => ({ data: { todos: [] } })),
        axios.get(`${API}/event-ops/events`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      const eventOpsList = eventOpsRes.data.events || eventOpsRes.data || [];
      setDataCounts({...countsRes.data, units_count: unitsRes.data.length, building_codes: codesRes.data.length, roll_calls: rollCallsRes.data.items?.length || 0, event_ops: countsRes.data.event_ops || eventOpsList.length});
      setAdminIncidents(incidentsRes.data);
      setAdminProfiles(profilesRes.data);
      setAdminLiveCalls(callsRes.data);
      setAdminUnits(unitsRes.data);
      setAdminBuildingCodes(codesRes.data);
      setAdminRollCalls(rollCallsRes.data.items || []);
      setAdminTodos(todosRes.data?.todos || []);
      setAdminEventOps(eventOpsList);
    } catch (error) {
      toast.error("Failed to load data management info");
    }
  };

  // ============== Unit Records (ID Cards / Plaques) ==============
  const [unitRecords, setUnitRecords] = useState({ id_cards: [], plaques: [] });
  const [recordsLoading, setRecordsLoading] = useState(false);
  const fetchUnitRecords = async () => {
    setRecordsLoading(true);
    try {
      const res = await axios.get(`${API}/units/records/all`, { withCredentials: true });
      setUnitRecords(res.data || { id_cards: [], plaques: [] });
    } catch {
      setUnitRecords({ id_cards: [], plaques: [] });
    } finally {
      setRecordsLoading(false);
    }
  };
  const handleDeleteCreditCard = async (rec) => {
    if (!window.confirm(`Delete ID card ${rec.card_number || "(no #)"} for ${rec.unit_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/units/${rec.unit_id}/id-cards/${rec.record_id}`, { withCredentials: true });
      toast.success("ID card deleted");
      fetchUnitRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };
  const handleDeletePlaque = async (rec) => {
    if (!window.confirm(`Delete plaque ${rec.plaque_number || "(no #)"} for ${rec.unit_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/units/${rec.unit_id}/plaques/${rec.record_id}`, { withCredentials: true });
      toast.success("Plaque deleted");
      fetchUnitRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  // ============== Known Locations ==============
  const fetchKnownLocations = async () => {
    try {
      const res = await axios.get(`${API}/known-locations`, { withCredentials: true });
      setKnownLocations(res.data?.items || []);
    } catch {
      setKnownLocations([]);
    }
  };

  const handleSaveKnownLocation = async () => {
    const f = knownLocationForm;
    if (!f.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    setSavingKnownLocation(true);
    try {
      const body = {
        name: f.name.trim(),
        address: f.address?.trim() || "",
        cross_streets: f.cross_streets?.trim() || "",
        location_notes: f.location_notes?.trim() || "",
        latitude: f.latitude ?? null,
        longitude: f.longitude ?? null,
      };
      if (f.id) {
        await axios.put(`${API}/known-locations/${f.id}`, body, { withCredentials: true });
        toast.success("Known location updated");
      } else {
        await axios.post(`${API}/known-locations`, body, { withCredentials: true });
        toast.success("Known location added");
      }
      setKnownLocationForm({ id: null, name: "", address: "", cross_streets: "", location_notes: "", latitude: null, longitude: null });
      fetchKnownLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSavingKnownLocation(false);
  };

  const handleDeleteKnownLocation = async (id) => {
    try {
      await axios.delete(`${API}/known-locations/${id}`, { withCredentials: true });
      toast.success("Known location deleted");
      fetchKnownLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleDeleteUnit = async (unitId) => {
    try {
      await axios.delete(`${API}/admin/units/${unitId}`, { withCredentials: true });
      setAdminUnits(adminUnits.filter(u => u.unit_id !== unitId));
      setDataCounts({ ...dataCounts, units_count: dataCounts.units_count - 1 });
      toast.success("Unit permanently deleted");
    } catch (error) {
      toast.error("Failed to delete unit");
    }
  };

  const handleDeleteIncident = async (incidentId) => {
    try {
      await axios.delete(`${API}/admin/incidents/${incidentId}`, { withCredentials: true });
      setAdminIncidents(adminIncidents.filter(i => i.incident_id !== incidentId));
      setDataCounts({ ...dataCounts, incidents: dataCounts.incidents - 1 });
      toast.success("Incident permanently deleted");
    } catch (error) {
      toast.error("Failed to delete incident");
    }
  };

  const handleDeleteProfile = async (profileId) => {
    try {
      await axios.delete(`${API}/admin/perpetrator-profiles/${profileId}`, { withCredentials: true });
      setAdminProfiles(adminProfiles.filter(p => p.profile_id !== profileId));
      setDataCounts({ ...dataCounts, perpetrator_profiles: dataCounts.perpetrator_profiles - 1 });
      toast.success("Profile permanently deleted");
    } catch (error) {
      toast.error("Failed to delete profile");
    }
  };

  const handleDeleteLiveCall = async (callId) => {
    try {
      const encodedCallId = encodeURIComponent(callId);
      await axios.delete(`${API}/admin/live-calls/${encodedCallId}`, { withCredentials: true });
      setAdminLiveCalls(adminLiveCalls.filter(c => c.call_id !== callId));
      setDataCounts({ ...dataCounts, live_calls: dataCounts.live_calls - 1 });
      toast.success("Live call permanently deleted");
    } catch (error) {
      console.error("Delete live call error:", error);
      toast.error("Failed to delete live call");
    }
  };

  const handleDeleteEventOp = async (eventId) => {
    try {
      await axios.delete(`${API}/event-ops/events/${eventId}`, { withCredentials: true });
      setAdminEventOps(adminEventOps.filter(e => e.event_id !== eventId));
      setDataCounts({ ...dataCounts, event_ops: (dataCounts.event_ops || 1) - 1 });
      toast.success("Event permanently deleted");
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const fetchMediaFiles = async () => {
    try {
      const response = await axios.get(`${API}/uploads/list`, { withCredentials: true });
      setAdminMediaFiles(response.data || []);
    } catch (error) {
      console.error("Failed to fetch media files:", error);
      setAdminMediaFiles([]);
    }
  };

  const fetchActiveLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await axios.get(`${API}/location/active`, { withCredentials: true });
      setActiveLocations(response.data?.locations || []);
    } catch (error) {
      console.error("Failed to fetch active locations:", error);
      setActiveLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Audit functions
  const fetchAuditLogs = async (page = 1) => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "50" });
      if (auditFilter.event_type && auditFilter.event_type !== "__all__") params.append("event_type", auditFilter.event_type);
      if (auditFilter.search) params.append("search", auditFilter.search);
      if (auditFilter.success && auditFilter.success !== "__all__") params.append("success", auditFilter.success);
      if (auditFilter.start_date) params.append("start_date", auditFilter.start_date);
      if (auditFilter.end_date) params.append("end_date", auditFilter.end_date);
      if (auditFilter.user_id) params.append("user_id", auditFilter.user_id);
      
      const response = await axios.get(`${API}/audit/logs?${params}`, { withCredentials: true });
      setAuditLogs(response.data.logs || []);
      setAuditTotal(response.data.total || 0);
      setAuditPage(page);
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      toast.error("Failed to load audit logs");
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchAuditStats = async () => {
    try {
      const response = await axios.get(`${API}/audit/stats?days=30`, { withCredentials: true });
      setAuditStats(response.data);
    } catch (error) {
      console.error("Failed to fetch audit stats:", error);
    }
  };

  const fetchUserAuditTrail = async (userId) => {
    try {
      const response = await axios.get(`${API}/audit/user/${userId}`, { withCredentials: true });
      setUserAuditTrail(response.data);
    } catch (error) {
      console.error("Failed to fetch user audit trail:", error);
      toast.error("Failed to load user audit trail");
    }
  };

  const handleAuditTabClick = () => {
    fetchAuditLogs(1);
    fetchAuditStats();
    fetchBlockedIPs();
    fetchBlockedEmails();
    fetchFailedAttempts();
    fetchEmailAlertsConfig();
    fetchWhatsappConfig();
    fetchBotStatus();
    fetchBotGroups();
    fetchAlertLine();
    fetchHotlineWa();
    fetchDeepgram();
  };

  const fetchAdminActivity = async (page = 1) => {
    setAdminActivityLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "50" });
      if (adminActivitySearch) params.append("search", adminActivitySearch);
      if (adminActivityStartDate) params.append("start_date", adminActivityStartDate);
      if (adminActivityEndDate) params.append("end_date", adminActivityEndDate);
      const response = await axios.get(`${API}/audit/admin-activity?${params}`, { withCredentials: true });
      setAdminActivityLogs(response.data.logs || []);
      setAdminActivityTotal(response.data.total || 0);
      setAdminActivityPage(page);
    } catch (error) {
      console.error("Failed to fetch admin activity:", error);
      toast.error("Failed to load admin activity");
    } finally {
      setAdminActivityLoading(false);
    }
  };

  const fetchBlockedIPs = async () => {
    try {
      const response = await axios.get(`${API}/audit/blocked-ips`, { withCredentials: true });
      setBlockedIPs(response.data.blocked_ips || []);
    } catch (error) {
      console.error("Failed to fetch blocked IPs:", error);
    }
  };

  const fetchBlockedEmails = async () => {
    try {
      const response = await axios.get(`${API}/audit/blocked-emails`, { withCredentials: true });
      setBlockedEmails(response.data.blocked_emails || []);
    } catch (error) {
      console.error("Failed to fetch blocked emails:", error);
    }
  };

  const fetchFailedAttempts = async () => {
    try {
      const response = await axios.get(`${API}/audit/failed-attempts?limit=200`, { withCredentials: true });
      setFailedAttempts(response.data.attempts || []);
    } catch (error) {
      console.error("Failed to fetch failed attempts:", error);
    }
  };

  const fetchMediaDownloads = async (page = 1) => {
    setMediaDownloadsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", 50);
      if (downloadFilterUser) params.append("user_id", downloadFilterUser);
      if (downloadFilterFilename) params.append("filename", downloadFilterFilename);
      if (downloadFilterStartDate) params.append("start_date", downloadFilterStartDate);
      if (downloadFilterEndDate) params.append("end_date", downloadFilterEndDate);

      const [downloadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/audit/media-downloads?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/audit/media-downloads/stats`, { withCredentials: true })
      ]);

      setMediaDownloads(downloadsRes.data.downloads || []);
      setMediaDownloadsTotal(downloadsRes.data.total || 0);
      setMediaDownloadsPage(downloadsRes.data.page || 1);
      setMediaDownloadsPages(downloadsRes.data.pages || 0);
      setMediaDownloadsStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch media downloads:", error);
    } finally {
      setMediaDownloadsLoading(false);
    }
  };

  const fetchEmailAlertsConfig = async () => {
    try {
      const [configRes, typesRes] = await Promise.all([
        axios.get(`${API}/audit/email-alerts/config`, { withCredentials: true }),
        axios.get(`${API}/audit/email-alerts/notification-types`, { withCredentials: true })
      ]);
      setEmailAlertsConfig(configRes.data);
      // Recipients now include preferences: [{email: "...", preferences: {...}}, ...]
      setAlertEmails(configRes.data.recipients || []);
      setNotificationTypes(typesRes.data.notification_types || {});

      // Set provider from config
      const provider = configRes.data.provider || "resend";
      setEmailProvider(provider);

      // Pre-fill Resend config if available
      if (configRes.data.resend_configured) {
        setResendConfig({
          api_key: "", // Don't show API key
          from_email: configRes.data.smtp_from || "onboarding@resend.dev",
          from_name: configRes.data.smtp_from_name || "TARU Alerts"
        });
      }

      // Pre-fill SMTP config if available
      if (configRes.data.config_source === "database" || configRes.data.smtp_host) {
        setSmtpConfig({
          host: configRes.data.smtp_host || "",
          port: configRes.data.smtp_port || 465,
          user: configRes.data.smtp_user || "",
          password: "", // Don't show password
          from_email: configRes.data.smtp_from || "",
          from_name: configRes.data.smtp_from_name || "TARU Security Alerts",
          use_ssl: configRes.data.use_ssl !== false
        });
      }
    } catch (error) {
      console.error("Failed to fetch email alerts config:", error);
    }
  };

  // WhatsApp config functions
  const fetchWhatsappConfig = async () => {
    try {
      const res = await axios.get(`${API}/audit/whatsapp/config`, { withCredentials: true });
      setWhatsappConfig(res.data);
      setWhatsappEnabled(res.data.enabled || false);
      setWhatsappFromNumber(res.data.from_number || "");
      setBotConfiguredGroups(res.data.bot_groups || []);
      setBotEnabled(res.data.bot_enabled || false);
      setBotGroupCollections(res.data.bot_group_collections || []);
    } catch (error) {
      console.error("Failed to fetch WhatsApp config:", error);
    }
  };

  const handleSaveWhatsappConfig = async () => {
    setSavingWhatsappConfig(true);
    try {
      await axios.post(`${API}/audit/whatsapp/config`, {
        enabled: whatsappEnabled,
        from_number: whatsappFromNumber,
      }, { withCredentials: true });
      toast.success("WhatsApp configuration saved");
      fetchWhatsappConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save WhatsApp config");
    }
    setSavingWhatsappConfig(false);
  };

  const handleSendTestWhatsapp = async () => {
    if (!testWhatsappNumber.trim()) return;
    setSendingTestWhatsapp(true);
    try {
      await axios.post(`${API}/audit/whatsapp/test`, {
        number: testWhatsappNumber.trim()
      }, { withCredentials: true });
      toast.success("Test message sent");
      setTestWhatsappNumber("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test message");
    }
    setSendingTestWhatsapp(false);
  };

  // WhatsApp Bot functions
  const fetchBotStatus = async () => {
    try {
      const res = await axios.get(`${API}/audit/whatsapp/bot-status`, { withCredentials: true });
      setBotStatus(res.data);
    } catch { setBotStatus(null); }
  };

  const fetchBotGroups = async () => {
    setLoadingBotGroups(true);
    try {
      const res = await axios.get(`${API}/audit/whatsapp/bot-groups`, { withCredentials: true });
      setBotGroups(res.data.groups || []);
    } catch { setBotGroups([]); }
    setLoadingBotGroups(false);
  };

  const fetchAlertLine = async () => {
    try {
      const res = await axios.get(`${API}/audit/twilio-alert-line`, { withCredentials: true });
      setAlertLineEnabled(!!res.data?.enabled);
      setAlertLineGroupJid(res.data?.group_jid || "");
      setAlertLineGroupName(res.data?.group_name || "");
      setAlertLineTwilioNumber(res.data?.twilio_number || "");
    } catch {
      // ignore — admin-only endpoint, may 403 for non-admins
    }
  };

  const handleSaveAlertLine = async () => {
    setSavingAlertLine(true);
    try {
      await axios.post(
        `${API}/audit/twilio-alert-line`,
        {
          enabled: alertLineEnabled,
          group_jid: alertLineGroupJid,
          group_name: alertLineGroupName,
          twilio_number: alertLineTwilioNumber,
        },
        { withCredentials: true }
      );
      toast.success("Alert Line configuration saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSavingAlertLine(false);
  };

  const fetchHotlineWa = async () => {
    try {
      const res = await axios.get(`${API}/audit/hotline-whatsapp`, { withCredentials: true });
      setHotlineWaEnabled(!!res.data?.enabled);
      setHotlineWaGroupJid(res.data?.group_jid || "");
      setHotlineWaGroupName(res.data?.group_name || "");
      setHotlineWaNumber(res.data?.twilio_number || "");
    } catch {
      // ignore
    }
  };

  const fetchDeepgram = async () => {
    try {
      const res = await axios.get(`${API}/audit/deepgram`, { withCredentials: true });
      setDeepgramEnabled(res.data?.enabled !== false);
    } catch {
      // ignore
    }
  };

  const handleToggleDeepgram = async (next) => {
    setSavingDeepgram(true);
    setDeepgramEnabled(next);
    try {
      await axios.post(`${API}/audit/deepgram`, { enabled: next }, { withCredentials: true });
      toast.success(next ? "Deepgram transcription enabled" : "Deepgram transcription disabled");
    } catch (err) {
      setDeepgramEnabled(!next); // revert on failure
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSavingDeepgram(false);
  };

  const handleSaveHotlineWa = async () => {
    setSavingHotlineWa(true);
    try {
      await axios.post(
        `${API}/audit/hotline-whatsapp`,
        {
          enabled: hotlineWaEnabled,
          group_jid: hotlineWaGroupJid,
          group_name: hotlineWaGroupName,
          twilio_number: hotlineWaNumber,
        },
        { withCredentials: true }
      );
      toast.success("Hotline WhatsApp configuration saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSavingHotlineWa(false);
  };

  const handleSaveBotConfig = async () => {
    setSavingBotConfig(true);
    try {
      await axios.post(`${API}/audit/whatsapp/bot-set-groups`, {
        group_jids: botConfiguredGroups,
        enabled: botEnabled,
        bot_group_collections: botGroupCollections,
      }, { withCredentials: true });
      toast.success("Bot group configuration saved");
      await fetchWhatsappConfig();
      if (botGroups.length > 0) await fetchBotGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save bot config");
    }
    setSavingBotConfig(false);
  };

  const handleBotTest = async () => {
    setSendingBotTest(true);
    try {
      const res = await axios.post(`${API}/audit/whatsapp/bot-test`, {}, { withCredentials: true });
      if (res.data.sent > 0) toast.success(`Test sent to ${res.data.sent} recipient(s)`);
      else toast.error(res.data.error || "No messages sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send bot test");
    }
    setSendingBotTest(false);
  };

  const [creatingV2Template, setCreatingV2Template] = useState(false);
  const [v2TemplateStatus, setV2TemplateStatus] = useState(null);

  const handleCreateV2Template = async (resetWhich = null) => {
    setCreatingV2Template(true);
    try {
      // If resetting, clear old SIDs first
      if (resetWhich) {
        await axios.post(`${API}/audit/whatsapp/setup-template`, {
          reset_v2: true, reset_which: resetWhich
        }, { withCredentials: true });
      }
      const res = await axios.post(`${API}/audit/whatsapp/setup-template`, {
        create_v2: true
      }, { withCredentials: true });
      setV2TemplateStatus(res.data);
      toast.success(resetWhich ? `Reset ${resetWhich} and recreated!` : "V2 templates created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create V2 templates");
    }
    setCreatingV2Template(false);
  };

  const [setSidType, setSetSidType] = useState("priority");
  const [setSidValue, setSetSidValue] = useState("");
  const handleSetTemplateSid = async () => {
    if (!setSidValue.startsWith("HX")) {
      toast.error("SID must start with HX");
      return;
    }
    try {
      await axios.post(`${API}/audit/whatsapp/setup-template`, {
        set_sid: true, sid_type: setSidType, sid_value: setSidValue
      }, { withCredentials: true });
      toast.success(`${setSidType} SID set to ${setSidValue}`);
      setSetSidValue("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set SID");
    }
  };

  const handleCheckTemplateStatus = async () => {
    try {
      const res = await axios.get(`${API}/audit/whatsapp/template-status`, { withCredentials: true });
      setV2TemplateStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to check template status");
    }
  };

  // Groups CRUD handlers
  const handleCreateGroup = async () => {
    try {
      const res = await axios.post(`${API}/groups`, newGroup, { withCredentials: true });
      setGroups([...groups, res.data]);
      setShowNewGroupDialog(false);
      setNewGroup({ name: "", members: [] });
      toast.success("Group created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create group");
    }
  };

  const handleUpdateGroup = async () => {
    try {
      const res = await axios.put(`${API}/groups/${editGroup.group_id}`, {
        name: editGroup.name,
        members: editGroup.members
      }, { withCredentials: true });
      setGroups(groups.map(g => g.group_id === editGroup.group_id ? res.data : g));
      setShowEditGroupDialog(false);
      toast.success("Group updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update group");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm("Delete this group?")) return;
    try {
      await axios.delete(`${API}/groups/${groupId}`, { withCredentials: true });
      setGroups(groups.filter(g => g.group_id !== groupId));
      toast.success("Group deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete group");
    }
  };

  const handleSaveEmailConfig = async () => {
    setSavingEmailConfig(true);
    try {
      if (emailProvider === "resend") {
        // Validate Resend config
        if (!resendConfig.api_key) {
          toast.error("Resend API key is required");
          setSavingEmailConfig(false);
          return;
        }
        await axios.post(`${API}/audit/email-alerts/email-config`, {
          provider: "resend",
          resend_api_key: resendConfig.api_key,
          from_email: resendConfig.from_email || "onboarding@resend.dev",
          from_name: resendConfig.from_name || "TARU Alerts"
        }, { withCredentials: true });
        toast.success("Resend configuration saved");
      } else {
        // Validate SMTP config
        if (smtpConfig.host || smtpConfig.user || smtpConfig.password || smtpConfig.from_email) {
          if (!smtpConfig.host || !smtpConfig.user || !smtpConfig.password || !smtpConfig.from_email) {
            toast.error("All SMTP fields are required");
            setSavingEmailConfig(false);
            return;
          }
        }
        await axios.post(`${API}/audit/email-alerts/email-config`, {
          provider: "smtp",
          ...smtpConfig
        }, { withCredentials: true });
        toast.success("SMTP configuration saved");
      }
      setShowEmailConfig(false);
      fetchEmailAlertsConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save email config");
    } finally {
      setSavingEmailConfig(false);
    }
  };

  const handleClearEmailConfig = async () => {
    setSavingEmailConfig(true);
    try {
      await axios.post(`${API}/audit/email-alerts/email-config`, {
        provider: "smtp",
        host: "",
        port: 587,
        user: "",
        password: "",
        from_email: "",
        from_name: ""
      }, { withCredentials: true });
      toast.success("Email configuration cleared");
      setSmtpConfig({
        host: "",
        port: 587,
        user: "",
        password: "",
        from_email: "",
        from_name: "TARU Security Alerts",
        use_ssl: true
      });
      setResendConfig({
        api_key: "",
        from_email: "onboarding@resend.dev",
        from_name: "TARU Alerts"
      });
      setShowEmailConfig(false);
      fetchEmailAlertsConfig();
    } catch (error) {
      toast.error("Failed to clear email config");
    } finally {
      setSavingEmailConfig(false);
    }
  };

  const handleAddAlertEmail = async () => {
    if (!newAlertEmail.trim()) {
      toast.error("Enter an email address");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAlertEmail)) {
      toast.error("Invalid email format");
      return;
    }
    if (alertEmails.some(r => r.email === newAlertEmail.trim())) {
      toast.error("Email already added");
      return;
    }
    try {
      // Default: enable security alerts for new recipients
      const defaultPrefs = { failed_login: true, failed_pin: true, ip_blocked: true };
      await axios.post(`${API}/audit/email-alerts/add-recipient`, {
        email: newAlertEmail.trim(),
        preferences: defaultPrefs
      }, { withCredentials: true });
      setNewAlertEmail("");
      fetchEmailAlertsConfig();
      toast.success("Email added");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add email");
    }
  };

  const handleRemoveAlertEmail = async (email) => {
    try {
      await axios.delete(`${API}/audit/email-alerts/recipient/${encodeURIComponent(email)}`, { withCredentials: true });
      fetchEmailAlertsConfig();
      toast.success("Email removed");
    } catch (error) {
      toast.error("Failed to remove email");
    }
  };

  const handleToggleRecipientPref = async (email, key) => {
    const recipient = alertEmails.find(r => r.email === email);
    if (!recipient) return;

    const newPrefs = { ...recipient.preferences, [key]: !recipient.preferences[key] };
    try {
      await axios.put(`${API}/audit/email-alerts/recipient/${encodeURIComponent(email)}/preferences`, newPrefs, { withCredentials: true });
      fetchEmailAlertsConfig();
    } catch (error) {
      toast.error("Failed to update preferences");
    }
  };

  const handleSendTestEmail = async () => {
    const emailToTest = testEmailAddress.trim() || user?.email;
    if (!emailToTest) {
      toast.error("Enter an email address");
      return;
    }
    setSendingTestEmail(true);
    try {
      await axios.post(`${API}/audit/email-alerts/test?email=${encodeURIComponent(emailToTest)}`, {}, { withCredentials: true });
      toast.success(`Test email sent to ${emailToTest}`);
      setTestEmailAddress("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleToggleNotificationPref = async (key) => {
    const newPrefs = { ...notificationPrefs, [key]: !notificationPrefs[key] };
    setNotificationPrefs(newPrefs);
    try {
      await axios.post(`${API}/audit/email-alerts/preferences`, newPrefs, { withCredentials: true });
    } catch (error) {
      // Revert on error
      setNotificationPrefs(notificationPrefs);
      toast.error("Failed to update preference");
    }
  };

  const handleEnableAllNotifications = async (category = null) => {
    const newPrefs = { ...notificationPrefs };
    Object.keys(notificationTypes).forEach(key => {
      if (!category || notificationTypes[key]?.category === category) {
        newPrefs[key] = true;
      }
    });
    setNotificationPrefs(newPrefs);
    try {
      await axios.post(`${API}/audit/email-alerts/preferences`, newPrefs, { withCredentials: true });
      toast.success(category ? `All ${category} notifications enabled` : "All notifications enabled");
    } catch (error) {
      setNotificationPrefs(notificationPrefs);
      toast.error("Failed to update preferences");
    }
  };

  const handleDisableAllNotifications = async (category = null) => {
    const newPrefs = { ...notificationPrefs };
    Object.keys(notificationTypes).forEach(key => {
      if (!category || notificationTypes[key]?.category === category) {
        newPrefs[key] = false;
      }
    });
    setNotificationPrefs(newPrefs);
    try {
      await axios.post(`${API}/audit/email-alerts/preferences`, newPrefs, { withCredentials: true });
      toast.success(category ? `All ${category} notifications disabled` : "All notifications disabled");
    } catch (error) {
      setNotificationPrefs(notificationPrefs);
      toast.error("Failed to update preferences");
    }
  };

  const handleUnblockIP = async (ipAddress) => {
    try {
      await axios.post(`${API}/audit/unblock-ip/${encodeURIComponent(ipAddress)}`, {}, { withCredentials: true });
      toast.success(`IP ${ipAddress} unblocked`);
      fetchBlockedIPs();
    } catch (error) {
      toast.error("Failed to unblock IP");
    }
  };

  const handleManualBlockIP = async () => {
    if (!manualBlockIP.trim()) {
      toast.error("Enter an IP address");
      return;
    }
    try {
      await axios.post(
        `${API}/audit/block-ip?ip_address=${encodeURIComponent(manualBlockIP)}&reason=${encodeURIComponent(manualBlockReason || "Manually blocked")}&hours=${manualBlockHours}${manualBlockForever ? "&permanent=true" : ""}`,
        {},
        { withCredentials: true }
      );
      toast.success(manualBlockForever ? `IP ${manualBlockIP} blocked forever` : `IP ${manualBlockIP} blocked for ${manualBlockHours} hours`);
      setManualBlockIP("");
      setManualBlockReason("");
      setManualBlockForever(false);
      fetchBlockedIPs();
    } catch (error) {
      toast.error("Failed to block IP");
    }
  };

  const handleUnblockEmail = async (email) => {
    try {
      await axios.post(`${API}/audit/unblock-email/${encodeURIComponent(email)}`, {}, { withCredentials: true });
      toast.success(`Email ${email} unblocked`);
      fetchBlockedEmails();
    } catch (error) {
      toast.error("Failed to unblock email");
    }
  };

  const handleManualBlockEmail = async () => {
    if (!manualBlockEmail.trim()) {
      toast.error("Enter an email address");
      return;
    }
    try {
      await axios.post(
        `${API}/audit/block-email?email=${encodeURIComponent(manualBlockEmail)}&reason=${encodeURIComponent(manualBlockEmailReason || "Manually blocked")}&hours=${manualBlockEmailHours}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Email ${manualBlockEmail} blocked for ${manualBlockEmailHours} hours`);
      setManualBlockEmail("");
      setManualBlockEmailReason("");
      fetchBlockedEmails();
    } catch (error) {
      toast.error("Failed to block email");
    }
  };

  const handleClearFailedAttempts = async (ipAddress = null) => {
    try {
      const url = ipAddress 
        ? `${API}/audit/clear-failed-attempts?ip_address=${encodeURIComponent(ipAddress)}`
        : `${API}/audit/clear-failed-attempts`;
      await axios.delete(url, { withCredentials: true });
      toast.success(ipAddress ? `Cleared attempts for ${ipAddress}` : "Cleared all failed attempts");
      fetchFailedAttempts();
    } catch (error) {
      toast.error("Failed to clear attempts");
    }
  };

  const handleExportAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (auditFilter.event_type && auditFilter.event_type !== "__all__") {
        params.append("event_type", auditFilter.event_type);
      }
      if (auditFilter.start_date) params.append("start_date", auditFilter.start_date);
      if (auditFilter.end_date) params.append("end_date", auditFilter.end_date);
      
      const response = await axios.get(`${API}/audit/export?${params}`, { withCredentials: true });
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.total_records} records`);
    } catch (error) {
      toast.error("Failed to export audit logs");
    }
  };

  const formatAuditDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true
    });
  };

  const getEventTypeColor = (eventType) => {
    if (eventType?.includes("landing_page_visit")) return "bg-slate-500";
    if (eventType?.includes("login_success")) return "bg-green-600";
    if (eventType?.includes("login_failed") || eventType?.includes("login_denied")) return "bg-red-600";
    if (eventType === "force_logout") return "bg-orange-600";
    if (eventType === "clear_push_subscriptions") return "bg-orange-600";
    if (eventType?.includes("admin_delete")) return "bg-red-600";
    if (eventType?.includes("admin_add")) return "bg-green-600";
    if (eventType?.includes("logout")) return "bg-amber-600";
    if (eventType?.includes("page_visit")) return "bg-blue-600";
    if (eventType?.includes("action_")) return "bg-purple-600";
    if (eventType?.includes("admin_")) return "bg-orange-600";
    if (eventType?.includes("notification")) return "bg-cyan-600";
    return "bg-slate-600";
  };

  const getEventTypeLabel = (eventType) => {
    const labels = {
      "landing_page_visit": "Landing Page Visit",
      "login_attempt": "Login Attempt",
      "login_success": "Login Success",
      "login_failed": "Login Failed",
      "login_denied": "Login Denied",
      "logout": "Logout",
      "page_visit": "Page Visit",
      "action_respond": "Responded to Call",
      "action_unrespond": "Unresponded",
      "action_create_call": "Created Call",
      "action_update_call": "Updated Call",
      "action_log_incident": "Logged Incident",
      "action_upload_media": "Uploaded Media",
      "action_assign_unit": "Assigned Unit",
      "admin_create_user": "Created User",
      "admin_update_user": "Updated User",
      "admin_deactivate_user": "Deactivated User",
      "admin_delete_user": "Deleted User",
      "admin_block_ip": "Blocked IP",
      "admin_unblock_ip": "Unblocked IP",
      "admin_update_email_config": "Updated Email Config",
      "admin_update_email_alerts": "Updated Email Alerts",
      "admin_test_email_alert": "Sent Test Email",
      "admin_update_notification_preferences": "Updated Notification Prefs",
      "admin_block_email": "Blocked Email",
      "admin_unblock_email": "Unblocked Email",
      "admin_delete_incident": "Deleted Incident",
      "admin_delete_profile": "Deleted Profile",
      "admin_delete_live_call": "Deleted Live Call",
      "admin_delete_unit": "Deleted Unit",
      "admin_delete_record": "Deleted",
      "admin_add_patrol_car": "Added Patrol Car",
      "admin_delete_patrol_car": "Deleted Patrol Car",
      "admin_add_call_type": "Added Call Type",
      "admin_delete_call_type": "Deleted Call Type",
      "admin_add_radio_code": "Added Radio Code",
      "admin_delete_radio_code": "Deleted Radio Code",
      "force_logout": "Force Logout",
      "clear_push_subscriptions": "Cleared Push Subscriptions",
      "notification_subscribe": "Push Subscribed",
      "notification_unsubscribe": "Push Unsubscribed"
    };
    return labels[eventType] || eventType?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Unknown";
  };

  const forceStopUserLocation = async (userId, username) => {
    try {
      await axios.post(`${API}/location/admin/force-stop/${userId}`, {}, { withCredentials: true });
      setActiveLocations(activeLocations.filter(l => l.user_id !== userId));
      toast.success(`Stopped location sharing for ${username}`);
    } catch (error) {
      console.error("Force stop error:", error);
      toast.error("Failed to stop location sharing");
    }
  };

  const forceStopAllLocations = async () => {
    try {
      await axios.post(`${API}/location/admin/force-stop-all`, {}, { withCredentials: true });
      setActiveLocations([]);
      toast.success("Stopped all active location sharing");
    } catch (error) {
      console.error("Force stop all error:", error);
      toast.error("Failed to stop all location sharing");
    }
  };

  const handleDeleteMedia = async (fileId, filename) => {
    try {
      await axios.delete(`${API}/uploads/${fileId}`, { withCredentials: true });
      setAdminMediaFiles(adminMediaFiles.filter(f => f.file_id !== fileId));
      toast.success(`Deleted ${filename}`);
    } catch (error) {
      console.error("Delete media error:", error);
      toast.error("Failed to delete media file");
    }
  };

  const handleDeleteBuildingCode = async (codeId) => {
    try {
      await axios.delete(`${API}/building-codes/${codeId}`, { withCredentials: true });
      setAdminBuildingCodes(adminBuildingCodes.filter(c => c.code_id !== codeId));
      setDataCounts({ ...dataCounts, building_codes: dataCounts.building_codes - 1 });
      toast.success("Building code permanently deleted");
    } catch (error) {
      console.error("Delete building code error:", error);
      toast.error("Failed to delete building code");
    }
  };

  const handleDeleteRollCall = async (rollcallId) => {
    try {
      await axios.delete(`${API}/rollcall/${rollcallId}`, { withCredentials: true });
      setAdminRollCalls(adminRollCalls.filter(rc => rc.rollcall_id !== rollcallId));
      setDataCounts({ ...dataCounts, roll_calls: (dataCounts.roll_calls || 1) - 1 });
      toast.success("Roll call permanently deleted");
    } catch (error) {
      console.error("Delete roll call error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete roll call");
    }
  };

  const handleDeleteTodo = async (todoId) => {
    try {
      await axios.delete(`${API}/patrol/todos/${todoId}`, { withCredentials: true });
      setAdminTodos(adminTodos.filter(t => t.todo_id !== todoId));
      toast.success("Todo permanently deleted");
    } catch (error) {
      toast.error("Failed to delete todo");
    }
  };

  const handleResetAllPatrolCars = async () => {
    try {
      await axios.post(`${API}/patrol/cars/reset-all`, {}, { withCredentials: true });
      toast.success("All patrol cars reset to available");
    } catch (error) {
      console.error("Reset patrol cars error:", error);
      toast.error(error.response?.data?.detail || "Failed to reset patrol cars");
    }
  };

  // User Management
  const handleCreateUser = async () => {
    try {
      const response = await axios.post(`${API}/users`, newUser, { withCredentials: true });
      setUsers([...users, response.data]);
      setShowNewUserDialog(false);
      setNewUser({ email: "", name: "", role: "live_calls_only" });
      toast.success("User created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdateUser = async () => {
    try {
      const updateData = {
        email: editUser.email,
        name: editUser.name,
        role: editUser.role,
        is_active: editUser.is_active,
        linked_unit: editUser.linked_unit || null,
        can_self_respond: editUser.can_self_respond !== false,
        patrol_unlock_disabled: editUser.patrol_unlock_disabled === true,
        permissions: editUser.permissions || [],
        page_access: editUser.page_access || [],
        // Notification preferences
        notif_enabled: editUser.notif_enabled,
        notif_all_enabled: editUser.notif_all_enabled,
        notif_can_edit: editUser.notif_can_edit,
        notif_normal_calls: editUser.notif_normal_calls,
        notif_priority_calls: editUser.notif_priority_calls,
        notif_priority_updates: editUser.notif_priority_updates,
        notif_1085: editUser.notif_1085,
        notif_1013: editUser.notif_1013,
        notif_call_updates: editUser.notif_call_updates,
        notif_patrol: editUser.notif_patrol,
        notif_perpetrator_updates: editUser.notif_perpetrator_updates,
        notif_muted_until: editUser.notif_muted_until || null,
        // Location sharing
        allow_location_sharing: editUser.allow_location_sharing,
        // Text size toggle
        allow_text_size_toggle: editUser.allow_text_size_toggle,
        // Theme access
        allowed_themes: editUser.allowed_themes || ["light", "tactical"],
        // Phone widget permissions
        phone_widget_enabled: editUser.phone_widget_enabled || false,
        phone_callback_enabled: editUser.phone_callback_enabled || false,
        phone_number: editUser.phone_number || null,
        // WhatsApp
        whatsapp_number: editUser.whatsapp_number || "",
        whatsapp_enabled: editUser.whatsapp_enabled || false,
        whatsapp_8513_only: editUser.whatsapp_8513_only || false,
        // Patrol admin access
        patrol_admin_access: editUser.patrol_admin_access || false,
        // Session admin access
        session_admin_access: editUser.session_admin_access || false,
        // Fleet (MyGeotab) access
        fleet_access: editUser.fleet_access || false,
        // Tickets: sees everyone's, edits them, marks them reimbursed
        tickets_access: editUser.tickets_access || false,
        // My Dashboard → Tickets tab hidden (editors always see it)
        tickets_tab_hidden: editUser.tickets_tab_hidden || false,
        // Default homepage
        default_homepage: editUser.default_homepage || null,
        // Perpetrator admin access
        perpetrator_admin_access: editUser.perpetrator_admin_access || false,
        // Roll Call access
        rollcall_access: editUser.rollcall_access || false,
        // Radio tab access
        radio_71_access: editUser.radio_71_access || false,
        recording_admin_access: editUser.recording_admin_access || false,
        // Hotline access
        incidents_access: editUser.incidents_access || false,
        investigations_access: editUser.investigations_access || false,
        hotline_access: editUser.hotline_access || false,
        hotline_dispatch_access: editUser.hotline_dispatch_access || false,
        // Event Ops access
        event_ops_dispatch_access: editUser.event_ops_dispatch_access || false,
        event_ops_unit_access: editUser.event_ops_unit_access || false,
        event_ops_unit_can_edit: editUser.event_ops_unit_can_edit || false,
        // Device login limit
        max_devices: editUser.max_devices || 1,
        device_access: editUser.device_access || "all",
        // Patrol tab cars ([] = all)
        patrol_visible_cars: editUser.patrol_visible_cars || [],
        // Session timeout settings
        session_timeout_type: editUser.session_timeout_type || "activity",
        session_timeout_days: editUser.session_timeout_days || 7,
        // PIN security - always send pin_required status
        pin_required: editUser.pin_required || false,
        pin_expiry_hours: editUser.pin_expiry_hours !== undefined ? editUser.pin_expiry_hours : 24,
        // Admin-controlled PIN
        admin_pin_enabled: editUser.admin_pin_enabled || false,
        admin_pin_message: editUser.admin_pin_message || "",
        // App Lock settings
        app_lock_enabled: editUser.app_lock_enabled || false,
        app_lock_timeout_minutes: editUser.app_lock_timeout_minutes !== undefined ? editUser.app_lock_timeout_minutes : 5,
        app_lock_scope: editUser.app_lock_scope || "app",
        app_lock_pages: editUser.app_lock_pages || []
      };

      // Only include pin_code if it's being set/changed (has actual value with at least 4 digits)
      if (editUser.pin_code && editUser.pin_code.length >= 4) {
        updateData.pin_code = editUser.pin_code;
      }

      // Only include admin_pin_code if it's being set/changed (has actual value with at least 4 digits)
      if (editUser.admin_pin_code && editUser.admin_pin_code.length >= 4) {
        updateData.admin_pin_code = editUser.admin_pin_code;
      }

      // Only include app_lock_pin if it's being set/changed (has actual value with at least 4 digits)
      if (editUser.app_lock_pin && editUser.app_lock_pin.length >= 4) {
        updateData.app_lock_pin = editUser.app_lock_pin;
      }

      const response = await axios.put(`${API}/users/${editUser.user_id}`, updateData, { withCredentials: true });
      setUsers(users.map(u => u.user_id === editUser.user_id ? response.data : u));
      setShowEditUserDialog(false);
      // Clear the pin codes from editUser to avoid confusion on next edit
      setEditUser(prev => ({ ...prev, pin_code: '', admin_pin_code: '', app_lock_pin: '' }));

      // If we updated the current logged-in user, refresh their auth data
      if (editUser.user_id === user?.user_id) {
        await checkAuth();
      }

      toast.success("User updated");
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      setUsers(users.filter(u => u.user_id !== userId));
      toast.success("User deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  // Incident Type Management
  const handleCreateType = async () => {
    try {
      const response = await axios.post(`${API}/incident-types`, newType, { withCredentials: true });
      setIncidentTypes([...incidentTypes, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setShowNewTypeDialog(false);
      setNewType({ name: "" });
      toast.success("Incident type created");
    } catch (error) {
      toast.error("Failed to create incident type");
    }
  };

  const handleUpdateType = async () => {
    try {
      const response = await axios.put(`${API}/incident-types/${editType.type_id}`, {
        name: editType.name
      }, { withCredentials: true });
      setIncidentTypes(incidentTypes.map(t => t.type_id === editType.type_id ? response.data : t).sort((a, b) => a.name.localeCompare(b.name)));
      setShowEditTypeDialog(false);
      toast.success("Incident type updated");
    } catch (error) {
      toast.error("Failed to update incident type");
    }
  };

  const handleDeleteType = async (typeId) => {
    try {
      await axios.delete(`${API}/incident-types/${typeId}`, { withCredentials: true });
      setIncidentTypes(incidentTypes.filter(t => t.type_id !== typeId));
      toast.success("Incident type removed");
    } catch (error) {
      toast.error("Failed to delete incident type");
    }
  };

  // Live Call Options Management
  const handleAddFlag = async () => {
    if (!newFlagLabel.trim()) {
      toast.error("Please enter a flag label");
      return;
    }
    try {
      const flagId = newFlagLabel.toLowerCase().replace(/\s+/g, '_');
      const response = await axios.post(`${API}/app-config/live-call-options/add-flag`, {
        flag_type: newFlagType,
        id: flagId,
        label: newFlagLabel
      }, { withCredentials: true });
      setLiveCallOptions(response.data);
      setNewFlagLabel("");
      toast.success("Flag added");
    } catch (error) {
      toast.error("Failed to add flag");
    }
  };

  const handleDeleteFlag = async (flagId) => {
    try {
      const response = await axios.delete(`${API}/app-config/live-call-options/flag/${flagId}`, { withCredentials: true });
      setLiveCallOptions(response.data.config);
      toast.success("Flag removed");
    } catch (error) {
      toast.error("Failed to remove flag");
    }
  };

  const handleToggleFlag = async (flagType, flagId, active) => {
    if (!liveCallOptions) return;
    
    const updatedConfig = {
      ...liveCallOptions,
      [flagType]: liveCallOptions[flagType].map(f => 
        f.id === flagId ? { ...f, active: active } : f
      )
    };
    
    try {
      const response = await axios.put(`${API}/app-config/live-call-options`, updatedConfig, { withCredentials: true });
      setLiveCallOptions(response.data);
      toast.success("Flag updated");
    } catch (error) {
      toast.error("Failed to update flag");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <SettingsIcon className="w-6 h-6 text-blue-500" />
        <h1 className="text-2xl font-bold uppercase tracking-tight">Settings</h1>
        <Badge variant="outline">Admin Only</Badge>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="!flex w-full sm:w-auto bg-slate-900 overflow-x-auto no-scrollbar max-w-full">
          <TabsTrigger value="users" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none min-w-0">
            <Users className="w-4 h-4 shrink-0" /><span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="types" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none min-w-0">
            <FileText className="w-4 h-4 shrink-0" /><span className="hidden sm:inline">Types</span>
          </TabsTrigger>
          <TabsTrigger value="patrol" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none min-w-0">
            <Car className="w-4 h-4 shrink-0" /><span className="hidden sm:inline">Patrol</span>
          </TabsTrigger>
          <TabsTrigger value="livecall" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none min-w-0">
            <SettingsIcon className="w-4 h-4 shrink-0" /><span className="hidden sm:inline">Live Call</span>
          </TabsTrigger>
          <TabsTrigger value="data" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none min-w-0" onClick={() => { fetchDataManagement(); fetchUnitRecords(); }}>
            <Database className="w-4 h-4 shrink-0" /><span className="hidden sm:inline">Data</span>
          </TabsTrigger>
          <TabsTrigger value="locations" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none min-w-0" onClick={fetchKnownLocations}>
            <MapPin className="w-4 h-4 shrink-0" /><span className="hidden sm:inline">Locations</span>
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm flex-1 sm:flex-none min-w-0" onClick={handleAuditTabClick}>
            <AlertTriangle className="w-4 h-4 shrink-0" /><span className="hidden sm:inline">Audit</span>
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          {/* Sub-tabs: Users | Groups */}
          <div className="flex gap-1 mb-4 border-b border-slate-700">
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                usersSubTab === "users"
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-slate-400 hover:text-slate-300"
              }`}
              onClick={() => setUsersSubTab("users")}
            >
              <Users className="w-4 h-4" /> Users
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                usersSubTab === "groups"
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-slate-400 hover:text-slate-300"
              }`}
              onClick={() => setUsersSubTab("groups")}
            >
              <Users className="w-4 h-4" /> Groups
              {groups.length > 0 && <span className="text-[10px] bg-slate-700 rounded-full px-1.5 py-0.5">{groups.length}</span>}
            </button>
          </div>

          {usersSubTab === "users" && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="flex flex-col gap-3">
              <CardTitle className="text-lg uppercase tracking-wider">User Management</CardTitle>
              <div className="flex gap-2 w-full flex-wrap">
                <div className="relative flex-1 min-w-[120px]">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="input-dark pl-8 h-9 w-full"
                  />
                </div>
                <select
                  value={userSort}
                  onChange={(e) => setUserSort(e.target.value)}
                  className="h-9 px-2 text-xs bg-slate-800 border border-slate-700 rounded text-slate-300 cursor-pointer"
                >
                  <option value="recent">Recently Added</option>
                  <option value="alpha">A → Z</option>
                  <option value="alpha-desc">Z → A</option>
                  <option value="unit">Unit #</option>
                  <option value="active">Active First</option>
                  <option value="inactive">Inactive First</option>
                  <option value="role">Role</option>
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-400 border-blue-400/50 hover:bg-blue-900/20"
                  onClick={async () => {
                    try {
                      const response = await axios.post(`${API}/push/refresh-all`, {}, { withCredentials: true });
                      toast.success(response.data.message);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to send refresh");
                    }
                  }}
                >
                  <RefreshCw className="w-4 h-4 shrink-0" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-amber-400 border-amber-400/50 hover:bg-amber-900/20"
                  onClick={() => {
                    setNotifSelectedUsers([]);
                    setNotifTitle("");
                    setNotifMessage("");
                    setNotifScheduleTime("");
                    setNotifSendPush(true);
                    setNotifSendWhatsapp(false);
                    setNotifSendToWaBotGroups(false);
                    setNotifWaBotGroupJids([]);
                    setNotifMasterTab("send");
                    setShowNotifMasterDialog(true);
                  }}
                >
                  <Bell className="w-4 h-4 shrink-0 mr-1" />Notify
                </Button>
                <Dialog open={showNewUserDialog} onOpenChange={setShowNewUserDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" size="sm" data-testid="add-user-btn">
                      <Plus className="w-4 h-4 shrink-0 mr-1" />Add
                    </Button>
                  </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-800">
                  <DialogHeader>
                    <DialogTitle className="uppercase tracking-wider">Add New User</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Email *</Label>
                      <Input
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="user@example.com"
                        className="input-dark"
                        data-testid="user-email-input"
                      />
                    </div>
                    <div>
                      <Label>Name *</Label>
                      <Input
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        placeholder="Full name"
                        className="input-dark"
                        data-testid="user-name-input"
                      />
                    </div>
                    <div className="p-3 bg-slate-800 rounded text-sm text-slate-400">
                      <p>After creating the user, click Edit to configure which pages they can access.</p>
                    </div>
                    <Button onClick={handleCreateUser} className="w-full btn-primary" data-testid="create-user-btn">
                      Create User
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const searched = userSearch.trim()
                  ? users.filter(u => {
                      const search = userSearch.toLowerCase();
                      return (
                        (u.name && u.name.toLowerCase().includes(search)) ||
                        (u.email && u.email.toLowerCase().includes(search)) ||
                        (u.linked_unit && u.linked_unit.toLowerCase().includes(search))
                      );
                    })
                  : users;
                const filteredUsers = [...searched].sort((a, b) => {
                  switch (userSort) {
                    case "alpha":
                      return (a.name || "").localeCompare(b.name || "");
                    case "alpha-desc":
                      return (b.name || "").localeCompare(a.name || "");
                    case "unit": {
                      const numA = parseInt((a.linked_unit || "").replace(/\D/g, "")) || 9999;
                      const numB = parseInt((b.linked_unit || "").replace(/\D/g, "")) || 9999;
                      return numA - numB || (a.name || "").localeCompare(b.name || "");
                    }
                    case "active":
                      return (b.is_active === false ? 0 : 1) - (a.is_active === false ? 0 : 1) || (a.name || "").localeCompare(b.name || "");
                    case "inactive":
                      return (a.is_active === false ? 0 : 1) - (b.is_active === false ? 0 : 1) || (a.name || "").localeCompare(b.name || "");
                    case "role":
                      return (a.role || "").localeCompare(b.role || "") || (a.name || "").localeCompare(b.name || "");
                    case "recent":
                    default:
                      return 0; // Keep original order (most recently added)
                  }
                });
                return (
              <ScrollArea className="h-96">
                {/* Mobile Card View */}
                <div className="space-y-3 md:hidden">
                  {filteredUsers.map((u) => (
                    <div key={u.user_id} className="p-3 bg-slate-950 rounded-sm border border-slate-800" data-testid={`user-row-${u.user_id}`}>
                      <div className="flex items-center gap-3 mb-3">
                        {u.picture && (
                          <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{u.name}</p>
                          <p className="text-xs text-slate-400 truncate">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant={u.role === "admin" ? "default" : "secondary"} className="text-xs">
                          {u.role?.replace("_", " ")}
                        </Badge>
                        <Badge variant={u.is_active ? "outline" : "destructive"} className="text-xs">
                          {u.is_active ? "Active" : "Inactive"}
                        </Badge>
                        {u.linked_unit && (
                          <Badge variant="outline" className="text-xs bg-blue-600 border-blue-600 text-white">
                            Unit: {u.linked_unit}
                          </Badge>
                        )}
                        {u.permissions && u.permissions.length > 0 && (
                          <Badge variant="outline" className="text-xs bg-purple-600 border-purple-600 text-white">
                            +{u.permissions.length} perm{u.permissions.length > 1 ? 's' : ''}
                          </Badge>
                        )}
                        {u.device_access && u.device_access !== "all" && (
                          <Badge variant="outline" className="text-xs bg-amber-600 border-amber-600 text-white">
                            {u.device_access === "mobile_only" ? "Mobile Only" : "Desktop Only"}
                          </Badge>
                        )}
                      </div>
                      {/* Action Buttons - Full width on mobile */}
                      <div className="flex gap-2 pt-2 border-t border-slate-800">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setEditUser({...u});
                            setShowEditUserDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        {u.user_id !== user?.user_id && u.email?.toLowerCase() !== 'sruliyavich@gmail.com' && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="flex-1 text-red-500 border-red-500/50 hover:bg-red-500/10">
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-slate-900 border-slate-800">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete User</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete {u.name}?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteUser(u.user_id)}
                                  className="btn-destructive"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="data-table min-w-full">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Unit</th>
                        <th>Status</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                          <td>
                            <div className="flex items-center gap-2">
                              {u.picture && (
                                <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" />
                              )}
                              <span className="font-medium">{u.name}</span>
                            </div>
                          </td>
                          <td className="text-slate-400">{u.email}</td>
                          <td>
                            <Badge variant={u.role === "admin" ? "default" : "secondary"}>
                              {u.role?.replace("_", " ")}
                            </Badge>
                          </td>
                          <td>
                            {u.linked_unit ? (
                              <Badge variant="outline" className="bg-blue-600 border-blue-600 text-white">
                                {u.linked_unit}
                              </Badge>
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </td>
                          <td>
                            <div className="flex flex-wrap gap-1">
                              <Badge variant={u.is_active ? "outline" : "destructive"}>
                                {u.is_active ? "Active" : "Inactive"}
                              </Badge>
                              {u.device_access && u.device_access !== "all" && (
                                <Badge variant="outline" className="bg-amber-600 border-amber-600 text-white">
                                  {u.device_access === "mobile_only" ? "Mobile" : "Desktop"}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditUser({...u});
                                  setShowEditUserDialog(true);
                                }}
                                title="Edit user"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {u.user_id !== user?.user_id && u.email?.toLowerCase() !== 'sruliyavich@gmail.com' && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm" title="Delete user">
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="bg-slate-900 border-slate-800">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete User</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to delete {u.name}? This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                                      <AlertDialogAction 
                                        onClick={() => handleDeleteUser(u.user_id)}
                                        className="btn-destructive"
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ScrollArea>
                );
              })()}
            </CardContent>
          </Card>
          )}

          {/* Groups Sub-tab */}
          {usersSubTab === "groups" && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg uppercase tracking-wider">Notification Groups</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Create groups to target push & WhatsApp notifications to specific users</p>
              </div>
              <Button onClick={() => { setNewGroup({ name: "", members: [] }); setShowNewGroupDialog(true); }} className="btn-primary" size="sm">
                <Plus className="w-4 h-4 mr-1" />Add Group
              </Button>
            </CardHeader>
            <CardContent>
              {groups.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No groups configured</p>
                  <p className="text-xs text-slate-600 mt-1">Groups let you send notifications to specific sets of users</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {groups.map((group) => (
                    <div key={group.group_id} className="flex items-center justify-between p-3 bg-slate-800 rounded border border-slate-700 hover:border-slate-600 transition-colors">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-200">{group.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {group.members?.length || 0} member{(group.members?.length || 0) !== 1 ? 's' : ''}
                          {group.members?.length > 0 && (
                            <span className="text-slate-600 ml-1">
                              — {group.members.slice(0, 3).map(mid => {
                                const mu = users.find(u => u.user_id === mid);
                                return mu?.name || "Unknown";
                              }).join(", ")}{group.members.length > 3 ? `, +${group.members.length - 3} more` : ""}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300" onClick={() => { setEditGroup({ ...group }); setShowEditGroupDialog(true); }}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-300" onClick={() => handleDeleteGroup(group.group_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Notification Master Dialog */}
          <Dialog open={showNotifMasterDialog} onOpenChange={setShowNotifMasterDialog}>
            <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="uppercase tracking-wider flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-400" />
                  Notification Master
                </DialogTitle>
              </DialogHeader>

              {/* Tabs */}
              <div className="flex gap-2 border-b border-slate-700 pb-2">
                <Button
                  variant={notifMasterTab === "send" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setNotifMasterTab("send")}
                  className={notifMasterTab === "send" ? "bg-amber-600" : ""}
                >
                  <Send className="w-4 h-4 mr-1" />Send
                </Button>
                <Button
                  variant={notifMasterTab === "history" ? "default" : "ghost"}
                  size="sm"
                  onClick={async () => {
                    setNotifMasterTab("history");
                    setNotifHistoryPage(1);
                    setNotifHistoryLoading(true);
                    try {
                      // Process any due scheduled notifications
                      await axios.post(`${API}/push/process-scheduled`, {}, { withCredentials: true }).catch(() => {});
                      const res = await axios.get(`${API}/push/notification-history?page=1&limit=20`, { withCredentials: true });
                      setNotifHistory(res.data.items);
                      setNotifHistoryTotal(res.data.total);
                      setNotifHistoryPages(res.data.pages);
                    } catch (error) {
                      toast.error("Failed to load history");
                    }
                    setNotifHistoryLoading(false);
                  }}
                  className={notifMasterTab === "history" ? "bg-amber-600" : ""}
                >
                  <History className="w-4 h-4 mr-1" />History
                </Button>
              </div>

              {/* Send Tab */}
              {notifMasterTab === "send" && (
                <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                  {/* User Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-semibold">Select Users</Label>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => setNotifSelectedUsers(users.filter(u => u.is_active !== false).map(u => u.user_id))}
                        >
                          All
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => setNotifSelectedUsers([])}
                        >
                          None
                        </Button>
                      </div>
                    </div>
                    {/* Group quick-select */}
                    {groups.length > 0 && (
                      <div className="flex items-center gap-1 mb-2 flex-wrap">
                        <span className="text-xs text-slate-500">Groups:</span>
                        {groups.map((g) => (
                          <Button
                            key={g.group_id}
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-xs"
                            onClick={() => setNotifSelectedUsers(g.members || [])}
                          >
                            {g.name}
                          </Button>
                        ))}
                      </div>
                    )}
                    {/* Search */}
                    <Input
                      placeholder="Search users..."
                      value={notifUserSearch || ""}
                      onChange={(e) => setNotifUserSearch(e.target.value)}
                      className="input-dark h-7 text-xs mb-2"
                    />
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 p-2 bg-slate-800 rounded max-h-40 overflow-y-auto">
                      {users
                        .filter(u => u.is_active !== false)
                        .filter(u => !notifUserSearch || (u.name && u.name.toLowerCase().includes(notifUserSearch.toLowerCase())) || (u.email && u.email.toLowerCase().includes(notifUserSearch.toLowerCase())))
                        .sort((a, b) => {
                          const numA = parseInt((a.linked_unit || "").replace(/\D/g, "")) || 9999;
                          const numB = parseInt((b.linked_unit || "").replace(/\D/g, "")) || 9999;
                          return numA - numB || (a.name || "").localeCompare(b.name || "");
                        })
                        .map((u) => (
                        <label key={u.user_id} className="flex items-center gap-2 text-xs p-1 hover:bg-slate-700 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifSelectedUsers.includes(u.user_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNotifSelectedUsers([...notifSelectedUsers, u.user_id]);
                              } else {
                                setNotifSelectedUsers(notifSelectedUsers.filter(id => id !== u.user_id));
                              }
                            }}
                            className="w-3 h-3"
                          />
                          <span className="truncate">{u.linked_unit ? `${u.linked_unit} - ` : ""}{u.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{notifSelectedUsers.length} user(s) selected</p>
                  </div>

                  {/* Title */}
                  <div>
                    <Label className="text-sm">Title</Label>
                    <Input
                      value={notifTitle}
                      onChange={(e) => setNotifTitle(e.target.value)}
                      placeholder="Notification title"
                      className="input-dark mt-1"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <Label className="text-sm">Message *</Label>
                    <textarea
                      value={notifMessage}
                      onChange={(e) => setNotifMessage(e.target.value)}
                      placeholder="Enter your notification message..."
                      rows={3}
                      className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  {/* Schedule Time */}
                  <div>
                    <Label className="text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Schedule (optional)
                    </Label>
                    <Input
                      type="datetime-local"
                      value={notifScheduleTime}
                      onChange={(e) => setNotifScheduleTime(e.target.value)}
                      className="input-dark mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Leave empty to send immediately</p>
                  </div>

                  {/* Delivery Method */}
                  <div>
                    <Label className="text-sm mb-2 block">Delivery Method</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={notifSendPush} onChange={(e) => setNotifSendPush(e.target.checked)} className="w-4 h-4 rounded" />
                        <Bell className="w-4 h-4 text-amber-400" />
                        <span className="text-sm">Push</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={notifSendWhatsapp} onChange={(e) => setNotifSendWhatsapp(e.target.checked)} className="w-4 h-4 rounded" />
                        <MessageSquare className="w-4 h-4 text-green-400" />
                        <span className="text-sm">WhatsApp</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={notifSendToWaBotGroups} onChange={(e) => {
                          setNotifSendToWaBotGroups(e.target.checked);
                          if (e.target.checked && botGroups.length === 0) fetchBotGroups();
                        }} className="w-4 h-4 rounded" />
                        <MessageSquare className="w-4 h-4 text-green-400" />
                        <span className="text-sm">WA Groups</span>
                      </label>
                    </div>
                    {/* WA Group selector when WA Groups is checked */}
                    {notifSendToWaBotGroups && (botGroupCollections.length > 0 || botGroups.length > 0 || botConfiguredGroups.length > 0) && (
                      <div className="flex items-center gap-x-3 gap-y-1 flex-wrap mt-2 pt-2 border-t border-slate-700">
                        <span className="text-xs text-slate-400">WA Groups:</span>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifWaBotGroupJids.length === 0}
                            onChange={() => setNotifWaBotGroupJids([])}
                            className="w-3 h-3 rounded"
                          />
                          <span className="text-[11px] text-slate-300">All</span>
                        </label>
                        {botGroupCollections.map((col) => {
                          const colJids = col.jids || [];
                          const allIncluded = colJids.length > 0 && colJids.every(j => notifWaBotGroupJids.includes(j));
                          return (
                            <label key={col.name} className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={allIncluded}
                                onChange={(e) => {
                                  let updated = [...notifWaBotGroupJids];
                                  if (e.target.checked) {
                                    colJids.forEach(j => { if (!updated.includes(j)) updated.push(j); });
                                  } else {
                                    updated = updated.filter(j => !colJids.includes(j));
                                  }
                                  setNotifWaBotGroupJids(updated);
                                }}
                                className="w-3 h-3 rounded"
                              />
                              <span className="text-[11px] text-green-300 font-semibold">{col.name}</span>
                            </label>
                          );
                        })}
                        {(botGroups.length > 0 ? botGroups : botConfiguredGroups.map(jid => ({ id: jid, name: jid.split("@")[0] }))).map((g) => (
                          <label key={g.id} className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={notifWaBotGroupJids.includes(g.id)}
                              onChange={(e) => {
                                if (e.target.checked) setNotifWaBotGroupJids([...notifWaBotGroupJids, g.id]);
                                else setNotifWaBotGroupJids(notifWaBotGroupJids.filter(j => j !== g.id));
                              }}
                              className="w-3 h-3 rounded"
                            />
                            <span className="text-[11px] text-slate-300">{g.name}</span>
                          </label>
                        ))}
                        {loadingBotGroups && <span className="text-[10px] text-slate-500">Loading groups...</span>}
                      </div>
                    )}
                  </div>

                  {/* Send Button */}
                  <div className="flex gap-2 pt-2 border-t border-slate-700">
                    <Button
                      className="flex-1 bg-amber-600 hover:bg-amber-700"
                      disabled={notifSending || !notifMessage || (notifSelectedUsers.length === 0 && !notifSendToWaBotGroups) || (!notifSendPush && !notifSendWhatsapp && !notifSendToWaBotGroups)}
                      onClick={async () => {
                        setNotifSending(true);
                        try {
                          const payload = {
                            user_ids: notifSelectedUsers,
                            title: notifTitle,
                            message: notifMessage,
                            scheduled_time: notifScheduleTime ? new Date(notifScheduleTime).toISOString() : null,
                            send_push: notifSendPush,
                            send_whatsapp: notifSendWhatsapp,
                            send_to_wa_bot_groups: notifSendToWaBotGroups,
                            wa_bot_group_jids: notifSendToWaBotGroups ? notifWaBotGroupJids : [],
                          };
                          const res = await axios.post(`${API}/push/notification-master`, payload, { withCredentials: true });
                          if (res.data.status === "scheduled") {
                            toast.success(`Notification scheduled for ${notifSelectedUsers.length} user(s)`);
                          } else {
                            toast.success(`Sending notification to ${notifSelectedUsers.length} user(s)`);
                          }
                          setShowNotifMasterDialog(false);
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Failed to send notification");
                        }
                        setNotifSending(false);
                      }}
                    >
                      {notifSending ? (
                        "Sending..."
                      ) : notifScheduleTime ? (
                        <><Clock className="w-4 h-4 mr-1" />Schedule</>
                      ) : (
                        <><Send className="w-4 h-4 mr-1" />Send Now</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* History Tab */}
              {notifMasterTab === "history" && (
                <div className="flex flex-col flex-1 overflow-hidden">
                  {/* Filters */}
                  <div className="flex flex-wrap gap-2 pb-3 border-b border-slate-700 mb-3">
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-slate-400">From:</label>
                      <input
                        type="date"
                        value={notifFilterStartDate}
                        onChange={(e) => setNotifFilterStartDate(e.target.value)}
                        className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-slate-400">To:</label>
                      <input
                        type="date"
                        value={notifFilterEndDate}
                        onChange={(e) => setNotifFilterEndDate(e.target.value)}
                        className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-slate-400">By:</label>
                      <select
                        value={notifFilterSentBy}
                        onChange={(e) => setNotifFilterSentBy(e.target.value)}
                        className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                      >
                        <option value="">All users</option>
                        {users.filter(u => u.role === "admin").map(u => (
                          <option key={u.user_id} value={u.user_id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-slate-400">Type:</label>
                      <select
                        value={notifFilterType}
                        onChange={(e) => setNotifFilterType(e.target.value)}
                        className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                      >
                        <option value="">All</option>
                        <option value="immediate">Immediate</option>
                        <option value="scheduled">Scheduled</option>
                      </select>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7"
                      onClick={() => loadNotifHistoryPage(1)}
                    >
                      <Search className="w-3 h-3 mr-1" />Filter
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs h-7 text-slate-400"
                      onClick={() => {
                        setNotifFilterStartDate("");
                        setNotifFilterEndDate("");
                        setNotifFilterSentBy("");
                        setNotifFilterType("");
                        loadNotifHistoryPage(1, "", "", "", "");
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7 ml-auto text-green-400 border-green-400/50"
                      onClick={exportNotifHistoryCSV}
                    >
                      <Download className="w-3 h-3 mr-1" />CSV
                    </Button>
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {notifHistoryLoading ? (
                      <div className="text-center py-8 text-slate-400">Loading...</div>
                    ) : notifHistory.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">No notification history</div>
                    ) : (
                      <div className="space-y-2">
                        {notifHistory.map((notif) => (
                          <div key={notif.notification_id} className="p-3 bg-slate-800 rounded border border-slate-700">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm truncate">{notif.title}</span>
                                  {notif.status === "completed" && (
                                    <Badge className="bg-green-600 text-xs"><Check className="w-3 h-3 mr-1" />Sent</Badge>
                                  )}
                                  {notif.status === "scheduled" && (
                                    <Badge className="bg-blue-600 text-xs"><Clock className="w-3 h-3 mr-1" />Scheduled</Badge>
                                  )}
                                  {notif.status === "sending" && (
                                    <Badge className="bg-amber-600 text-xs">Sending...</Badge>
                                  )}
                                  {notif.status === "cancelled" && (
                                    <Badge className="bg-red-600 text-xs"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>
                                  )}
                                </div>
                                <p className="text-xs text-slate-400 mt-1 line-clamp-2">{notif.message}</p>
                                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                  <span>By: {notif.created_by_name}</span>
                                  <span>{new Date(notif.created_at).toLocaleString('en-US', { timeZone: 'America/New_York' })}</span>
                                  <span className={notif.scheduled_time ? "text-blue-400" : "text-slate-400"}>
                                    {notif.scheduled_time ? `Scheduled for ${new Date(notif.scheduled_time).toLocaleString('en-US', { timeZone: 'America/New_York' })}` : "Immediate"}
                                  </span>
                                  {notif.status === "completed" && (
                                    <span className="text-green-400">{notif.sent_count} sent, {notif.failed_count} failed</span>
                                  )}
                                </div>
                                {/* Recipients toggle */}
                                {notif.user_ids && notif.user_ids.length > 0 && (
                                  <div className="mt-2">
                                    <button
                                      className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-300"
                                      onClick={() => setExpandedRecipients(prev => ({ ...prev, [notif.notification_id]: !prev[notif.notification_id] }))}
                                    >
                                      <Users className="w-3 h-3" />
                                      <span>{notif.user_ids.length} recipient{notif.user_ids.length !== 1 ? 's' : ''}</span>
                                      {expandedRecipients[notif.notification_id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                    </button>
                                    {expandedRecipients[notif.notification_id] && (
                                      <div className="mt-1 p-2 bg-slate-900 rounded border border-slate-700 max-h-32 overflow-y-auto">
                                        <div className="flex flex-wrap gap-1">
                                          {notif.user_ids.map((uid) => {
                                            const recipientUser = users.find(u => u.user_id === uid);
                                            const result = notif.results?.find(r => r.user_id === uid);
                                            const statusColor = result?.status === "sent" ? "text-green-400"
                                              : result?.status === "failed" ? "text-red-400"
                                              : result?.status === "no_subscription" ? "text-amber-400"
                                              : "text-slate-400";
                                            return (
                                              <span key={uid} className={`text-xs px-2 py-0.5 rounded-full bg-slate-800 border border-slate-600 ${statusColor}`}>
                                                {recipientUser?.name || uid}
                                                {result && result.status !== "sent" && (
                                                  <span className="ml-1 opacity-70">({result.status === "no_subscription" ? "no sub" : result.status})</span>
                                                )}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              {notif.status === "scheduled" && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                  onClick={async () => {
                                    try {
                                      await axios.delete(`${API}/push/notification-history/${notif.notification_id}`, { withCredentials: true });
                                      setNotifHistory(notifHistory.filter(n => n.notification_id !== notif.notification_id));
                                      setNotifHistoryTotal(prev => prev - 1);
                                      toast.success("Notification cancelled");
                                    } catch (error) {
                                      toast.error("Failed to cancel notification");
                                    }
                                  }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Pagination */}
                  {notifHistoryPages > 1 && (
                    <div className="flex items-center justify-between pt-3 border-t border-slate-700 mt-2">
                      <span className="text-xs text-slate-400">
                        Page {notifHistoryPage} of {notifHistoryPages} ({notifHistoryTotal} total)
                      </span>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={notifHistoryPage <= 1 || notifHistoryLoading}
                          onClick={() => loadNotifHistoryPage(notifHistoryPage - 1)}
                          className="text-xs h-7 px-2"
                        >
                          Prev
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={notifHistoryPage >= notifHistoryPages || notifHistoryLoading}
                          onClick={() => loadNotifHistoryPage(notifHistoryPage + 1)}
                          className="text-xs h-7 px-2"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Incident Types Tab */}
        <TabsContent value="types" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg uppercase tracking-wider">Incident Types</CardTitle>
              <Dialog open={showNewTypeDialog} onOpenChange={setShowNewTypeDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-type-btn">
                    <Plus className="w-4 h-4 mr-1" />Add Type
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-800">
                  <DialogHeader>
                    <DialogTitle className="uppercase tracking-wider">Add Incident Type</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Type Name *</Label>
                      <Input
                        value={newType.name}
                        onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                        placeholder="e.g., Theft, Assault..."
                        className="input-dark"
                        data-testid="type-name-input"
                      />
                    </div>
                    <Button onClick={handleCreateType} className="w-full btn-primary" data-testid="create-type-btn">
                      Create Type
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                <div className="grid grid-cols-3 gap-2">
                  {incidentTypes.map((type) => (
                    <div
                      key={type.type_id}
                      className="p-3 bg-slate-950 rounded-sm border border-slate-800 flex items-center justify-between group"
                      data-testid={`type-${type.type_id}`}
                    >
                      <span className="font-medium">{type.name}</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditType(type);
                            setShowEditTypeDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-slate-900 border-slate-800">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Incident Type</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete &quot;{type.name}&quot;? Historical data will be preserved.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteType(type.type_id)}
                                className="btn-destructive"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
          {/* Call Final Options */}
          <Card className="card-dark mt-4">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Call Final Options</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="New call final option..."
                  value={newCallFinal}
                  onChange={(e) => setNewCallFinal(e.target.value)}
                  className="input-dark"
                  onKeyDown={(e) => e.key === "Enter" && handleAddCallFinal()}
                />
                <Button onClick={handleAddCallFinal} className="btn-primary">
                  <Plus className="w-4 h-4 mr-1" />Add
                </Button>
              </div>
              {(callFinalOptions?.options || []).length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">No call final options configured</p>
              ) : (
                <div className="space-y-1">
                  {(callFinalOptions?.options || []).map((opt) => (
                    <div key={opt.id} className="flex items-center justify-between p-2 bg-slate-800 rounded">
                      <span className="text-sm">{opt.label}</span>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-slate-900 border-slate-800">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Call Final Option</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete &quot;{opt.label}&quot;?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteCallFinal(opt.id)} className="btn-destructive">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          {/* Watermark presets for the Watermarking page */}
          <WatermarkPresetsCard />
          {/* Priority Call Types */}
          <Card className="card-dark mt-4">
            <CardHeader>
              <CardTitle>Priority Call Types</CardTitle>
              <p className="text-xs text-slate-400 mt-1">Call types checked below will automatically set priority when selected in the new call form.</p>
            </CardHeader>
            <CardContent>
              {incidentTypes.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">No call types configured</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {incidentTypes.map((type) => (
                    <label key={type.type_id} className="flex items-center gap-2 p-2 bg-slate-800 rounded cursor-pointer hover:bg-slate-700">
                      <Checkbox
                        checked={(priorityCallTypes || []).includes(type.name)}
                        onCheckedChange={async (checked) => {
                          const current = priorityCallTypes || [];
                          const updated = checked
                            ? [...current, type.name]
                            : current.filter(n => n !== type.name);
                          try {
                            const res = await axios.put(`${API}/app-config/priority-call-types`, { type_names: updated }, { withCredentials: true });
                            setPriorityCallTypes(res.data.type_names || []);
                            toast.success(checked ? `${type.name} → auto-priority` : `${type.name} → normal`);
                          } catch { toast.error("Failed to update"); }
                        }}
                      />
                      <span className="text-sm">{type.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Patrol Cars Tab */}
        <TabsContent value="patrol" className="mt-4">
          {/* Sub-tabs: Cars | Vehicles */}
          <div className="flex gap-1 mb-4 border-b border-slate-700">
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                patrolSubTab === "cars"
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-slate-400 hover:text-slate-300"
              }`}
              onClick={() => setPatrolSubTab("cars")}
            >
              <Car className="w-4 h-4" /> Cars
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                patrolSubTab === "vehicles"
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-slate-400 hover:text-slate-300"
              }`}
              onClick={() => setPatrolSubTab("vehicles")}
            >
              <Lock className="w-4 h-4" /> Vehicles
            </button>
          </div>

          {patrolSubTab === "cars" && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-lg uppercase tracking-wider flex items-center gap-2">
                  <Car className="w-5 h-5 text-emerald-500" />
                  Patrol Cars
                </CardTitle>
                <p className="text-sm text-slate-400 mt-1">
                  Manage patrol vehicles
                </p>
              </div>
              <div className="flex gap-2 items-center">
                <Input
                  placeholder="New car name..."
                  value={newCarName}
                  onChange={(e) => setNewCarName(e.target.value)}
                  className="input-dark w-40"
                  onKeyDown={(e) => e.key === "Enter" && handleAddPatrolCar()}
                />
                <Button onClick={handleAddPatrolCar} className="btn-primary" disabled={!newCarName.trim()}>
                  <Plus className="w-4 h-4 mr-1" />Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {patrolCars.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No patrol cars configured</p>
                ) : (
                  <div className="space-y-2">
                    {patrolCars.map((car) => (
                      <div 
                        key={car.car_id}
                        className={`flex items-center justify-between p-3 rounded-sm ${car.is_active ? 'bg-slate-800' : 'bg-slate-800/50'}`}
                      >
                        <div className="flex items-center gap-3">
                          <Car className={`w-5 h-5 ${car.is_active ? 'text-emerald-500' : 'text-slate-600'}`} />
                          <span className={`font-medium ${!car.is_active ? 'text-slate-500 line-through' : ''}`}>
                            {car.name}
                          </span>
                          {!car.is_active && (
                            <Badge variant="outline" className="text-xs">Inactive</Badge>
                          )}
                          {car.status === "out_of_service" && (
                            <Badge variant="destructive" className="text-xs">Out of Service</Badge>
                          )}
                          {car.status === "active_patrol" && (
                            <Badge className="bg-blue-600 text-xs">Active: {car.assigned_unit}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditCar(car); setShowEditCarDialog(true); }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {car.is_active && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-slate-900 border-slate-800">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Deactivate Patrol Car</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to deactivate {car.name}? It will no longer appear in the patrol page.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    className="bg-red-600 hover:bg-red-700"
                                    onClick={() => handleDeletePatrolCar(car.car_id)}
                                  >
                                    Deactivate
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
          )}

          {patrolSubTab === "vehicles" && <PatrolVehicles />}
        </TabsContent>

        {/* Live Call Options Tab */}
        <TabsContent value="livecall" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg uppercase tracking-wider flex items-center gap-2">
                <SettingsIcon className="w-5 h-5 text-blue-500" />
                Live Call Options
              </CardTitle>
              <p className="text-sm text-slate-400">
                Configure the flags and options available in live call updates
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Unit Flags Section */}
              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-3 uppercase tracking-wider">Unit Flags</h3>
                <p className="text-xs text-slate-500 mb-3">These flags appear as checkboxes when adding updates in Dispatch (e.g., Units 84, Units 98)</p>
                <div className="space-y-2">
                  {liveCallOptions?.unit_flags?.map((flag) => (
                    <div key={flag.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-sm">
                      <div className="flex items-center gap-3">
                        <Badge variant={flag.active ? "default" : "outline"} className={flag.active ? "bg-blue-600" : ""}>
                          {flag.label}
                        </Badge>
                        <span className="text-xs text-slate-500 font-mono">{flag.id}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleFlag('unit_flags', flag.id, !flag.active)}
                        >
                          {flag.active ? "Disable" : "Enable"}
                        </Button>
                        {!['units_84', 'units_98'].includes(flag.id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteFlag(flag.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Flags Section */}
              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-3 uppercase tracking-wider">Status Flags</h3>
                <p className="text-xs text-slate-500 mb-3">These flags appear for call status updates (e.g., No Further, Call Reactivated)</p>
                <div className="space-y-2">
                  {liveCallOptions?.status_flags?.map((flag) => (
                    <div key={flag.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-sm">
                      <div className="flex items-center gap-3">
                        <Badge variant={flag.active ? "default" : "outline"} className={flag.active ? "bg-green-600" : ""}>
                          {flag.label}
                        </Badge>
                        <span className="text-xs text-slate-500 font-mono">{flag.id}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleFlag('status_flags', flag.id, !flag.active)}
                        >
                          {flag.active ? "Disable" : "Enable"}
                        </Button>
                        {!['no_further', 'call_reactivated'].includes(flag.id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteFlag(flag.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add New Flag */}
              <div className="pt-4 border-t border-slate-800">
                <h3 className="text-sm font-medium text-slate-300 mb-3 uppercase tracking-wider">Add New Flag</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Select value={newFlagType} onValueChange={setNewFlagType}>
                    <SelectTrigger className="w-full sm:w-40 bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unit_flags">Unit Flag</SelectItem>
                      <SelectItem value="status_flags">Status Flag</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Flag label (e.g., Units 99)"
                    value={newFlagLabel}
                    onChange={(e) => setNewFlagLabel(e.target.value)}
                    className="flex-1 bg-slate-800 border-slate-700"
                  />
                  <Button onClick={handleAddFlag} className="btn-primary">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Flag
                  </Button>
                </div>
              </div>
              
              {/* Custom Radio Codes Section */}
              <div className="mt-6 pt-6 border-t border-slate-800">
                <h3 className="text-sm font-medium text-slate-300 mb-3 uppercase tracking-wider">Custom Radio Codes</h3>
                <p className="text-xs text-slate-500 mb-3">Add custom radio codes that will appear in Dispatch and automatically create updates when toggled.</p>
                <div className="space-y-2 mb-4">
                  {radioCodes?.codes?.map((code) => (
                    <div key={code.id} className="flex items-center justify-between bg-slate-800 px-3 py-2 rounded">
                      <span className="text-sm font-medium text-slate-200">{code.label}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                        onClick={() => handleDeleteRadioCode(code.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {(!radioCodes?.codes || radioCodes.codes.length === 0) && (
                    <p className="text-xs text-slate-500 italic">No custom radio codes configured</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newRadioCode}
                    onChange={(e) => setNewRadioCode(e.target.value)}
                    placeholder="New radio code label..."
                    className="input-dark flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddRadioCode()}
                  />
                  <Button onClick={handleAddRadioCode} className="btn-primary">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Management Tab */}
        <TabsContent value="data" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg uppercase tracking-wider flex items-center gap-2">
                <Database className="w-5 h-5 text-red-500" />
                Data Management
              </CardTitle>
              <p className="text-sm text-slate-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Warning: Deletions are permanent and cannot be undone.
              </p>
            </CardHeader>
            <CardContent>
              {/* Data Counts */}
              {dataCounts && (
                <div className="grid grid-cols-2 sm:grid-cols-7 gap-3 mb-6">
                  <div className="bg-slate-800 p-3 rounded-sm text-center">
                    <p className="text-xl sm:text-2xl font-bold text-blue-400">{dataCounts.incidents}</p>
                    <p className="text-xs text-slate-500 uppercase">Incidents</p>
                  </div>
                  <div className="bg-slate-800 p-3 rounded-sm text-center">
                    <p className="text-xl sm:text-2xl font-bold text-purple-400">{dataCounts.perpetrator_profiles}</p>
                    <p className="text-xs text-slate-500 uppercase">Profiles</p>
                  </div>
                  <div className="bg-slate-800 p-3 rounded-sm text-center">
                    <p className="text-xl sm:text-2xl font-bold text-amber-400">{dataCounts.live_calls}</p>
                    <p className="text-xs text-slate-500 uppercase">Dispatch</p>
                  </div>
                  <div className="bg-slate-800 p-3 rounded-sm text-center">
                    <p className="text-xl sm:text-2xl font-bold text-cyan-400">{dataCounts.units_count || adminUnits.length}</p>
                    <p className="text-xs text-slate-500 uppercase">Units</p>
                  </div>
                  <div className="bg-slate-800 p-3 rounded-sm text-center">
                    <p className="text-xl sm:text-2xl font-bold text-emerald-400">{dataCounts.users}</p>
                    <p className="text-xs text-slate-500 uppercase">Users</p>
                  </div>
                  <div className="bg-slate-800 p-3 rounded-sm text-center">
                    <p className="text-xl sm:text-2xl font-bold text-pink-400">{dataCounts.building_codes || adminBuildingCodes.length}</p>
                    <p className="text-xs text-slate-500 uppercase">Codes</p>
                  </div>
                  <div className="bg-slate-800 p-3 rounded-sm text-center">
                    <p className="text-xl sm:text-2xl font-bold text-orange-400">{dataCounts.event_ops || 0}</p>
                    <p className="text-xs text-slate-500 uppercase">Event Ops</p>
                  </div>
                </div>
              )}

              {/* Exports — moved from the Incidents page so admins have all
                  CSV exports in one place. */}
              <div className="mb-6 rounded-sm border border-slate-800 bg-slate-950/40 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Download className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-200">Exports</h3>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Generate a CSV. The result opens in a viewer where you can copy or download it.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start h-auto py-2"
                    onClick={() => handleExportIncidents(false)}
                    disabled={exportLoading !== null}
                  >
                    {exportLoading === "all" ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    <span className="flex-1 text-left">
                      <span className="block text-sm">All Incidents</span>
                      <span className="block text-[10px] text-slate-500">Every incident in the system</span>
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start h-auto py-2"
                    onClick={() => handleExportIncidents(true)}
                    disabled={exportLoading !== null}
                  >
                    {exportLoading === "arrests" ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Shield className="w-4 h-4 mr-2 text-emerald-400" />
                    )}
                    <span className="flex-1 text-left">
                      <span className="block text-sm">Arrests Only</span>
                      <span className="block text-[10px] text-slate-500">Incidents flagged is_arrest</span>
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start h-auto py-2"
                    onClick={handleExportBuildingCodesData}
                    disabled={exportLoading !== null}
                  >
                    {exportLoading === "codes" ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <MapPin className="w-4 h-4 mr-2 text-pink-400" />
                    )}
                    <span className="flex-1 text-left">
                      <span className="block text-sm">Building Codes</span>
                      <span className="block text-[10px] text-slate-500">All saved building codes</span>
                    </span>
                  </Button>
                </div>
              </div>

              {/* Data Type Tabs */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Button 
                  variant={dataActiveTab === "incidents" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setDataActiveTab("incidents")}
                >
                  Incidents
                </Button>
                <Button 
                  variant={dataActiveTab === "profiles" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setDataActiveTab("profiles")}
                >
                  Profiles
                </Button>
                <Button 
                  variant={dataActiveTab === "livecalls" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setDataActiveTab("livecalls")}
                >
                  Dispatch
                </Button>
                <Button
                  variant={dataActiveTab === "units" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setDataActiveTab("units")}
                >
                  Units
                </Button>
                <Button
                  variant={dataActiveTab === "patrol" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDataActiveTab("patrol")}
                >
                  Patrol
                </Button>
                <Button
                  variant={dataActiveTab === "rollcall" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDataActiveTab("rollcall")}
                >
                  Roll Call
                </Button>
                <Button
                  variant={dataActiveTab === "media" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setDataActiveTab("media"); fetchMediaFiles(); }}
                >
                  Media
                </Button>
                <Button 
                  variant={dataActiveTab === "codes" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setDataActiveTab("codes")}
                >
                  Building Codes
                </Button>
                <Button 
                  variant={dataActiveTab === "locations" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => { setDataActiveTab("locations"); fetchActiveLocations(); }}
                  className="border-cyan-600 text-cyan-400"
                >
                  📍 Active Locations
                </Button>
                <Button
                  variant={dataActiveTab === "eventops" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDataActiveTab("eventops")}
                  className="border-orange-600 text-orange-400"
                >
                  Event Ops
                </Button>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={dataSearchQuery}
                  onChange={(e) => setDataSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="pl-10 input-dark"
                />
              </div>

              <ScrollArea className="h-64">
                {/* Incidents List */}
                {dataActiveTab === "incidents" && (
                  <div className="space-y-2">
                    {adminIncidents
                      .filter(i => i.headline?.toLowerCase().includes(dataSearchQuery.toLowerCase()) || i.incident_id?.includes(dataSearchQuery))
                      .map(incident => (
                        <div key={incident.incident_id} className="p-3 bg-slate-800 rounded-sm">
                          <div className="grid grid-cols-[1fr,auto] gap-2 items-center">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{incident.headline || "No headline"}</p>
                              <p className="text-xs text-slate-500 truncate">{incident.incident_id} • {incident.date_time?.split("T")[0]} • {incident.status}</p>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-red-400">Permanently Delete Incident?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete &quot;{incident.headline}&quot;. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                  <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                    onClick={() => handleDeleteIncident(incident.incident_id)}
                                  >
                                    Delete Permanently
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    {adminIncidents.length === 0 && (
                      <p className="text-slate-500 text-center py-8">No incidents found</p>
                    )}
                  </div>
                )}

                {/* Profiles List */}
                {dataActiveTab === "profiles" && (
                  <div className="space-y-2">
                    {adminProfiles
                      .filter(p => p.name?.toLowerCase().includes(dataSearchQuery.toLowerCase()) || p.profile_id?.includes(dataSearchQuery))
                      .map(profile => (
                        <div key={profile.profile_id} className="p-3 bg-slate-800 rounded-sm">
                          <div className="grid grid-cols-[1fr,auto] gap-2 items-center">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{profile.name}</p>
                              <p className="text-xs text-slate-500 truncate">{profile.profile_id}</p>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-red-400">Permanently Delete Profile?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete &quot;{profile.name}&quot;. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                  <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                    onClick={() => handleDeleteProfile(profile.profile_id)}
                                  >
                                    Delete Permanently
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    {adminProfiles.length === 0 && (
                      <p className="text-slate-500 text-center py-8">No profiles found</p>
                    )}
                  </div>
                )}

                {/* Live Calls List */}
                {dataActiveTab === "livecalls" && (
                  <div className="space-y-2">
                    {adminLiveCalls
                      .filter(c => c.call_type?.toLowerCase().includes(dataSearchQuery.toLowerCase()) || c.location?.toLowerCase().includes(dataSearchQuery.toLowerCase()) || c.call_id?.includes(dataSearchQuery))
                      .map(call => (
                        <div key={call.call_id} className="p-3 bg-slate-800 rounded-sm">
                          <div className="grid grid-cols-[1fr,auto] gap-2 items-center">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{call.call_type || "Unknown"} - {call.location || "No location"}</p>
                              <p className="text-xs text-slate-500 truncate">{call.call_id} • {call.status}</p>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-red-400">Permanently Delete Live Call?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete this live call. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                  <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                    onClick={() => handleDeleteLiveCall(call.call_id)}
                                  >
                                    Delete Permanently
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    {adminLiveCalls.length === 0 && (
                      <p className="text-slate-500 text-center py-8">No dispatch calls found</p>
                    )}
                  </div>
                )}

                {/* Units List */}
                {dataActiveTab === "units" && (
                  <div className="space-y-2">
                    {adminUnits.map((unit) => (
                      <div key={unit.unit_id} className="p-3 bg-slate-800 rounded-sm">
                        <div className="grid grid-cols-[1fr,auto] gap-2 items-center">
                          <div className="min-w-0">
                            <p className="font-mono font-bold truncate">{unit.name}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {unit.is_dispatcher && <Badge className="text-xs bg-amber-900/50 text-amber-200">Dispatcher</Badge>}
                              {unit.is_investigator && <Badge className="text-xs bg-blue-900/50 text-blue-200">Investigator</Badge>}
                              {!unit.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                            </div>
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-red-500">Permanently Delete Unit</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to permanently delete unit <strong>{unit.name}</strong>? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                  onClick={() => handleDeleteUnit(unit.unit_id)}
                                >
                                  Delete Permanently
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                    {adminUnits.length === 0 && (
                      <p className="text-slate-500 text-center py-8">No units found</p>
                    )}
                  </div>
                )}

                {/* Patrol Data */}
                {dataActiveTab === "patrol" && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-400">Manage patrol cars.</p>

                    {/* Patrol Cars List */}
                    <div className="border-t border-slate-700 pt-4">
                      <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                        <Car className="w-4 h-4" />
                        Patrol Cars ({patrolCars.length})
                      </h4>
                      <ScrollArea className="h-[300px]">
                        <div className="space-y-2">
                          {patrolCars.map((car) => (
                            <div
                              key={car.car_id}
                              className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <Car className={`w-5 h-5 ${car.is_active ? 'text-emerald-500' : 'text-slate-600'}`} />
                                <span className={`font-medium ${!car.is_active ? 'text-slate-500' : ''}`}>
                                  {car.name}
                                </span>
                                {!car.is_active && (
                                  <Badge variant="outline" className="text-xs">Inactive</Badge>
                                )}
                                {car.status === "out_of_service" && (
                                  <Badge variant="destructive" className="text-xs">Out of Service</Badge>
                                )}
                                {car.status === "active_patrol" && (
                                  <Badge className="bg-blue-600 text-xs">{car.assigned_unit}</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setEditCar(car); setShowEditCarDialog(true); }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {car.is_active && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Deactivate Patrol Car</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Are you sure you want to deactivate {car.name}? It will no longer appear in the patrol page.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          className="bg-red-600 hover:bg-red-700"
                                          onClick={() => handleDeletePatrolCar(car.car_id)}
                                        >
                                          Deactivate
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            </div>
                          ))}
                          {patrolCars.length === 0 && (
                            <p className="text-slate-500 text-center py-8">No patrol cars configured</p>
                          )}
                        </div>
                      </ScrollArea>
                    </div>

                    {/* FSD To Do Items */}
                    <div className="border-t border-slate-700 pt-4">
                      <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                        <ClipboardList className="w-4 h-4" />
                        FSD To Do Items ({adminTodos.length})
                      </h4>
                      <ScrollArea className="h-[300px]">
                        <div className="space-y-2">
                          {adminTodos.map((todo) => (
                            <div
                              key={todo.todo_id}
                              className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                            >
                              <div className="min-w-0 flex-1">
                                <span className={`text-sm ${todo.completed ? "line-through text-slate-500" : ""}`}>
                                  {todo.text}
                                </span>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  by {todo.created_by_name} · {todo.updates?.length || 0} updates
                                  {todo.completed && ` · completed by ${todo.completed_by_name}`}
                                </p>
                              </div>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 flex-shrink-0">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Todo</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to permanently delete this todo? This cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-red-600 hover:bg-red-700"
                                      onClick={() => handleDeleteTodo(todo.todo_id)}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          ))}
                          {adminTodos.length === 0 && (
                            <p className="text-slate-500 text-center py-8">No to do items</p>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  </div>
                )}

                {/* Roll Call Data */}
                {dataActiveTab === "rollcall" && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-700">
                      <span className="text-sm text-slate-400">{adminRollCalls.length} roll calls</span>
                    </div>

                    {/* Roll Calls List */}
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {adminRollCalls
                          .filter(rc =>
                            rc.parsha?.toLowerCase().includes(dataSearchQuery.toLowerCase()) ||
                            rc.rollcall_id?.toLowerCase().includes(dataSearchQuery.toLowerCase()) ||
                            rc.created_by_name?.toLowerCase().includes(dataSearchQuery.toLowerCase()) ||
                            (rc.assigned_units || []).some(u => u.toLowerCase().includes(dataSearchQuery.toLowerCase()))
                          )
                          .map(rc => (
                            <div key={rc.rollcall_id} className="p-3 bg-slate-800 rounded-sm">
                              <div className="grid grid-cols-[1fr,auto] gap-2 items-center">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {new Date(rc.created_at).toLocaleDateString("en-US", {
                                      weekday: "short",
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric"
                                    })}
                                    {rc.parsha && <span className="text-amber-400 ml-2">({rc.parsha})</span>}
                                  </p>
                                  <p className="text-xs text-slate-500 truncate">
                                    {rc.assigned_units?.length || 0} units • {rc.status} • by {rc.created_by_name || "Unknown"}
                                  </p>
                                </div>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle className="text-red-400">Permanently Delete Roll Call?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will permanently delete the roll call from {new Date(rc.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}{rc.parsha ? ` (${rc.parsha})` : ""}. This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                      <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                        onClick={() => handleDeleteRollCall(rc.rollcall_id)}
                                      >
                                        Delete Permanently
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                          ))}
                        {adminRollCalls.length === 0 && (
                          <p className="text-slate-500 text-center py-8">No roll calls found</p>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Media Files List */}
                {dataActiveTab === "media" && (
                  <div className="space-y-4">
                    {/* Delete All Embedded Media Button */}
                    <div className="p-4 bg-red-950/30 border border-red-900/50 rounded-lg">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-medium text-red-400 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Delete All Embedded Media
                          </h4>
                          <p className="text-xs text-slate-400 mt-1">
                            Removes all base64 media data from incidents and live calls. R2 uploads are preserved.
                          </p>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm" className="whitespace-nowrap">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete All Embedded
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-red-500">Delete All Embedded Media?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete all base64 media data embedded in incidents and live calls. 
                                Media files uploaded to R2 storage will NOT be affected.
                                <br /><br />
                                <strong>This action cannot be undone.</strong>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                              <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                onClick={async () => {
                                  try {
                                    const response = await axios.post(`${API}/delete-all-embedded-media`, {}, { withCredentials: true });
                                    toast.success(`Deleted embedded media from ${response.data.incidents_cleared} incidents and ${response.data.live_calls_cleared} live calls`);
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || "Failed to delete embedded media");
                                  }
                                }}
                              >
                                Delete All Embedded Media
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>

                    {/* Media Files List */}
                    <div className="space-y-2">
                    {adminMediaFiles
                      .filter(f => f.filename?.toLowerCase().includes(dataSearchQuery.toLowerCase()) || f.file_id?.includes(dataSearchQuery))
                      .map((file) => (
                        <div key={file.file_id} className="p-3 bg-slate-800 rounded-sm">
                          <div className="grid grid-cols-[1fr,auto] gap-2 items-center">
                            <div className="min-w-0 flex items-center gap-2">
                              {(file.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i) || file.file_id?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) ? (
                                <img 
                                  src={`${API}/uploads/${file.file_id}`} 
                                  alt={file.filename} 
                                  className="w-12 h-12 object-cover rounded"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="w-12 h-12 bg-slate-700 rounded flex items-center justify-center">
                                  <FileText className="w-6 h-6 text-slate-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{file.filename || file.file_id}</p>
                                <p className="text-xs text-slate-500 truncate">
                                  {file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' }) : 'Unknown date'}
                                  {file.storage && ` • ${file.storage}`}
                                </p>
                              </div>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-red-500">Permanently Delete Media</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to permanently delete <strong>{file.filename || file.file_id}</strong>? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                  <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                    onClick={() => handleDeleteMedia(file.file_id, file.filename)}
                                  >
                                    Delete Permanently
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    {adminMediaFiles.length === 0 && (
                      <p className="text-slate-500 text-center py-8">No media files found</p>
                    )}
                    </div>
                  </div>
                )}

                {/* Building Codes */}
                {dataActiveTab === "codes" && (
                  <div className="space-y-2">
                    {adminBuildingCodes
                      .filter(code => 
                        code.address?.toLowerCase().includes(dataSearchQuery.toLowerCase()) || 
                        code.building_code?.toLowerCase().includes(dataSearchQuery.toLowerCase())
                      )
                      .map((code) => (
                        <div key={code.code_id} className="p-3 bg-slate-800 rounded-sm">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-sm font-medium">{code.address}</p>
                                <Badge variant="outline" className="text-xs">{code.building_code}</Badge>
                              </div>
                              {code.notes && (
                                <p className="text-xs text-slate-400 mb-1">{code.notes}</p>
                              )}
                              <p className="text-xs text-slate-500">
                                Created by {code.created_by_name} • {new Date(code.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}
                              </p>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-900/20 flex-shrink-0 h-10 w-10 min-w-[40px]">
                                  <Trash2 className="w-5 h-5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-red-500">Permanently Delete Building Code</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to permanently delete the building code for <strong>{code.address}</strong> ({code.building_code})? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                  <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                    onClick={() => handleDeleteBuildingCode(code.code_id)}
                                  >
                                    Delete Permanently
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    {adminBuildingCodes.length === 0 && (
                      <p className="text-slate-500 text-center py-8">No building codes found</p>
                    )}
                  </div>
                )}

                {/* Active Locations */}
                {dataActiveTab === "locations" && (
                  <div className="space-y-3">
                    {/* Header with refresh and stop all */}
                    <div className="flex items-center justify-between p-3 bg-cyan-900/20 border border-cyan-700 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-cyan-400">📍 Active Location Shares</p>
                        <p className="text-xs text-slate-400">{activeLocations.length} user(s) currently sharing</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={fetchActiveLocations} disabled={loadingLocations}>
                          <RefreshCw className={`w-4 h-4 ${loadingLocations ? 'animate-spin' : ''}`} />
                        </Button>
                        {activeLocations.length > 0 && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm">Stop All</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-slate-900 border-slate-800">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-red-400">Stop All Location Sharing?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will force stop location sharing for all {activeLocations.length} user(s). They will need to manually restart sharing.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={forceStopAllLocations}>
                                  Stop All
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>

                    {loadingLocations ? (
                      <div className="flex justify-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin text-slate-500" />
                      </div>
                    ) : activeLocations.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No active location shares</p>
                    ) : (
                      activeLocations
                        .filter(loc => 
                          loc.username?.toLowerCase().includes(dataSearchQuery.toLowerCase()) ||
                          loc.live_call_id?.includes(dataSearchQuery)
                        )
                        .map((loc) => (
                          <div key={loc.user_id} className="p-3 bg-slate-800 rounded-lg">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-sm font-medium text-cyan-400">{loc.username || loc.user_id}</p>
                                  {loc.badge_number && <Badge variant="outline" className="text-xs">{loc.badge_number}</Badge>}
                                </div>
                                <p className="text-xs text-slate-400">
                                  📍 {loc.latitude?.toFixed(5)}, {loc.longitude?.toFixed(5)} • ±{Math.round(loc.accuracy || 0)}m
                                </p>
                                {loc.live_call_id && (
                                  <p className="text-xs text-amber-400 mt-1">
                                    Call: {loc.live_call_id}
                                  </p>
                                )}
                                <p className="text-xs text-slate-500 mt-1">
                                  Last update: {loc.last_update ? new Date(loc.last_update).toLocaleTimeString('en-US', { timeZone: 'America/New_York' }) : 'N/A'}
                                </p>
                              </div>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-900/20">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-slate-900 border-slate-800">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="text-red-400">Stop Location Sharing?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will force stop location sharing for <strong>{loc.username}</strong>. They will need to manually restart sharing.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      className="bg-red-600 hover:bg-red-700"
                                      onClick={() => forceStopUserLocation(loc.user_id, loc.username)}
                                    >
                                      Force Stop
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                )}

                {/* Event Ops List */}
                {dataActiveTab === "eventops" && (
                  <div className="space-y-2">
                    {adminEventOps
                      .filter(e => e.name?.toLowerCase().includes(dataSearchQuery.toLowerCase()) || e.event_id?.includes(dataSearchQuery))
                      .map(ev => (
                        <div key={ev.event_id} className="p-3 bg-slate-800 rounded-sm">
                          <div className="grid grid-cols-[1fr,auto] gap-2 items-center">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{ev.name || "Untitled Event"}</p>
                              <p className="text-xs text-slate-500 truncate">{ev.event_id} • {ev.date} • {ev.status}</p>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-slate-900 border-slate-800 mx-4">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-red-400">Permanently Delete Event?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete &quot;{ev.name}&quot; and all its audit logs. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                  <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                                    onClick={() => handleDeleteEventOp(ev.event_id)}
                                  >
                                    Delete Permanently
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    {adminEventOps.length === 0 && (
                      <p className="text-slate-500 text-center py-8">No event operations found</p>
                    )}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Unit Records — admin-only delete of ID cards & plaques.
              Issuance and edits live under Units → ID. */}
          <Card className="bg-slate-900 border-slate-800 mb-4 mt-4">
            <CardHeader>
              <CardTitle className="text-lg uppercase tracking-wider flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-400" />Unit ID Cards ({unitRecords.id_cards.length})
              </CardTitle>
              <p className="text-sm text-slate-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Deletions are permanent. Issuance and edits happen under Units → ID.
              </p>
            </CardHeader>
            <CardContent>
              {recordsLoading ? (
                <p className="text-center text-slate-500 py-6 text-sm">Loading…</p>
              ) : unitRecords.id_cards.length === 0 ? (
                <p className="text-center text-slate-500 py-6 text-sm">No ID card records.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500">
                      <tr><th className="text-left p-2">Unit</th><th className="text-left p-2">Card #</th><th className="text-left p-2">Issued</th><th className="text-left p-2">Expires</th><th className="text-left p-2">Returned</th><th className="text-left p-2">Notes</th><th className="p-2"></th></tr>
                    </thead>
                    <tbody>
                      {unitRecords.id_cards.map(rec => (
                        <tr key={rec.record_id} className="border-t border-slate-800">
                          <td className="p-2 font-mono">{rec.unit_name}{!rec.unit_active && <Badge variant="outline" className="ml-2 text-[10px]">Deactivated</Badge>}</td>
                          <td className="p-2 font-mono text-blue-300">{rec.card_number || "—"}</td>
                          <td className="p-2">{rec.issued_at || "—"}</td>
                          <td className="p-2">{rec.expires_at || "—"}</td>
                          <td className="p-2">{rec.returned_at || "—"}</td>
                          <td className="p-2 text-slate-400 max-w-[280px] truncate">{rec.notes || ""}</td>
                          <td className="p-2 text-right"><Button size="sm" variant="destructive" onClick={() => handleDeleteCreditCard(rec)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg uppercase tracking-wider flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-400" />Unit Plaques ({unitRecords.plaques.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recordsLoading ? (
                <p className="text-center text-slate-500 py-6 text-sm">Loading…</p>
              ) : unitRecords.plaques.length === 0 ? (
                <p className="text-center text-slate-500 py-6 text-sm">No plaque records.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500">
                      <tr><th className="text-left p-2">Unit</th><th className="text-left p-2">Plaque #</th><th className="text-left p-2">Issued</th><th className="text-left p-2">Expires</th><th className="text-left p-2">Returned</th><th className="text-left p-2">Notes</th><th className="p-2"></th></tr>
                    </thead>
                    <tbody>
                      {unitRecords.plaques.map(rec => (
                        <tr key={rec.record_id} className="border-t border-slate-800">
                          <td className="p-2 font-mono">{rec.unit_name}{!rec.unit_active && <Badge variant="outline" className="ml-2 text-[10px]">Deactivated</Badge>}</td>
                          <td className="p-2 font-mono text-amber-300">{rec.plaque_number || "—"}</td>
                          <td className="p-2">{rec.issued_at || "—"}</td>
                          <td className="p-2">{rec.expires_at || "—"}</td>
                          <td className="p-2">{rec.returned_at || "—"}</td>
                          <td className="p-2 text-slate-400 max-w-[280px] truncate">{rec.notes || ""}</td>
                          <td className="p-2 text-right"><Button size="sm" variant="destructive" onClick={() => handleDeletePlaque(rec)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Known Locations Tab */}
        <TabsContent value="locations" className="mt-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg uppercase tracking-wider flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-500" />
                Known Locations
              </CardTitle>
              <p className="text-sm text-slate-400">
                Pre-saved addresses that auto-suggest in the LiveCalls / Dispatch new-call form alongside Google results.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 bg-slate-800 rounded">
                <div className="md:col-span-2">
                  <Label className="text-xs">Name (display label) *</Label>
                  <Input
                    value={knownLocationForm.name}
                    onChange={(e) => setKnownLocationForm({ ...knownLocationForm, name: e.target.value })}
                    className="input-dark h-8"
                    placeholder="e.g., Headquarters / Beth Medrash"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Address (optional)</Label>
                  <Input
                    value={knownLocationForm.address}
                    onChange={(e) => setKnownLocationForm({ ...knownLocationForm, address: e.target.value })}
                    className="input-dark h-8"
                    placeholder="e.g., 1234 Eastern Pkwy"
                  />
                </div>
                <div>
                  <Label className="text-xs">Cross Streets</Label>
                  <Input
                    value={knownLocationForm.cross_streets}
                    onChange={(e) => setKnownLocationForm({ ...knownLocationForm, cross_streets: e.target.value })}
                    className="input-dark h-8"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Label className="text-xs">Location Notes</Label>
                  <Input
                    value={knownLocationForm.location_notes}
                    onChange={(e) => setKnownLocationForm({ ...knownLocationForm, location_notes: e.target.value })}
                    className="input-dark h-8"
                    placeholder="Optional (apt #, landmarks…)"
                  />
                </div>

                {/* Map picker */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">Pin on Map (optional)</Label>
                    <div className="flex items-center gap-2">
                      {knownLocationForm.latitude != null && knownLocationForm.longitude != null && (
                        <>
                          <span className="text-[10px] text-emerald-400 font-mono">
                            {knownLocationForm.latitude.toFixed(5)}, {knownLocationForm.longitude.toFixed(5)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[11px] text-red-400"
                            onClick={() => setKnownLocationForm({ ...knownLocationForm, latitude: null, longitude: null })}
                          >
                            Clear pin
                          </Button>
                        </>
                      )}
                      {(knownLocationForm.latitude == null || knownLocationForm.longitude == null) && (
                        <span className="text-[10px] text-slate-500 italic">Click map to drop a pin</span>
                      )}
                    </div>
                  </div>
                  <div className="h-64 w-full rounded overflow-hidden border border-slate-700">
                    <MapContainer
                      center={[
                        knownLocationForm.latitude ?? 40.6694,
                        knownLocationForm.longitude ?? -73.9422,
                      ]}
                      zoom={knownLocationForm.latitude != null ? 16 : 13}
                      style={{ height: "100%", width: "100%", background: "#1a1a2e" }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; OpenStreetMap"
                      />
                      <KnownLocPinPicker
                        value={
                          knownLocationForm.latitude != null && knownLocationForm.longitude != null
                            ? { lat: knownLocationForm.latitude, lng: knownLocationForm.longitude }
                            : null
                        }
                        onChange={({ lat, lng }) => setKnownLocationForm({ ...knownLocationForm, latitude: lat, longitude: lng })}
                      />
                    </MapContainer>
                  </div>
                </div>

                <div className="md:col-span-2 flex justify-end gap-2">
                  {knownLocationForm.id && (
                    <Button variant="outline" size="sm" onClick={() => setKnownLocationForm({ id: null, name: "", address: "", cross_streets: "", location_notes: "", latitude: null, longitude: null })}>
                      Cancel
                    </Button>
                  )}
                  <Button size="sm" onClick={handleSaveKnownLocation} disabled={savingKnownLocation}>
                    {savingKnownLocation ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                    {knownLocationForm.id ? "Update" : "Add"}
                  </Button>
                </div>
              </div>

              {/* Existing list */}
              {knownLocations.length === 0 ? (
                <p className="text-center text-sm text-slate-500 py-6">No known locations yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {knownLocations.map((loc) => (
                    <div key={loc.id} className="p-2 bg-slate-800 rounded flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-200">{loc.name}</p>
                        {loc.address && <p className="text-xs text-slate-400">{loc.address}</p>}
                        {loc.cross_streets && <p className="text-[11px] text-emerald-400">Cross: {loc.cross_streets}</p>}
                        {loc.location_notes && <p className="text-[11px] text-slate-500 italic">{loc.location_notes}</p>}
                        {loc.latitude != null && loc.longitude != null && (
                          <p className="text-[11px] text-cyan-400 font-mono">
                            📍 <a
                              href={`https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:text-cyan-300"
                            >
                              {loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}
                            </a>
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-400" onClick={() => setKnownLocationForm({ id: loc.id, name: loc.name || "", address: loc.address || "", cross_streets: loc.cross_streets || "", location_notes: loc.location_notes || "", latitude: loc.latitude ?? null, longitude: loc.longitude ?? null })}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-slate-900 border-slate-700">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete known location?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Remove "{loc.name}"{loc.address ? ` (${loc.address})` : ""}. This won't affect existing calls/incidents.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-slate-700 border-slate-600">Cancel</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDeleteKnownLocation(loc.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="mt-4">
          <div className="space-y-4">
            {/* Sub-tabs for Audit */}
            <div className="flex flex-wrap gap-2 mb-4 items-center justify-between">
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant={auditSubTab === "logs" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setAuditSubTab("logs")}
                >
                  Activity Logs
                </Button>
                <Button 
                  variant={auditSubTab === "blocked" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setAuditSubTab("blocked")}
                  className={blockedIPs.length > 0 ? "border-red-500 text-red-400" : ""}
                >
                  🚫 Blocked IPs ({blockedIPs.length})
                </Button>
                <Button
                  variant={auditSubTab === "attempts" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAuditSubTab("attempts")}
                >
                  Failed Attempts
                </Button>
                <Button
                  variant={auditSubTab === "alerts" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setAuditSubTab("alerts"); fetchEmailAlertsConfig(); fetchWhatsappConfig(); fetchBotStatus(); fetchAlertLine(); fetchHotlineWa(); fetchDeepgram(); }}
                  className={alertEmails.length > 0 ? "border-green-500 text-green-400" : ""}
                >
                  <Mail className="w-3 h-3 mr-1" />
                  Email Alerts
                </Button>
                <Button
                  variant={auditSubTab === "downloads" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setAuditSubTab("downloads"); fetchMediaDownloads(); }}
                >
                  <Download className="w-3 h-3 mr-1" />
                  Media Downloads
                </Button>
                <Button
                  variant={auditSubTab === "admin-activity" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setAuditSubTab("admin-activity"); fetchAdminActivity(1); }}
                  className="border-orange-500/50 text-orange-400"
                >
                  <Shield className="w-3 h-3 mr-1" />
                  Admin Activity
                </Button>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExportAuditLogs}
                className="border-green-600 text-green-400 hover:bg-green-900/20"
              >
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
            </div>

            {/* Blocked IPs Sub-tab */}
            {auditSubTab === "blocked" && (
              <div className="space-y-4">
                {/* Manual Block Form */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm uppercase tracking-wider">Manually Block IP</CardTitle>
                  </CardHeader>
                  <CardContent className="py-2">
                    <div className="flex flex-wrap gap-2 items-end">
                      <div className="flex-1 min-w-[150px]">
                        <Label className="text-xs">IP Address</Label>
                        <Input
                          value={manualBlockIP}
                          onChange={(e) => setManualBlockIP(e.target.value)}
                          placeholder="192.168.1.1"
                          className="input-dark h-8"
                        />
                      </div>
                      <div className="flex-1 min-w-[150px]">
                        <Label className="text-xs">Reason</Label>
                        <Input
                          value={manualBlockReason}
                          onChange={(e) => setManualBlockReason(e.target.value)}
                          placeholder="Suspicious activity"
                          className="input-dark h-8"
                        />
                      </div>
                      <div className="w-24">
                        <Label className="text-xs">Hours</Label>
                        <Input
                          type="number"
                          value={manualBlockHours}
                          onChange={(e) => setManualBlockHours(parseInt(e.target.value) || 24)}
                          className="input-dark h-8"
                          min={1}
                          max={720}
                          disabled={manualBlockForever}
                        />
                      </div>
                      {/* Forever: blocked until an admin presses Unblock. */}
                      <label className="flex items-center gap-1.5 h-8 text-xs text-slate-300 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={manualBlockForever}
                          onChange={(e) => setManualBlockForever(e.target.checked)}
                          className="w-4 h-4 accent-red-600"
                        />
                        Forever
                      </label>
                      <Button onClick={handleManualBlockIP} size="sm" className="bg-red-600 hover:bg-red-700">
                        Block IP
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Currently Blocked IPs */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm uppercase tracking-wider text-red-400">
                      Currently Blocked IPs ({blockedIPs.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {blockedIPs.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">No IPs currently blocked</div>
                    ) : (
                      <ScrollArea className="h-[400px]">
                        <div className="divide-y divide-slate-800">
                          {blockedIPs.map((block, idx) => (
                            <div key={block.block_id || idx} className="p-3 hover:bg-slate-800/50">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-mono text-red-400 font-bold">{block.ip_address}</span>
                                    {block.manual_block && <Badge className="bg-amber-600 text-xs">Manual</Badge>}
                                    {block.permanent && <Badge className="bg-red-700 text-xs">Forever</Badge>}
                                  </div>
                                  <p className="text-sm text-slate-400">{block.reason}</p>
                                  <div className="text-xs text-slate-500 mt-1">
                                    <span>Blocked: {formatAuditDate(block.blocked_at)}</span>
                                    <span className="mx-2">•</span>
                                    <span>Until: {block.permanent ? "Forever (until unblocked)" : formatAuditDate(block.blocked_until)}</span>
                                  </div>
                                  {block.recent_attempts?.length > 0 && (
                                    <div className="mt-2 text-xs">
                                      <span className="text-slate-500">Recent attempts: </span>
                                      {block.recent_attempts.slice(0, 3).map((a, i) => (
                                        <span key={i} className="text-slate-400">
                                          {a.details?.email || a.attempt_type}{i < 2 && block.recent_attempts.length > i + 1 ? ", " : ""}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUnblockIP(block.ip_address)}
                                  className="text-green-400 border-green-600 hover:bg-green-900/20"
                                >
                                  Unblock
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>

                {/* Manual Block Email Form */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm uppercase tracking-wider">Manually Block Email</CardTitle>
                  </CardHeader>
                  <CardContent className="py-2">
                    <div className="flex flex-wrap gap-2 items-end">
                      <div className="flex-1 min-w-[150px]">
                        <Label className="text-xs">Email Address</Label>
                        <Input
                          value={manualBlockEmail}
                          onChange={(e) => setManualBlockEmail(e.target.value)}
                          placeholder="user@example.com"
                          className="input-dark h-8"
                        />
                      </div>
                      <div className="flex-1 min-w-[150px]">
                        <Label className="text-xs">Reason</Label>
                        <Input
                          value={manualBlockEmailReason}
                          onChange={(e) => setManualBlockEmailReason(e.target.value)}
                          placeholder="Suspicious activity"
                          className="input-dark h-8"
                        />
                      </div>
                      <div className="w-24">
                        <Label className="text-xs">Hours</Label>
                        <Input
                          type="number"
                          value={manualBlockEmailHours}
                          onChange={(e) => setManualBlockEmailHours(parseInt(e.target.value) || 24)}
                          className="input-dark h-8"
                          min={1}
                          max={720}
                        />
                      </div>
                      <Button onClick={handleManualBlockEmail} size="sm" className="bg-red-600 hover:bg-red-700">
                        Block Email
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Currently Blocked Emails */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm uppercase tracking-wider text-red-400">
                      Currently Blocked Emails ({blockedEmails.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {blockedEmails.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">No emails currently blocked</div>
                    ) : (
                      <ScrollArea className="h-[400px]">
                        <div className="divide-y divide-slate-800">
                          {blockedEmails.map((block, idx) => (
                            <div key={block.block_id || idx} className="p-3 hover:bg-slate-800/50">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-mono text-red-400 font-bold">{block.email}</span>
                                    {block.manual_block && <Badge className="bg-amber-600 text-xs">Manual</Badge>}
                                  </div>
                                  <p className="text-sm text-slate-400">{block.reason}</p>
                                  <div className="text-xs text-slate-500 mt-1">
                                    <span>Blocked: {formatAuditDate(block.blocked_at)}</span>
                                    <span className="mx-2">•</span>
                                    <span>Until: {formatAuditDate(block.blocked_until)}</span>
                                  </div>
                                  {block.last_ip && (
                                    <div className="text-xs text-slate-500 mt-1">
                                      Last IP: {block.last_ip}
                                    </div>
                                  )}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUnblockEmail(block.email)}
                                  className="text-green-400 border-green-600 hover:bg-green-900/20"
                                >
                                  Unblock
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Failed Attempts Sub-tab */}
            {auditSubTab === "attempts" && (
              <div className="space-y-4">
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm uppercase tracking-wider">
                      Recent Failed Attempts ({failedAttempts.length})
                    </CardTitle>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-red-400 border-red-600">
                          Clear All
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-slate-900 border-slate-800">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Clear All Failed Attempts?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will delete all failed attempt records. This won't unblock any IPs.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleClearFailedAttempts()} className="bg-red-600 hover:bg-red-700">
                            Clear All
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardHeader>
                  <CardContent className="p-0">
                    {failedAttempts.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">No failed attempts recorded</div>
                    ) : (
                      <ScrollArea className="h-[400px]">
                        <div className="divide-y divide-slate-800">
                          {failedAttempts.map((attempt, idx) => (
                            <div key={attempt.attempt_id || idx} className="p-3 hover:bg-slate-800/50">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-mono text-sm">{attempt.ip_address}</span>
                                  <Badge className={
                                    attempt.attempt_type === "login" ? "bg-red-600" :
                                    attempt.attempt_type === "pin" ? "bg-amber-600" :
                                    attempt.attempt_type === "admin_pin" ? "bg-purple-600" : "bg-slate-600"
                                  }>
                                    {attempt.attempt_type}
                                  </Badge>
                                  {attempt.details?.email && (
                                    <span className="text-sm text-blue-400">{attempt.details.email}</span>
                                  )}
                                  {attempt.details?.reason && (
                                    <span className="text-xs text-slate-500">({attempt.details.reason})</span>
                                  )}
                                </div>
                                <span className="text-xs text-slate-500 whitespace-nowrap">{formatAuditDate(attempt.timestamp)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Email Alerts Sub-tab */}
            {auditSubTab === "alerts" && (
              <div className="space-y-4">
                {/* Email Configuration */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email Configuration
                      </CardTitle>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7"
                        onClick={() => setShowEmailConfig(!showEmailConfig)}
                      >
                        {showEmailConfig ? "Cancel" : "Configure"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="py-2">
                    {!showEmailConfig ? (
                      <>
                        {emailAlertsConfig?.email_configured ? (
                          <div className="flex items-center gap-2 text-green-400">
                            <Check className="w-4 h-4" />
                            <span>
                              {emailAlertsConfig.provider === "resend" ? "Resend" : "SMTP"} Configured
                            </span>
                            <span className="text-slate-500 text-sm">
                              {emailAlertsConfig.provider === "resend"
                                ? "(Resend API)"
                                : `(${emailAlertsConfig.smtp_host})`}
                              {emailAlertsConfig.config_source === "database" && (
                                <Badge variant="outline" className="ml-2 text-xs">In-App</Badge>
                              )}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-amber-400">
                            <AlertCircle className="w-4 h-4" />
                            <span>Email Not Configured</span>
                            <span className="text-slate-500 text-sm ml-2">Click Configure to set up email</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="space-y-3">
                        {/* Provider Selector */}
                        <div className="flex gap-2 p-2 bg-slate-800 rounded">
                          <Button
                            size="sm"
                            variant={emailProvider === "resend" ? "default" : "ghost"}
                            className={emailProvider === "resend" ? "bg-blue-600" : ""}
                            onClick={() => setEmailProvider("resend")}
                          >
                            Resend API
                          </Button>
                          <Button
                            size="sm"
                            variant={emailProvider === "smtp" ? "default" : "ghost"}
                            className={emailProvider === "smtp" ? "bg-blue-600" : ""}
                            onClick={() => setEmailProvider("smtp")}
                          >
                            SMTP
                          </Button>
                        </div>

                        {emailProvider === "resend" ? (
                          /* Resend Configuration */
                          <div className="space-y-3">
                            <div className="p-2 bg-blue-900/30 border border-blue-800 rounded text-xs text-blue-300">
                              Resend is recommended for Railway hosting. Get your API key from{" "}
                              <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">
                                resend.com
                              </a>
                            </div>
                            <div>
                              <Label className="text-xs text-slate-400">Resend API Key</Label>
                              <Input
                                type="password"
                                value={resendConfig.api_key}
                                onChange={(e) => setResendConfig({...resendConfig, api_key: e.target.value})}
                                placeholder={emailAlertsConfig?.resend_configured ? "••••••••" : "re_..."}
                                className="input-dark mt-1"
                              />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-slate-400">From Email</Label>
                                <Input
                                  value={resendConfig.from_email}
                                  onChange={(e) => setResendConfig({...resendConfig, from_email: e.target.value})}
                                  placeholder="onboarding@resend.dev"
                                  className="input-dark mt-1"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                  Use onboarding@resend.dev for testing, or your verified domain
                                </p>
                              </div>
                              <div>
                                <Label className="text-xs text-slate-400">From Name</Label>
                                <Input
                                  value={resendConfig.from_name}
                                  onChange={(e) => setResendConfig({...resendConfig, from_name: e.target.value})}
                                  placeholder="TARU Alerts"
                                  className="input-dark mt-1"
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* SMTP Configuration */
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-slate-400">SMTP Host</Label>
                                <Input
                                  value={smtpConfig.host}
                                  onChange={(e) => setSmtpConfig({...smtpConfig, host: e.target.value})}
                                  placeholder="smtp.gmail.com"
                                  className="input-dark mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-400">SMTP Port</Label>
                                <Input
                                  type="number"
                                  value={smtpConfig.port}
                                  onChange={(e) => setSmtpConfig({...smtpConfig, port: parseInt(e.target.value) || 465})}
                                  placeholder="465"
                                  className="input-dark mt-1"
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-2 p-2 bg-slate-800/50 rounded">
                              <Checkbox
                                id="use_ssl"
                                checked={smtpConfig.use_ssl}
                                onCheckedChange={(checked) => setSmtpConfig({...smtpConfig, use_ssl: checked})}
                              />
                              <Label htmlFor="use_ssl" className="text-sm text-slate-300 cursor-pointer">
                                Enable SSL/TLS (required for port 465)
                              </Label>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-slate-400">SMTP Username</Label>
                                <Input
                                  value={smtpConfig.user}
                                  onChange={(e) => setSmtpConfig({...smtpConfig, user: e.target.value})}
                                  placeholder="your-email@gmail.com"
                                  className="input-dark mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-400">SMTP Password</Label>
                                <Input
                                  type="password"
                                  value={smtpConfig.password}
                                  onChange={(e) => setSmtpConfig({...smtpConfig, password: e.target.value})}
                                  placeholder={emailAlertsConfig?.config_source === "database" ? "••••••••" : "App password"}
                                  className="input-dark mt-1"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-slate-400">From Email</Label>
                                <Input
                                  value={smtpConfig.from_email}
                                  onChange={(e) => setSmtpConfig({...smtpConfig, from_email: e.target.value})}
                                  placeholder="alerts@yourdomain.com"
                                  className="input-dark mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-400">From Name</Label>
                                <Input
                                  value={smtpConfig.from_name}
                                  onChange={(e) => setSmtpConfig({...smtpConfig, from_name: e.target.value})}
                                  placeholder="TARU Security Alerts"
                                  className="input-dark mt-1"
                                />
                              </div>
                            </div>
                            <p className="text-xs text-slate-500">
                              For Gmail: Use smtp.gmail.com, port 587, and an App Password (not your regular password).
                            </p>
                          </div>
                        )}

                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={handleSaveEmailConfig}
                            disabled={savingEmailConfig}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            {savingEmailConfig ? (
                              <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Save className="w-4 h-4 mr-1" />
                            )}
                            Save Configuration
                          </Button>
                          {emailAlertsConfig?.config_source === "database" && (
                            <Button
                              onClick={handleClearEmailConfig}
                              disabled={savingEmailConfig}
                              variant="outline"
                              size="sm"
                              className="text-red-400 border-red-400 hover:bg-red-900/20"
                            >
                              Clear Config
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Alert Recipients with Per-Recipient Preferences */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm uppercase tracking-wider">
                      Alert Recipients ({alertEmails.length})
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                      Each recipient can have individual notification preferences.
                    </p>
                  </CardHeader>
                  <CardContent className="py-2 space-y-3">
                    {/* Add Email Form */}
                    <div className="flex gap-2">
                      <Input
                        value={newAlertEmail}
                        onChange={(e) => setNewAlertEmail(e.target.value)}
                        placeholder="email@example.com"
                        className="input-dark flex-1"
                        onKeyDown={(e) => e.key === "Enter" && handleAddAlertEmail()}
                      />
                      <Button onClick={handleAddAlertEmail} size="sm" className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>

                    {/* Email List with Preferences */}
                    {alertEmails.length === 0 ? (
                      <p className="text-slate-500 text-sm text-center py-4">No alert recipients configured</p>
                    ) : (
                      <div className="space-y-2">
                        {alertEmails.map((recipient, idx) => {
                          const isExpanded = expandedRecipients[recipient.email];
                          const enabledCount = Object.values(recipient.preferences || {}).filter(Boolean).length;
                          const totalCount = Object.keys(notificationTypes).length;

                          return (
                            <div key={idx} className="p-3 bg-slate-800 rounded">
                              <div className="flex items-center justify-between">
                                <div
                                  className="flex items-center gap-2 flex-1 cursor-pointer"
                                  onClick={() => setExpandedRecipients({
                                    ...expandedRecipients,
                                    [recipient.email]: !isExpanded
                                  })}
                                >
                                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-0" : "-rotate-90"}`} />
                                  <Mail className="w-4 h-4 text-slate-400" />
                                  <span className="text-sm font-medium">{recipient.email}</span>
                                  <Badge variant="outline" className="text-xs ml-2">
                                    {enabledCount}/{totalCount} alerts
                                  </Badge>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveAlertEmail(recipient.email)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-7 w-7 p-0"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                              {/* Per-recipient notification preferences - collapsed by default */}
                              {isExpanded && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 mt-3 pt-2 border-t border-slate-700">
                                  {Object.entries(notificationTypes).map(([key, info]) => (
                                    <div
                                      key={key}
                                      className="flex items-center gap-1 text-xs cursor-pointer hover:bg-slate-700/50 p-1 rounded"
                                      onClick={() => handleToggleRecipientPref(recipient.email, key)}
                                    >
                                      <Checkbox
                                        checked={recipient.preferences?.[key] || false}
                                        onCheckedChange={() => handleToggleRecipientPref(recipient.email, key)}
                                        className="h-3 w-3"
                                      />
                                      <span className={recipient.preferences?.[key] ? "text-slate-200" : "text-slate-500"}>
                                        {info.label}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Test Email */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm uppercase tracking-wider">
                      Send Test Email
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-2">
                    <div className="flex gap-2">
                      <Input
                        value={testEmailAddress}
                        onChange={(e) => setTestEmailAddress(e.target.value)}
                        placeholder={user?.email || "email@example.com"}
                        className="input-dark flex-1"
                      />
                      <Button
                        onClick={handleSendTestEmail}
                        size="sm"
                        disabled={!emailAlertsConfig?.email_configured || sendingTestEmail}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {sendingTestEmail ? (
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-1" />
                        )}
                        Send Test
                      </Button>
                    </div>
                    {!emailAlertsConfig?.email_configured && (
                      <p className="text-amber-400 text-xs mt-2">Configure email (Resend or SMTP) to send test emails</p>
                    )}
                  </CardContent>
                </Card>

                {/* WhatsApp Notifications */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-green-400" />
                        WhatsApp Notifications
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="whatsapp_enabled"
                          checked={whatsappEnabled}
                          onCheckedChange={(checked) => setWhatsappEnabled(checked)}
                        />
                        <Label htmlFor="whatsapp_enabled" className="text-xs cursor-pointer">
                          {whatsappEnabled ? "Enabled" : "Disabled"}
                        </Label>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="py-2 space-y-3">
                    {/* Twilio status */}
                    {whatsappConfig?.twilio_configured ? (
                      <div className="flex items-center gap-2 text-green-400 text-xs">
                        <Check className="w-3.5 h-3.5" /> Twilio Credentials Found
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-amber-400 text-xs">
                        <AlertCircle className="w-3.5 h-3.5" /> Set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_WHATSAPP_FROM env vars
                      </div>
                    )}

                    {/* From Number */}
                    <div>
                      <Label className="text-xs text-slate-400">Sender Number</Label>
                      <Input
                        value={whatsappFromNumber}
                        onChange={(e) => setWhatsappFromNumber(e.target.value)}
                        placeholder="whatsapp:+14155238886"
                        className="input-dark"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">Twilio Sandbox or approved WhatsApp sender number</p>
                    </div>

                    <Button onClick={handleSaveWhatsappConfig} size="sm" disabled={savingWhatsappConfig}>
                      {savingWhatsappConfig ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                      Save Config
                    </Button>

                    {/* Registered WhatsApp Numbers */}
                    <div className="border-t border-slate-800 pt-3">
                      <p className="text-xs text-slate-400 uppercase mb-1">Registered Numbers</p>
                      <p className="text-[10px] text-slate-600 mb-2">Enabled users also receive bot alerts as individual messages</p>
                      {(() => {
                        const waUsers = users.filter(u => u.whatsapp_number);
                        if (waUsers.length === 0) return <p className="text-[11px] text-slate-500 italic">No users have a WhatsApp number configured</p>;
                        const toggleWaUser = async (u) => {
                          try {
                            const res = await axios.put(`${API}/users/${u.user_id}`, { whatsapp_enabled: !u.whatsapp_enabled }, { withCredentials: true });
                            setUsers(users.map(x => x.user_id === u.user_id ? res.data : x));
                          } catch { toast.error("Failed to update"); }
                        };
                        const sendTestToUser = async (number, name) => {
                          try {
                            await axios.post(`${API}/audit/whatsapp/test`, { number }, { withCredentials: true });
                            toast.success(`Test sent to ${name || number}`);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to send test");
                          }
                        };
                        return (
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {waUsers.map(u => (
                              <div key={u.user_id} className="flex items-center justify-between text-xs bg-slate-800/50 rounded px-2 py-1.5">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <button
                                    onClick={() => toggleWaUser(u)}
                                    className={`w-8 h-4 rounded-full relative transition-colors flex-shrink-0 ${u.whatsapp_enabled ? 'bg-green-600' : 'bg-slate-600'}`}
                                  >
                                    <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${u.whatsapp_enabled ? 'left-4' : 'left-0.5'}`} />
                                  </button>
                                  <span className="text-slate-300 truncate">{u.name || u.email}</span>
                                  {u.whatsapp_8513_only && <span className="text-[9px] bg-red-500/20 text-red-400 px-1 rounded flex-shrink-0">85/13</span>}
                                </div>
                                <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                  <span className="text-slate-400 font-mono text-[11px]">{u.whatsapp_number}</span>
                                  <button
                                    onClick={() => sendTestToUser(u.whatsapp_number, u.name)}
                                    className="text-[9px] px-1.5 py-0.5 rounded bg-blue-600/20 text-blue-400 hover:bg-blue-600/40 transition-colors"
                                  >
                                    Test
                                  </button>
                                </div>
                              </div>
                            ))}
                            <p className="text-[10px] text-slate-500 mt-1">{waUsers.filter(u => u.whatsapp_enabled).length} enabled / {waUsers.length} registered</p>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Test */}
                    <div className="border-t border-slate-800 pt-3">
                      <p className="text-xs text-slate-400 uppercase mb-2">Send Test Message</p>
                      <div className="flex gap-2">
                        <Input
                          value={testWhatsappNumber}
                          onChange={(e) => setTestWhatsappNumber(e.target.value)}
                          placeholder="+1234567890"
                          className="input-dark flex-1"
                          onKeyDown={(e) => e.key === "Enter" && handleSendTestWhatsapp()}
                        />
                        <Button
                          onClick={handleSendTestWhatsapp}
                          size="sm"
                          disabled={sendingTestWhatsapp || !testWhatsappNumber.trim()}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {sendingTestWhatsapp ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* V2 Template Setup */}
                    <div className="border-t border-slate-800 pt-3">
                      <p className="text-xs text-slate-400 uppercase mb-2">WhatsApp Template (V2 - Multi-line)</p>
                      <p className="text-[10px] text-slate-500 mb-2">Creates a new template with proper line breaks instead of | separators. Must be approved by WhatsApp before it takes effect.</p>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          onClick={() => handleCreateV2Template()}
                          size="sm"
                          disabled={creatingV2Template}
                          className="bg-blue-600 hover:bg-blue-700 text-xs"
                        >
                          {creatingV2Template ? (
                            <><RefreshCw className="w-3 h-3 animate-spin mr-1" /> Creating...</>
                          ) : (
                            "Create Both"
                          )}
                        </Button>
                        <Button
                          onClick={() => handleCreateV2Template("alert")}
                          size="sm"
                          disabled={creatingV2Template}
                          className="bg-amber-600 hover:bg-amber-700 text-xs"
                        >
                          Recreate Alert
                        </Button>
                        <Button
                          onClick={() => handleCreateV2Template("perp")}
                          size="sm"
                          disabled={creatingV2Template}
                          className="bg-purple-600 hover:bg-purple-700 text-xs"
                        >
                          Recreate Perp
                        </Button>
                        <Button
                          onClick={() => handleCreateV2Template("priority")}
                          size="sm"
                          disabled={creatingV2Template}
                          className="bg-red-600 hover:bg-red-700 text-xs"
                        >
                          Recreate Priority
                        </Button>
                        <Button
                          onClick={() => handleCreateV2Template("ten85")}
                          size="sm"
                          disabled={creatingV2Template}
                          className="bg-orange-600 hover:bg-orange-700 text-xs"
                        >
                          Recreate 10-85
                        </Button>
                        <Button
                          onClick={() => handleCreateV2Template("ten13")}
                          size="sm"
                          disabled={creatingV2Template}
                          className="bg-orange-600 hover:bg-orange-700 text-xs"
                        >
                          Recreate 10-13
                        </Button>
                        <Button
                          onClick={handleCheckTemplateStatus}
                          size="sm"
                          variant="outline"
                          className="border-slate-700 text-xs"
                        >
                          Check Status
                        </Button>
                      </div>
                      <div className="flex gap-2 items-center mt-2">
                        <select
                          value={setSidType}
                          onChange={(e) => setSetSidType(e.target.value)}
                          className="bg-slate-800 border border-slate-700 text-xs text-slate-300 rounded px-2 py-1.5"
                        >
                          <option value="alert">Alert</option>
                          <option value="perp">Perp</option>
                          <option value="priority">Priority</option>
                          <option value="ten85">10-85</option>
                          <option value="ten13">10-13</option>
                        </select>
                        <input
                          type="text"
                          value={setSidValue}
                          onChange={(e) => setSetSidValue(e.target.value)}
                          placeholder="HX..."
                          className="bg-slate-800 border border-slate-700 text-xs text-slate-300 rounded px-2 py-1.5 w-64"
                        />
                        <Button
                          onClick={handleSetTemplateSid}
                          size="sm"
                          disabled={!setSidValue}
                          className="bg-green-600 hover:bg-green-700 text-xs"
                        >
                          Set SID
                        </Button>
                      </div>
                      {v2TemplateStatus && (
                        <div className="mt-2 p-2 bg-slate-800/50 rounded text-[10px] text-slate-400 space-y-1">
                          {v2TemplateStatus.v1 && (
                            <p>V1 ({v2TemplateStatus.v1.friendly_name || "chsp_alert3"}): <span className={v2TemplateStatus.v1.status === "approved" ? "text-green-400" : v2TemplateStatus.v1.status === "rejected" ? "text-red-400" : "text-yellow-400"}>{v2TemplateStatus.v1.status}</span></p>
                          )}
                          {v2TemplateStatus.v2_alert && (
                            <p>V2 Alert: <span className={v2TemplateStatus.v2_alert.status === "approved" ? "text-green-400" : v2TemplateStatus.v2_alert.status === "rejected" ? "text-red-400" : v2TemplateStatus.v2_alert.status === "not_configured" ? "text-slate-500" : "text-yellow-400"}>{v2TemplateStatus.v2_alert.status}</span>{v2TemplateStatus.v2_alert.content_sid ? ` (${v2TemplateStatus.v2_alert.content_sid})` : ""}</p>
                          )}
                          {v2TemplateStatus.v2_perp && (
                            <p>V2 Perp: <span className={v2TemplateStatus.v2_perp.status === "approved" ? "text-green-400" : v2TemplateStatus.v2_perp.status === "rejected" ? "text-red-400" : v2TemplateStatus.v2_perp.status === "not_configured" ? "text-slate-500" : "text-yellow-400"}>{v2TemplateStatus.v2_perp.status}</span>{v2TemplateStatus.v2_perp.content_sid ? ` (${v2TemplateStatus.v2_perp.content_sid})` : ""}</p>
                          )}
                          {v2TemplateStatus.v2_priority && (
                            <p>Priority: <span className={v2TemplateStatus.v2_priority.status === "approved" ? "text-green-400" : v2TemplateStatus.v2_priority.status === "rejected" ? "text-red-400" : v2TemplateStatus.v2_priority.status === "not_configured" ? "text-slate-500" : "text-yellow-400"}>{v2TemplateStatus.v2_priority.status}</span>{v2TemplateStatus.v2_priority.content_sid ? ` (${v2TemplateStatus.v2_priority.content_sid})` : ""}</p>
                          )}
                          {v2TemplateStatus.v2_ten85 && (
                            <p>10-85: <span className={v2TemplateStatus.v2_ten85.status === "approved" ? "text-green-400" : v2TemplateStatus.v2_ten85.status === "rejected" ? "text-red-400" : v2TemplateStatus.v2_ten85.status === "not_configured" ? "text-slate-500" : "text-yellow-400"}>{v2TemplateStatus.v2_ten85.status}</span>{v2TemplateStatus.v2_ten85.content_sid ? ` (${v2TemplateStatus.v2_ten85.content_sid})` : ""}</p>
                          )}
                          {v2TemplateStatus.v2_ten13 && (
                            <p>10-13: <span className={v2TemplateStatus.v2_ten13.status === "approved" ? "text-green-400" : v2TemplateStatus.v2_ten13.status === "rejected" ? "text-red-400" : v2TemplateStatus.v2_ten13.status === "not_configured" ? "text-slate-500" : "text-yellow-400"}>{v2TemplateStatus.v2_ten13.status}</span>{v2TemplateStatus.v2_ten13.content_sid ? ` (${v2TemplateStatus.v2_ten13.content_sid})` : ""}</p>
                          )}
                          {v2TemplateStatus.v2_result && (
                            <p className="text-green-400">Templates submitted for approval!</p>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* WhatsApp Bot (Group Messaging) */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-green-400" />
                        WhatsApp Bot (Group Alerts)
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="bot_enabled"
                          checked={botEnabled}
                          onCheckedChange={(checked) => setBotEnabled(checked)}
                        />
                        <Label htmlFor="bot_enabled" className="text-xs cursor-pointer">
                          {botEnabled ? "Enabled" : "Disabled"}
                        </Label>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="py-2 space-y-3">
                    {/* Bot connection status */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full ${botStatus?.connected ? 'bg-green-400' : 'bg-red-400'}`} />
                        <span className={botStatus?.connected ? 'text-green-400' : 'text-red-400'}>
                          {botStatus?.connected ? 'Bot Connected' : 'Bot Disconnected'}
                        </span>
                      </div>
                      <Button onClick={fetchBotStatus} size="sm" variant="ghost" className="h-6 px-2">
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>

                    {/* Group selection */}
                    <div className="border-t border-slate-800 pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-400 uppercase">Alert Groups</p>
                        <Button onClick={fetchBotGroups} size="sm" variant="outline" className="h-6 px-2 text-xs border-slate-700" disabled={loadingBotGroups}>
                          {loadingBotGroups ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Load Groups"}
                        </Button>
                      </div>

                      {botGroups.length > 0 ? (
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {botGroups.map(g => (
                            <div key={g.id} className="flex items-center gap-2 text-xs bg-slate-800/50 rounded px-2 py-1.5">
                              <Checkbox
                                checked={botConfiguredGroups.includes(g.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) setBotConfiguredGroups([...botConfiguredGroups, g.id]);
                                  else setBotConfiguredGroups(botConfiguredGroups.filter(j => j !== g.id));
                                }}
                              />
                              <span className="text-slate-300 flex-1 truncate">{g.name}</span>
                              <span className="text-slate-500 text-[10px]">{g.participants} members</span>
                            </div>
                          ))}
                        </div>
                      ) : botConfiguredGroups.length > 0 ? (
                        <div className="space-y-1">
                          {botConfiguredGroups.map(jid => (
                            <div key={jid} className="flex items-center gap-2 text-xs bg-slate-800/50 rounded px-2 py-1.5">
                              <Check className="w-3 h-3 text-green-400" />
                              <span className="text-slate-400 font-mono text-[10px] truncate">{jid}</span>
                              <button onClick={() => setBotConfiguredGroups(botConfiguredGroups.filter(j => j !== jid))} className="ml-auto text-red-400 hover:text-red-300">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] text-slate-500 italic">Click "Load Groups" to see available WhatsApp groups</p>
                      )}
                    </div>

                    {/* Group Collections (groups of groups) */}
                    <div className="border-t border-slate-800 pt-3">
                      <p className="text-xs text-slate-400 uppercase mb-2">Group Collections</p>
                      <p className="text-[10px] text-slate-500 mb-2">Create named collections of WhatsApp groups for quick selection in Dispatch</p>

                      {botGroupCollections.map((col, idx) => (
                        <div key={idx} className="bg-slate-800/50 rounded px-2 py-2 mb-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-300 font-semibold">{col.name}</span>
                            <button onClick={() => setBotGroupCollections(botGroupCollections.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-300">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="space-y-0.5">
                            {(botGroups.length > 0 ? botGroups : botConfiguredGroups.filter(jid => jid.endsWith("@g.us")).map(jid => ({ id: jid, name: jid.split("@")[0] }))).map(g => {
                              const gId = g.id || g;
                              return (
                                <label key={gId} className="flex items-center gap-2 text-[11px] cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={(col.jids || []).includes(gId)}
                                    onChange={(e) => {
                                      const updated = [...botGroupCollections];
                                      const jids = updated[idx].jids || [];
                                      updated[idx] = { ...updated[idx], jids: e.target.checked ? [...jids, gId] : jids.filter(j => j !== gId) };
                                      setBotGroupCollections(updated);
                                    }}
                                    className="w-3 h-3 rounded border-slate-600 bg-slate-800"
                                  />
                                  <span className="text-slate-400 truncate">{g.name}</span>
                                </label>
                              );
                            })}
                          </div>
                          <p className="text-[10px] text-slate-500 mt-1">{(col.jids || []).length} group(s) selected</p>
                        </div>
                      ))}

                      <div className="flex gap-2 mt-2">
                        <Input
                          value={newCollectionName}
                          onChange={(e) => setNewCollectionName(e.target.value)}
                          placeholder="Collection name (e.g. Dispatch)"
                          className="input-dark text-xs h-7"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-xs border-slate-700 whitespace-nowrap"
                          disabled={!newCollectionName.trim()}
                          onClick={() => {
                            setBotGroupCollections([...botGroupCollections, { name: newCollectionName.trim(), jids: [] }]);
                            setNewCollectionName("");
                          }}
                        >
                          + Add
                        </Button>
                      </div>
                    </div>

                    {/* Individual recipients info */}
                    {(() => {
                      const waEnabled = users.filter(u => u.whatsapp_number && u.whatsapp_enabled);
                      if (waEnabled.length > 0) return (
                        <div className="border-t border-slate-800 pt-3">
                          <p className="text-xs text-slate-400 uppercase mb-1">Individual Recipients</p>
                          <p className="text-[11px] text-slate-500">
                            {waEnabled.length} user{waEnabled.length !== 1 ? 's' : ''} with WhatsApp enabled will also receive bot alerts individually ({waEnabled.map(u => u.name || u.whatsapp_number).join(', ')})
                          </p>
                          <p className="text-[10px] text-slate-600 mt-1">Manage in the WhatsApp Notifications section above</p>
                        </div>
                      );
                      return null;
                    })()}

                    {/* Save & Test */}
                    <div className="flex gap-2">
                      <Button onClick={handleSaveBotConfig} size="sm" disabled={savingBotConfig}>
                        {savingBotConfig ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                        Save
                      </Button>
                      <Button onClick={handleBotTest} size="sm" className="bg-green-600 hover:bg-green-700" disabled={sendingBotTest || (botConfiguredGroups.length === 0 && users.filter(u => u.whatsapp_number && u.whatsapp_enabled).length === 0)}>
                        {sendingBotTest ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                        Send Test
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Alert Line — dedicated Twilio number → WhatsApp group */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                        <PhoneIncoming className="w-4 h-4 text-cyan-400" />
                        Alert Line → WhatsApp
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="alert_line_enabled"
                          checked={alertLineEnabled}
                          onCheckedChange={(checked) => setAlertLineEnabled(!!checked)}
                        />
                        <Label htmlFor="alert_line_enabled" className="text-xs cursor-pointer">
                          {alertLineEnabled ? "Enabled" : "Disabled"}
                        </Label>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Every call to the configured Twilio number posts a WhatsApp message to the selected group (caller name, number, timestamp). No hotline popup or DB row.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs">Twilio Number (for reference only — set the webhook URL in Twilio)</Label>
                      <Input
                        value={alertLineTwilioNumber}
                        onChange={(e) => setAlertLineTwilioNumber(e.target.value)}
                        placeholder="+19298286444"
                        className="input-dark h-8"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">
                        Point this number's Voice webhook in Twilio to <code className="text-cyan-400">/api/incoming-calls/twilio-alert</code>.
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs">WhatsApp Group</Label>
                      <Select value={alertLineGroupJid} onValueChange={(jid) => {
                        setAlertLineGroupJid(jid);
                        const g = botGroups.find(x => (x.id || x.jid) === jid);
                        setAlertLineGroupName(g ? (g.subject || g.name || "") : "");
                      }}>
                        <SelectTrigger className="input-dark h-8 text-sm">
                          <SelectValue placeholder="Select a group..." />
                        </SelectTrigger>
                        <SelectContent>
                          {botGroups
                            .filter(g => (g.id || g.jid || "").endsWith("@g.us"))
                            .sort((a, b) => (a.subject || a.name || "").localeCompare(b.subject || b.name || ""))
                            .map(g => {
                              const jid = g.id || g.jid;
                              const name = g.subject || g.name || jid;
                              return <SelectItem key={jid} value={jid}>{name}</SelectItem>;
                            })}
                        </SelectContent>
                      </Select>
                      {alertLineGroupJid && !botGroups.some(g => (g.id || g.jid) === alertLineGroupJid) && (
                        <p className="text-[10px] text-amber-400 mt-1">
                          Saved group is not in the current bot group list. Selected JID: {alertLineGroupJid}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-end">
                      <Button onClick={handleSaveAlertLine} size="sm" disabled={savingAlertLine}>
                        {savingAlertLine ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                        Save
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Hotline → WhatsApp (existing hotline number, adds a group blast on every call) */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                        <PhoneIncoming className="w-4 h-4 text-amber-400" />
                        Hotline → WhatsApp
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="hotline_wa_enabled"
                          checked={hotlineWaEnabled}
                          onCheckedChange={(checked) => setHotlineWaEnabled(!!checked)}
                        />
                        <Label htmlFor="hotline_wa_enabled" className="text-xs cursor-pointer">
                          {hotlineWaEnabled ? "Enabled" : "Disabled"}
                        </Label>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      In addition to the existing dispatcher popup, every call to the hotline also posts a WhatsApp message to the selected group. Does not replace the hotline popup flow.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs">Hotline Twilio Number (reference only — webhook URL is unchanged)</Label>
                      <Input
                        value={hotlineWaNumber}
                        onChange={(e) => setHotlineWaNumber(e.target.value)}
                        placeholder="+19298288822"
                        className="input-dark h-8"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">
                        Uses the existing <code className="text-amber-400">/api/incoming-calls/twilio</code> webhook.
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs">WhatsApp Group</Label>
                      <Select value={hotlineWaGroupJid} onValueChange={(jid) => {
                        setHotlineWaGroupJid(jid);
                        const g = botGroups.find(x => (x.id || x.jid) === jid);
                        setHotlineWaGroupName(g ? (g.subject || g.name || "") : "");
                      }}>
                        <SelectTrigger className="input-dark h-8 text-sm">
                          <SelectValue placeholder="Select a group..." />
                        </SelectTrigger>
                        <SelectContent>
                          {botGroups
                            .filter(g => (g.id || g.jid || "").endsWith("@g.us"))
                            .sort((a, b) => (a.subject || a.name || "").localeCompare(b.subject || b.name || ""))
                            .map(g => {
                              const jid = g.id || g.jid;
                              const name = g.subject || g.name || jid;
                              return <SelectItem key={jid} value={jid}>{name}</SelectItem>;
                            })}
                        </SelectContent>
                      </Select>
                      {hotlineWaGroupJid && !botGroups.some(g => (g.id || g.jid) === hotlineWaGroupJid) && (
                        <p className="text-[10px] text-amber-400 mt-1">
                          Saved group is not in the current bot group list. Selected JID: {hotlineWaGroupJid}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-end">
                      <Button onClick={handleSaveHotlineWa} size="sm" disabled={savingHotlineWa}>
                        {savingHotlineWa ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                        Save
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Deepgram auto-transcription toggle — turn off when out of credit */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                        <Mic className="w-4 h-4 text-emerald-400" />
                        Radio Auto-Transcription (Deepgram)
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="deepgram_enabled"
                          checked={deepgramEnabled}
                          disabled={savingDeepgram}
                          onCheckedChange={(checked) => handleToggleDeepgram(!!checked)}
                        />
                        <Label htmlFor="deepgram_enabled" className="text-xs cursor-pointer">
                          {savingDeepgram ? "Saving…" : deepgramEnabled ? "Enabled" : "Disabled"}
                        </Label>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      When off, radio recordings and 71-tab audio still come in normally — the app just stops sending clips to Deepgram for text transcription. Turn this off if the Deepgram account is out of credit so it stops retrying and failing.
                    </p>
                  </CardHeader>
                </Card>

              </div>
            )}

            {/* Activity Logs Sub-tab (original content) */}
            {auditSubTab === "logs" && (
              <>
            {/* Stats Overview */}
            {auditStats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-slate-300">{auditStats.landing_page_visits || 0}</p>
                    <p className="text-xs text-slate-500 uppercase">Page Visits</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-green-400">{auditStats.login_stats?.successful || 0}</p>
                    <p className="text-xs text-slate-500 uppercase">Successful Logins</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-red-400">{auditStats.login_stats?.failed || 0}</p>
                    <p className="text-xs text-slate-500 uppercase">Failed Logins</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-blue-400">{auditStats.unique_users || 0}</p>
                    <p className="text-xs text-slate-500 uppercase">Unique Users</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-purple-400">{auditStats.unique_ips || 0}</p>
                    <p className="text-xs text-slate-500 uppercase">Unique IPs</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-amber-400">{auditStats.login_stats?.success_rate || 0}%</p>
                    <p className="text-xs text-slate-500 uppercase">Success Rate</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-cyan-400">{auditStats.suspicious_ips?.length || 0}</p>
                    <p className="text-xs text-slate-500 uppercase">Suspicious IPs</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Suspicious IPs Warning */}
            {auditStats?.suspicious_ips?.length > 0 && (
              <Card className="bg-red-900/30 border-red-700">
                <CardHeader className="py-2">
                  <CardTitle className="text-sm text-red-400 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Suspicious Activity Detected
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="space-y-2">
                    {auditStats.suspicious_ips.map((ip, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-slate-900/50 rounded">
                        <div>
                          <span className="font-mono text-red-300">{ip._id}</span>
                          <span className="text-slate-400 ml-2">({ip.count} failed attempts)</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          Tried: {ip.emails?.slice(0, 3).join(", ")}{ip.emails?.length > 3 ? "..." : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Device Breakdown */}
            {auditStats?.devices?.length > 0 && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="py-2">
                  <CardTitle className="text-sm uppercase tracking-wider">Device Types (Last 30 Days)</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="flex gap-4 flex-wrap">
                    {auditStats.devices.map((d, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Badge className={d.type === "mobile" ? "bg-blue-600" : d.type === "tablet" ? "bg-purple-600" : "bg-slate-600"}>
                          {d.type}
                        </Badge>
                        <span className="text-sm">{d.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Filters */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-3">
                <div className="flex flex-wrap gap-2 items-center">
                  <Input
                    placeholder="Search email, name, IP..."
                    value={auditFilter.search}
                    onChange={(e) => setAuditFilter({...auditFilter, search: e.target.value})}
                    className="input-dark h-8 w-48"
                  />
                  <Select value={auditFilter.user_id || "__all__"} onValueChange={(v) => setAuditFilter({...auditFilter, user_id: v === "__all__" ? "" : v})}>
                    <SelectTrigger className="input-dark h-8 w-40">
                      <SelectValue placeholder="User" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Users</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={auditFilter.event_type} onValueChange={(v) => setAuditFilter({...auditFilter, event_type: v})}>
                    <SelectTrigger className="input-dark h-8 w-40">
                      <SelectValue placeholder="Event Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Events</SelectItem>
                      <SelectItem value="landing_page_visit">Landing Page Visit</SelectItem>
                      <SelectItem value="login_success">Login Success</SelectItem>
                      <SelectItem value="login_failed">Login Failed</SelectItem>
                      <SelectItem value="login_denied">Login Denied</SelectItem>
                      <SelectItem value="logout">Logout</SelectItem>
                      <SelectItem value="page_visit">Page Visit</SelectItem>
                      <SelectItem value="action_respond">Respond</SelectItem>
                      <SelectItem value="action_unrespond">Unrespond</SelectItem>
                      <SelectItem value="action_upload_media">Upload Media</SelectItem>
                      <SelectItem value="action_create_call">Create Call</SelectItem>
                      <SelectItem value="action_update_call">Update Call</SelectItem>
                      <SelectItem value="action_log_incident">Log Incident</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={auditFilter.success} onValueChange={(v) => setAuditFilter({...auditFilter, success: v})}>
                    <SelectTrigger className="input-dark h-8 w-32">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All</SelectItem>
                      <SelectItem value="true">Success</SelectItem>
                      <SelectItem value="false">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    value={auditFilter.start_date}
                    onChange={(e) => setAuditFilter({...auditFilter, start_date: e.target.value})}
                    className="input-dark h-8 w-36"
                  />
                  <Input
                    type="date"
                    value={auditFilter.end_date}
                    onChange={(e) => setAuditFilter({...auditFilter, end_date: e.target.value})}
                    className="input-dark h-8 w-36"
                  />
                  <Button size="sm" onClick={() => fetchAuditLogs(1)} className="h-8">
                    <Search className="w-3 h-3 mr-1" /> Filter
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setAuditFilter({ event_type: "__all__", search: "", success: "__all__", start_date: "", end_date: "", user_id: "" }); fetchAuditLogs(1); }} className="h-8">
                    <RefreshCw className="w-3 h-3 mr-1" /> Reset
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Audit Logs Table */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="py-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm uppercase tracking-wider">
                  Audit Logs ({auditTotal} total)
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    disabled={auditPage <= 1} 
                    onClick={() => fetchAuditLogs(auditPage - 1)}
                    className="h-7"
                  >
                    ← Prev
                  </Button>
                  <span className="text-xs text-slate-500">Page {auditPage}</span>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    disabled={auditLogs.length < 50} 
                    onClick={() => fetchAuditLogs(auditPage + 1)}
                    className="h-7"
                  >
                    Next →
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {auditLoading ? (
                  <div className="p-8 text-center text-slate-500">Loading audit logs...</div>
                ) : auditLogs.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No audit logs found</div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="divide-y divide-slate-800">
                      {auditLogs.map((log, idx) => (
                        <div key={log.audit_id || idx} className="p-3 hover:bg-slate-800/50">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`text-xs ${getEventTypeColor(log.event_type)}`}>
                                {getEventTypeLabel(log.event_type)}
                              </Badge>
                              {log.success === false && (
                                <Badge variant="destructive" className="text-xs">FAILED</Badge>
                              )}
                              {log.user_email ? (
                                <span className="text-sm font-medium text-blue-400">{log.user_email}</span>
                              ) : (
                                <span className="text-sm text-slate-500 italic">Unknown user</span>
                              )}
                              {log.unit_name && (
                                <Badge variant="outline" className="text-xs">{log.unit_name}</Badge>
                              )}
                            </div>
                            <span className="text-xs text-slate-500">{formatAuditDate(log.timestamp)}</span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                            <span className="font-mono">IP: {log.ip_address || "N/A"}</span>
                            {log.device_info && (
                              <>
                                <span>{log.device_info.device_type}</span>
                                <span>{log.device_info.os}</span>
                                <span>{log.device_info.browser}</span>
                              </>
                            )}
                            {/* Which phone: model, app version, and its own device ID
                                (same ID = same phone, across logins). */}
                            {log.device_model && <span>{log.device_model}</span>}
                            {log.app_version && <span>App v{log.app_version}</span>}
                            {log.device_id && (
                              <button type="button" className="font-mono hover:text-slate-200" title="This phone's ID — tap to search for it"
                                onClick={() => setAuditFilter((f) => ({ ...f, search: log.device_id }))}>
                                Device {log.device_id.replace(/^dev_/, "").slice(0, 8)}
                              </button>
                            )}
                          </div>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <div className="mt-1 text-xs text-slate-500 space-x-2">
                              {log.details.page && <span>Page: {log.details.page}</span>}
                              {log.details.reason && <span className="text-red-400">Reason: {log.details.reason}</span>}
                              {log.details.action && <span>Action: {log.details.action}</span>}
                              {log.details.call_id && <span>Call: {log.details.call_id}</span>}
                              {log.details.incident_id && <span>Incident: {log.details.incident_id}</span>}
                              {log.details.filename && <span>File: {log.details.filename}</span>}
                              {log.details.profile_id && <span>Profile: {log.details.profile_id}</span>}
                              {log.details.call_type && <span>Type: {log.details.call_type}</span>}
                              {log.details.location && <span>Location: {log.details.location}</span>}
                              {Array.isArray(log.details.fields) && log.details.fields.length > 0 && (
                                <span>Changed: {log.details.fields.map((f) => f.replace(/_/g, " ")).join(", ")}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Recent Failed Logins */}
            {auditStats?.recent_failed_logins?.length > 0 && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="py-2">
                  <CardTitle className="text-sm uppercase tracking-wider text-red-400">
                    Recent Failed Login Attempts
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-48">
                    <div className="divide-y divide-slate-800">
                      {auditStats.recent_failed_logins.map((log, idx) => (
                        <div key={idx} className="p-2 text-sm flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-red-400">{log.user_email || "Unknown"}</span>
                            <span className="font-mono text-xs text-slate-500">{log.ip_address}</span>
                          </div>
                          <div className="text-xs text-slate-500">
                            {log.details?.reason && <span className="mr-2">({log.details.reason})</span>}
                            {formatAuditDate(log.timestamp)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {/* Top Pages Visited */}
            {auditStats?.page_visits?.length > 0 && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="py-2">
                  <CardTitle className="text-sm uppercase tracking-wider">Top Pages Visited (Last 30 Days)</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {auditStats.page_visits.slice(0, 10).map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-800 rounded">
                        <span className="text-sm truncate">{p.page}</span>
                        <Badge variant="secondary">{p.count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
              </>
            )}

            {/* Media Downloads Sub-tab */}
            {auditSubTab === "downloads" && (
              <>
                {/* Downloads Stats */}
                {mediaDownloadsStats && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Card className="bg-slate-900 border-slate-800">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-blue-400">{mediaDownloadsStats.total_downloads || 0}</p>
                        <p className="text-xs text-slate-500 uppercase">Total Downloads</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-900 border-slate-800">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-green-400">{mediaDownloadsStats.unique_users || 0}</p>
                        <p className="text-xs text-slate-500 uppercase">Unique Users</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-900 border-slate-800">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-purple-400">{mediaDownloadsStats.unique_files || 0}</p>
                        <p className="text-xs text-slate-500 uppercase">Unique Files</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-900 border-slate-800">
                      <CardContent className="p-3 text-center">
                        <p className="text-2xl font-bold text-amber-400">{mediaDownloadsStats.downloads_today || 0}</p>
                        <p className="text-xs text-slate-500 uppercase">Downloads Today</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Top Downloaders */}
                {mediaDownloadsStats?.top_users?.length > 0 && (
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader className="py-2">
                      <CardTitle className="text-sm uppercase tracking-wider">Top Downloaders</CardTitle>
                    </CardHeader>
                    <CardContent className="py-2">
                      <div className="flex flex-wrap gap-2">
                        {mediaDownloadsStats.top_users.slice(0, 5).map((u, idx) => (
                          <Badge key={idx} className="bg-blue-600">
                            {u.user_name || u.user_id}: {u.count}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Filters */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-3">
                    <div className="flex flex-wrap gap-2 items-center">
                      <Select value={downloadFilterUser || "__all__"} onValueChange={(v) => setDownloadFilterUser(v === "__all__" ? "" : v)}>
                        <SelectTrigger className="input-dark h-8 w-40">
                          <SelectValue placeholder="User" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Users</SelectItem>
                          {users.map(u => (
                            <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Search filename..."
                        value={downloadFilterFilename}
                        onChange={(e) => setDownloadFilterFilename(e.target.value)}
                        className="input-dark h-8 w-48"
                      />
                      <Input
                        type="date"
                        value={downloadFilterStartDate}
                        onChange={(e) => setDownloadFilterStartDate(e.target.value)}
                        className="input-dark h-8 w-36"
                        title="Start Date"
                      />
                      <Input
                        type="date"
                        value={downloadFilterEndDate}
                        onChange={(e) => setDownloadFilterEndDate(e.target.value)}
                        className="input-dark h-8 w-36"
                        title="End Date"
                      />
                      <Button
                        size="sm"
                        onClick={() => fetchMediaDownloads(1)}
                        className="bg-blue-600 hover:bg-blue-700 h-8"
                      >
                        <Search className="w-3 h-3 mr-1" />
                        Search
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setDownloadFilterUser("");
                          setDownloadFilterFilename("");
                          setDownloadFilterStartDate("");
                          setDownloadFilterEndDate("");
                          fetchMediaDownloads(1);
                        }}
                        className="h-8"
                      >
                        Clear
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Downloads Table */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      Media Download History
                      <Badge variant="secondary" className="ml-2">{mediaDownloadsTotal}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {mediaDownloadsLoading ? (
                      <div className="p-4 text-center text-slate-400">Loading...</div>
                    ) : mediaDownloads.length === 0 ? (
                      <div className="p-4 text-center text-slate-500">No download records found</div>
                    ) : (
                      <ScrollArea className="h-[400px]">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-800 sticky top-0">
                            <tr>
                              <th className="text-left p-2 text-slate-400">User</th>
                              <th className="text-left p-2 text-slate-400">File</th>
                              <th className="text-left p-2 text-slate-400">IP</th>
                              <th className="text-left p-2 text-slate-400">Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mediaDownloads.map((dl, idx) => (
                              <tr key={dl.download_id || idx} className="border-t border-slate-800 hover:bg-slate-800/50">
                                <td className="p-2">
                                  <div>
                                    <span className="text-slate-200">{dl.user_name || "Unknown"}</span>
                                    <div className="text-xs text-slate-500">{dl.user_email}</div>
                                  </div>
                                </td>
                                <td className="p-2 max-w-[200px]">
                                  <span className="text-slate-300 truncate block" title={dl.filename}>
                                    {dl.filename?.length > 30 ? `${dl.filename.substring(0, 30)}...` : dl.filename}
                                  </span>
                                </td>
                                <td className="p-2">
                                  <span className="font-mono text-xs text-slate-400">{dl.ip_address}</span>
                                </td>
                                <td className="p-2">
                                  <span className="text-xs text-slate-400">
                                    {dl.timestamp ? new Date(dl.timestamp).toLocaleString('en-US', { timeZone: 'America/New_York' }) : "N/A"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>

                {/* Pagination */}
                {mediaDownloadsPages > 1 && (
                  <div className="flex justify-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={mediaDownloadsPage <= 1}
                      onClick={() => fetchMediaDownloads(mediaDownloadsPage - 1)}
                    >
                      Previous
                    </Button>
                    <span className="py-1 px-3 text-sm text-slate-400">
                      Page {mediaDownloadsPage} of {mediaDownloadsPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={mediaDownloadsPage >= mediaDownloadsPages}
                      onClick={() => fetchMediaDownloads(mediaDownloadsPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Admin Activity Sub-tab */}
            {auditSubTab === "admin-activity" && (
              <>
                {/* Filters */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-3">
                    <div className="flex flex-wrap gap-2 items-center">
                      <Input
                        placeholder="Search admin, user, email..."
                        value={adminActivitySearch}
                        onChange={(e) => setAdminActivitySearch(e.target.value)}
                        className="input-dark h-8 w-56"
                      />
                      <Input
                        type="date"
                        value={adminActivityStartDate}
                        onChange={(e) => setAdminActivityStartDate(e.target.value)}
                        className="input-dark h-8 w-36"
                      />
                      <Input
                        type="date"
                        value={adminActivityEndDate}
                        onChange={(e) => setAdminActivityEndDate(e.target.value)}
                        className="input-dark h-8 w-36"
                      />
                      <Button size="sm" onClick={() => fetchAdminActivity(1)} className="h-8">
                        <Search className="w-3 h-3 mr-1" /> Filter
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setAdminActivitySearch(""); setAdminActivityStartDate(""); setAdminActivityEndDate(""); fetchAdminActivity(1); }} className="h-8">
                        <RefreshCw className="w-3 h-3 mr-1" /> Reset
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Admin Activity Logs */}
                <Card className="bg-slate-900 border-slate-800 overflow-hidden">
                  <CardHeader className="py-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm uppercase tracking-wider">
                      Admin Activity ({adminActivityTotal} total)
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={adminActivityPage <= 1}
                        onClick={() => fetchAdminActivity(adminActivityPage - 1)}
                        className="h-7"
                      >
                        ← Prev
                      </Button>
                      <span className="text-xs text-slate-500">Page {adminActivityPage}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={adminActivityLogs.length < 50}
                        onClick={() => fetchAdminActivity(adminActivityPage + 1)}
                        className="h-7"
                      >
                        Next →
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {adminActivityLoading ? (
                      <div className="p-8 text-center text-slate-500">Loading admin activity...</div>
                    ) : adminActivityLogs.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">No admin activity found</div>
                    ) : (
                      <ScrollArea className="h-[600px]">
                        <div className="divide-y divide-slate-800">
                          {adminActivityLogs.map((log, idx) => (
                            <div key={log.audit_id || idx} className="p-3 hover:bg-slate-800/50 overflow-hidden">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge className={`text-xs shrink-0 ${getEventTypeColor(log.event_type)}`}>
                                    {getEventTypeLabel(log.event_type)}
                                  </Badge>
                                  <span className="text-sm font-medium text-orange-400 break-all">
                                    {log.details?.performed_by_name || log.user_name || "Unknown Admin"}
                                  </span>
                                  {log.details?.target_name && (
                                    <>
                                      <span className="text-xs text-slate-500">→</span>
                                      <span className="text-sm text-blue-400 break-all">{log.details.target_name}</span>
                                      {log.details.target_email && (
                                        <span className="text-xs text-slate-500 break-all">({log.details.target_email})</span>
                                      )}
                                    </>
                                  )}
                                </div>
                                <span className="text-xs text-slate-500">{formatAuditDate(log.timestamp)}</span>
                              </div>
                              {/* Show changes for user updates */}
                              {realChanges(log.details?.changes).length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {realChanges(log.details.changes).map(([field, change]) => (
                                    <div key={field} className="text-xs pl-2 border-l-2 border-slate-700 break-all">
                                      <span className="text-slate-400 font-medium">{field.replace(/_/g, ' ')}: </span>
                                      <span className="text-red-400 line-through">
                                        {typeof change.from === 'object' ? JSON.stringify(change.from) : String(change.from ?? '(empty)')}
                                      </span>
                                      <span className="text-slate-600"> → </span>
                                      <span className="text-green-400">
                                        {typeof change.to === 'object' ? JSON.stringify(change.to) : String(change.to ?? '(empty)')}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Show role for user creation */}
                              {log.event_type === "admin_create_user" && log.details?.role && (
                                <div className="mt-1 text-xs text-slate-500 pl-2 border-l-2 border-slate-700">
                                  <span className="text-slate-400">Role: </span>
                                  <span className="text-green-400">{log.details.role}</span>
                                </div>
                              )}
                              {/* Show extra details for non-user events */}
                              {log.details?.sessions_deleted !== undefined && (
                                <div className="mt-1 text-xs text-slate-500 pl-2 border-l-2 border-slate-700">
                                  Sessions terminated: {log.details.sessions_deleted}
                                </div>
                              )}
                              {log.details?.ip_address && (
                                <div className="mt-1 text-xs text-slate-500 pl-2 border-l-2 border-slate-700 break-all">
                                  IP: <span className="font-mono">{log.details.ip_address}</span>
                                  {log.details.reason && <span className="ml-2">Reason: {log.details.reason}</span>}
                                </div>
                              )}
                              {/* Show details for data operations */}
                              {(log.details?.headline || log.details?.profile_name || log.details?.call_type || log.details?.unit_name || log.details?.car_name || log.details?.flag_label || log.details?.code_label || log.details?.unblocked_email) && (
                                <div className="mt-1 text-xs text-slate-500 pl-2 border-l-2 border-slate-700 break-all">
                                  {log.details.headline && <span>{log.details.headline}</span>}
                                  {log.details.profile_name && <span>{log.details.profile_name}</span>}
                                  {log.details.call_type && !log.details.flag_label && <span>{log.details.call_id ? `${log.details.call_id} — ` : ""}{log.details.call_type}</span>}
                                  {log.details.car_name && <span>{log.details.car_name}</span>}
                                  {log.details.unit_name && !log.details.car_name && <span>{log.details.unit_name}</span>}
                                  {log.details.flag_label && <span>{log.details.flag_label}</span>}
                                  {log.details.code_label && <span>{log.details.code_label}</span>}
                                  {log.details.unblocked_email && <span>{log.details.unblocked_email}</span>}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>

        {/* Groups tab removed - now inside Users tab as sub-tab */}
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-h-[85vh] h-[85vh] w-[calc(100vw-2rem)] max-w-lg flex flex-col overflow-hidden" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="uppercase tracking-wider">Edit User</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 min-h-0 pr-4">
            <div className="space-y-4 py-2">
              {/* Email */}
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={editUser?.email || ""}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  className="input-dark h-9"
                  placeholder="user@example.com"
                />
              </div>
              
              {/* Name & Status Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={editUser?.name || ""}
                    onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                    className="input-dark h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select
                    value={editUser?.is_active !== false ? "active" : "inactive"}
                    onValueChange={(v) => setEditUser({ ...editUser, is_active: v === "active" })}
                    disabled={editUser?.email?.toLowerCase() === 'sruliyavich@gmail.com' && user?.email?.toLowerCase() !== 'sruliyavich@gmail.com'}
                  >
                    <SelectTrigger className="input-dark h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Admin & Patrol Admin Toggles */}
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2 sm:gap-4 p-2 bg-blue-900/20 rounded border border-blue-700/50">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is-admin"
                    checked={editUser?.role === 'admin'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditUser({ ...editUser, role: 'admin', page_access: [] });
                      } else {
                        setEditUser({ ...editUser, role: 'custom', page_access: editUser?.page_access || [] });
                      }
                    }}
                    disabled={editUser?.email?.toLowerCase() === 'sruliyavich@gmail.com' && user?.email?.toLowerCase() !== 'sruliyavich@gmail.com'}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="is-admin" className="text-xs sm:text-sm font-medium text-blue-300 cursor-pointer whitespace-nowrap">
                    Admin
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="patrol-admin"
                    checked={editUser?.patrol_admin_access || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const currentPages = editUser?.page_access || [];
                      // Auto-add patrol page access when enabling patrol admin
                      const newPages = checked && !currentPages.includes('patrol')
                        ? [...currentPages, 'patrol']
                        : currentPages;
                      setEditUser({
                        ...editUser,
                        patrol_admin_access: checked,
                        page_access: newPages,
                        role: newPages.length > 0 ? 'custom' : editUser?.role
                      });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="patrol-admin" className="text-xs sm:text-sm font-medium text-orange-300 cursor-pointer whitespace-nowrap">
                    Patrol Admin
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="session-admin"
                    checked={editUser?.session_admin_access || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const currentPages = editUser?.page_access || [];
                      // Auto-add patrol page access when enabling session admin
                      const newPages = checked && !currentPages.includes('patrol')
                        ? [...currentPages, 'patrol']
                        : currentPages;
                      setEditUser({
                        ...editUser,
                        session_admin_access: checked,
                        page_access: newPages,
                        role: newPages.length > 0 ? 'custom' : editUser?.role
                      });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="session-admin" className="text-xs sm:text-sm font-medium text-emerald-300 cursor-pointer whitespace-nowrap">
                    Session Admin
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="fleet-access"
                    checked={editUser?.fleet_access || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const currentPages = editUser?.page_access || [];
                      // Auto-add patrol page access when enabling fleet access
                      const newPages = checked && !currentPages.includes('patrol')
                        ? [...currentPages, 'patrol']
                        : currentPages;
                      setEditUser({
                        ...editUser,
                        fleet_access: checked,
                        page_access: newPages,
                        role: newPages.length > 0 ? 'custom' : editUser?.role
                      });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="fleet-access" className="text-xs sm:text-sm font-medium text-cyan-300 cursor-pointer whitespace-nowrap">
                    Fleet (Geotab)
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="tickets-access"
                    checked={editUser?.tickets_access || false}
                    onChange={(e) => setEditUser({ ...editUser, tickets_access: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="tickets-access" className="text-xs sm:text-sm font-medium text-rose-300 cursor-pointer whitespace-nowrap" title="Sees everyone's tickets (My Dashboard → Tickets), edits them and marks them reimbursed">
                    Tickets
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="perpetrator-admin"
                    checked={editUser?.perpetrator_admin_access || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const currentPages = editUser?.page_access || [];
                      // Auto-add perpetrators page access when enabling perpetrator admin
                      const newPages = checked && !currentPages.includes('perpetrators')
                        ? [...currentPages, 'perpetrators']
                        : currentPages;
                      setEditUser({
                        ...editUser,
                        perpetrator_admin_access: checked,
                        page_access: newPages,
                        role: newPages.length > 0 ? 'custom' : editUser?.role
                      });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="perpetrator-admin" className="text-xs sm:text-sm font-medium text-purple-300 cursor-pointer whitespace-nowrap">
                    Profiles Admin
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="rollcall-access"
                    checked={editUser?.rollcall_access || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const currentPages = editUser?.page_access || [];
                      // Auto-add rollcall page access when enabling rollcall access
                      const newPages = checked && !currentPages.includes('rollcall')
                        ? [...currentPages, 'rollcall']
                        : currentPages;
                      setEditUser({
                        ...editUser,
                        rollcall_access: checked,
                        page_access: newPages,
                        role: newPages.length > 0 ? 'custom' : editUser?.role
                      });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="rollcall-access" className="text-xs sm:text-sm font-medium text-teal-300 cursor-pointer whitespace-nowrap">
                    Roll Call
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="radio-71-access"
                    checked={editUser?.radio_71_access || false}
                    onChange={(e) => {
                      setEditUser({ ...editUser, radio_71_access: e.target.checked });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="radio-71-access" className="text-xs sm:text-sm font-medium text-green-300 cursor-pointer whitespace-nowrap">
                    Radio - 71
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="radio-recordings-access"
                    checked={editUser?.recording_admin_access || false}
                    onChange={(e) => {
                      setEditUser({ ...editUser, recording_admin_access: e.target.checked });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="radio-recordings-access" className="text-xs sm:text-sm font-medium text-green-300 cursor-pointer whitespace-nowrap">
                    Radio - Recordings
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="incidents-access"
                    checked={editUser?.incidents_access || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const currentPages = editUser?.page_access || [];
                      const newPages = checked && !currentPages.includes('incidents')
                        ? [...currentPages, 'incidents']
                        : currentPages;
                      setEditUser({
                        ...editUser,
                        incidents_access: checked,
                        page_access: newPages,
                        role: newPages.length > 0 ? 'custom' : editUser?.role
                      });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="incidents-access" className="text-xs sm:text-sm font-medium text-orange-300 cursor-pointer whitespace-nowrap">
                    Incidents
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="investigations-access"
                    checked={editUser?.investigations_access || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const currentPages = editUser?.page_access || [];
                      const newPages = checked && !currentPages.includes('investigations')
                        ? [...currentPages, 'investigations']
                        : currentPages;
                      setEditUser({
                        ...editUser,
                        investigations_access: checked,
                        page_access: newPages,
                        role: newPages.length > 0 ? 'custom' : editUser?.role
                      });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="investigations-access" className="text-xs sm:text-sm font-medium text-blue-300 cursor-pointer whitespace-nowrap">
                    Investigations
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="hotline-access"
                    checked={editUser?.hotline_access || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const currentPages = editUser?.page_access || [];
                      const newPages = checked && !currentPages.includes('hotline')
                        ? [...currentPages, 'hotline']
                        : currentPages;
                      setEditUser({
                        ...editUser,
                        hotline_access: checked,
                        page_access: newPages,
                        role: newPages.length > 0 ? 'custom' : editUser?.role
                      });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="hotline-access" className="text-xs sm:text-sm font-medium text-red-300 cursor-pointer whitespace-nowrap">
                    Hotline
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="hotline-dispatch-access"
                    checked={editUser?.hotline_dispatch_access || false}
                    onChange={(e) => setEditUser({ ...editUser, hotline_dispatch_access: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <label htmlFor="hotline-dispatch-access" className="text-xs sm:text-sm font-medium text-red-300 cursor-pointer whitespace-nowrap">
                    Hotline Dispatch
                  </label>
                </div>
              </div>

              {/* DEFAULT HOMEPAGE SECTION */}
              <div className="p-2 bg-slate-800 rounded border border-slate-700">
                <Label className="text-xs font-semibold mb-2 block">Default Homepage</Label>
                <div className="flex items-center gap-3">
                  <select
                    value={editUser?.default_homepage || ""}
                    onChange={(e) => setEditUser({ ...editUser, default_homepage: e.target.value || null })}
                    className="bg-slate-900 border border-slate-600 rounded px-3 py-1 text-sm text-white"
                  >
                    <option value="">First accessible page</option>
                    <option value="dashboard">Dashboard</option>
                    <option value="live_calls">Dispatch</option>
                    <option value="incidents">Incidents</option>
                    <option value="response">Response</option>
                    <option value="call_history">Call History</option>
                    <option value="patrol">Patrol</option>
                    <option value="perpetrators">Profiles</option>
                    <option value="rollcall">Roll Call</option>
                    <option value="building_codes">Building Codes</option>
                    <option value="map">Map</option>
                    <option value="notification_history">Notifications</option>
                    <option value="media">Media</option>
                    <option value="member_dashboard">My Dashboard</option>
                    <option value="radio">Radio</option>
                    <option value="pr">PR</option>
                    <option value="units">Units</option>
                  </select>
                  <span className="text-xs text-slate-400">Home button destination</span>
                </div>
              </div>

              {/* MY DASHBOARD TABS SECTION */}
              <div className="p-2 bg-slate-800 rounded border border-slate-700">
                <Label className="text-xs font-semibold mb-2 block">My Dashboard Tabs</Label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editUser?.role === "admin" || editUser?.tickets_access || !editUser?.tickets_tab_hidden}
                    disabled={editUser?.role === "admin" || editUser?.tickets_access}
                    onChange={(e) => setEditUser({ ...editUser, tickets_tab_hidden: !e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                  />
                  <span className="text-slate-300">Tickets tab</span>
                </label>
                <p className="text-[10px] text-slate-500 mt-1">
                  {editUser?.role === "admin" || editUser?.tickets_access
                    ? "Always shown — this user is a Tickets editor"
                    : editUser?.tickets_tab_hidden
                      ? "Hidden — can't see or submit tickets"
                      : "Shown — can submit and see their own tickets"}
                </p>
              </div>

              {/* MAX DEVICES SECTION */}
              <div className="p-2 bg-slate-800 rounded border border-slate-700">
                <Label className="text-xs font-semibold mb-2 block">Device Login Limit</Label>
                <div className="flex items-center gap-3">
                  <select
                    value={editUser?.max_devices || 1}
                    onChange={(e) => setEditUser({ ...editUser, max_devices: parseInt(e.target.value) })}
                    className="bg-slate-900 border border-slate-600 rounded px-3 py-1 text-sm text-white"
                  >
                    <option value={1}>1 device</option>
                    <option value={2}>2 devices</option>
                    <option value={3}>3 devices</option>
                    <option value={5}>5 devices</option>
                    <option value={10}>10 devices</option>
                    <option value={99}>Unlimited</option>
                  </select>
                  <span className="text-xs text-slate-400">Max concurrent logins</span>
                </div>
              </div>

              {/* DEVICE ACCESS SECTION */}
              <div className="p-2 bg-slate-800 rounded border border-slate-700">
                <Label className="text-xs font-semibold mb-2 block">Device Access</Label>
                <div className="flex items-center gap-3">
                  <select
                    value={editUser?.device_access || "all"}
                    onChange={(e) => setEditUser({ ...editUser, device_access: e.target.value })}
                    className="bg-slate-900 border border-slate-600 rounded px-3 py-1 text-sm text-white"
                  >
                    <option value="all">All Devices</option>
                    <option value="mobile_only">Mobile Only</option>
                    <option value="desktop_only">Desktop Only</option>
                  </select>
                  <span className="text-xs text-slate-400">Restrict which devices can access the app</span>
                </div>
              </div>

              {/* PATROL CARS SECTION */}
              <div className="p-2 bg-slate-800 rounded border border-slate-700">
                <Label className="text-xs font-semibold mb-2 block">Patrol Tab Cars</Label>
                <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
                  {patrolCars.filter(car => car.is_active !== false).map(car => (
                    <label key={car.car_id} className="flex items-center gap-2 text-xs cursor-pointer p-1 hover:bg-slate-900 rounded">
                      <input
                        type="checkbox"
                        checked={(editUser?.patrol_visible_cars || []).includes(car.car_id)}
                        onChange={(e) => {
                          const current = editUser?.patrol_visible_cars || [];
                          if (e.target.checked) {
                            setEditUser({ ...editUser, patrol_visible_cars: [...current, car.car_id] });
                          } else {
                            setEditUser({ ...editUser, patrol_visible_cars: current.filter(id => id !== car.car_id) });
                          }
                        }}
                        className="w-3 h-3 rounded border-slate-600 bg-slate-800"
                      />
                      <span className="text-slate-300">{car.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  {(editUser?.patrol_visible_cars || []).length > 0
                    ? `Sees ${(editUser?.patrol_visible_cars || []).length} car(s) in the Patrol tab`
                    : "None selected = sees all cars"}
                </p>
              </div>

              {/* SESSION TIMEOUT SECTION */}
              <div className="p-2 bg-slate-800 rounded border border-slate-700">
                <Label className="text-xs font-semibold mb-2 block">Session Timeout</Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <select
                      value={editUser?.session_timeout_type || "activity"}
                      onChange={(e) => setEditUser({ ...editUser, session_timeout_type: e.target.value })}
                      className="bg-slate-900 border border-slate-600 rounded px-3 py-1 text-sm text-white"
                    >
                      <option value="activity">From Last Activity</option>
                      <option value="login">From Login</option>
                    </select>
                    <span className="text-xs text-slate-400">When to start counting</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={editUser?.session_timeout_days || 7}
                      onChange={(e) => setEditUser({ ...editUser, session_timeout_days: parseInt(e.target.value) })}
                      className="bg-slate-900 border border-slate-600 rounded px-3 py-1 text-sm text-white"
                    >
                      <option value={1}>1 day</option>
                      <option value={3}>3 days</option>
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                    </select>
                    <span className="text-xs text-slate-400">Session duration</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    {editUser?.session_timeout_type === "login"
                      ? `User will be logged out ${editUser?.session_timeout_days || 7} days after login, regardless of activity.`
                      : `Session extends on each activity. User logged out after ${editUser?.session_timeout_days || 7} days of inactivity.`}
                  </p>
                </div>
              </div>

              {/* NOTIFICATION STATUS SECTION */}
              <div className="p-2 bg-slate-800 rounded border border-slate-700">
                <Label className="text-xs font-semibold mb-1 block">Notifications Enabled</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Turning this off silences everything for this user — 10-85 and 10-13
                  included. Their push subscription stays connected, so switching it
                  back on takes effect without them reinstalling anything.
                </p>
                <div className="space-y-2">
                  {/* Status = will their phone get notifications: every link
                      (admin switch as staged here, their master switch, a live
                      device sign-up). The lines under it say which link. */}
                  {(() => {
                    const ps = pushStatus && typeof pushStatus === "object" ? pushStatus : null;
                    const adminOn = editUser?.notif_all_enabled !== false;   // staged, may be unsaved
                    const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";
                    let ok, text;
                    if (pushStatus === "loading") { ok = null; text = "Checking…"; }
                    else if (!ps) { ok = null; text = "Couldn't check"; }
                    else if (!adminOn) { ok = false; text = "Off — turned off here"; }
                    else { ok = ps.device_ok; text = ps.device_ok ? "Will receive" : ps.device_reason; }
                    const row = (label, value, tone = "text-slate-300") => (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-sm text-slate-400 shrink-0">{label}</span>
                        <span className={`text-sm font-medium text-right ${tone}`}>{value}</span>
                      </div>
                    );
                    return (
                      <>
                        {row("Status:", text, ok === true ? "text-green-400" : ok === false ? "text-red-400" : "text-slate-400")}
                        {row("Admin switch:", adminOn ? "On" : "Off", adminOn ? "text-slate-300" : "text-red-400")}
                        {ps && row("Master switch:", ps.master_on === false ? "Off (their own)" : ps.master_on === true ? "On" : "Never set",
                          ps.master_on === false ? "text-red-400" : "text-slate-300")}
                        {ps && row("Phone:", ps.devices > 0
                          ? `Connected${ps.devices > 1 ? ` (${ps.devices} devices)` : ""} since ${fmt(ps.connected_since)}`
                          : ps.expired > 0 ? "Sign-up expired" : "Not connected", ps.devices > 0 ? "text-slate-300" : "text-red-400")}
                        {ps && row("Last push delivered:", ps.last_delivered ? fmt(ps.last_delivered) : "Never")}
                      </>
                    );
                  })()}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Muted:</span>
                    <span className={`text-sm font-medium ${editUser?.notif_muted_until && new Date(editUser.notif_muted_until) > new Date() ? 'text-amber-400' : 'text-slate-400'}`}>
                      {editUser?.notif_muted_until && new Date(editUser.notif_muted_until) > new Date()
                        ? `Until ${new Date(editUser.notif_muted_until).toLocaleString('en-US', { timeZone: 'America/New_York' })}`
                        : 'Not muted'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
                    <Button
                      size="sm"
                      variant={editUser?.notif_all_enabled !== false ? "outline" : "default"}
                      className={`text-xs ${editUser?.notif_all_enabled !== false ? 'text-red-400 border-red-400/50 hover:bg-red-900/20' : 'bg-green-600 hover:bg-green-700'}`}
                      onClick={() => setEditUser({ ...editUser, notif_all_enabled: editUser?.notif_all_enabled === false ? true : false })}
                    >
                      {editUser?.notif_all_enabled !== false ? 'Disable' : 'Enable'}
                    </Button>
                    {editUser?.notif_muted_until && new Date(editUser.notif_muted_until) > new Date() ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs text-amber-400 border-amber-400/50 hover:bg-amber-900/20"
                        onClick={() => setEditUser({ ...editUser, notif_muted_until: null })}
                      >
                        Unmute
                      </Button>
                    ) : (
                      <select
                        className="text-xs bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-300"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            const minutes = parseInt(e.target.value);
                            const muteUntil = new Date(Date.now() + minutes * 60 * 1000);
                            setEditUser({ ...editUser, notif_muted_until: muteUntil.toISOString() });
                          }
                        }}
                      >
                        <option value="">Mute for...</option>
                        <option value="30">30 min</option>
                        <option value="60">1 hour</option>
                        <option value="120">2 hours</option>
                        <option value="480">8 hours</option>
                        <option value="1440">24 hours</option>
                      </select>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs text-blue-400 border-blue-400/50 hover:bg-blue-900/20 ml-auto"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/push/refresh/${editUser.user_id}`, {}, { withCredentials: true });
                          toast.success("Refresh sent to user");
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Failed to send refresh");
                        }
                      }}
                    >
                      Refresh
                    </Button>
                  </div>
                  {/* Refresh above fires immediately; Disable/Mute/Unmute only
                      stage until Save. Call that out rather than letting a click
                      look like it took effect. */}
                  {(() => {
                    const original = users.find(u => u.user_id === editUser?.user_id);
                    if (!original) return null;
                    const changed =
                      (original.notif_all_enabled !== false) !== (editUser?.notif_all_enabled !== false) ||
                      (original.notif_muted_until || null) !== (editUser?.notif_muted_until || null);
                    return changed ? (
                      <p className="text-xs text-amber-400 pt-1">
                        Unsaved — click Save to apply.
                      </p>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* PAGE ACCESS SECTION */}
              {editUser?.role !== 'admin' && (
                <div>
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                    <Label className="text-xs font-semibold">Page Access</Label>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[10px] border-green-600 text-green-400 hover:bg-green-950"
                        onClick={() => setEditUser({ ...editUser, page_access: availablePages.filter(p => p !== 'settings'), role: 'custom' })}
                      >
                        Select All
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[10px] border-red-600 text-red-400 hover:bg-red-950"
                        onClick={() => setEditUser({ ...editUser, page_access: [], role: 'custom' })}
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2 bg-slate-800 rounded max-h-48 overflow-y-auto">
                    {availablePages.map((page) => {
                      const isChecked = (editUser?.page_access || []).includes(page);
                      const isSettings = page === 'settings';

                      return (
                        <label
                          key={page}
                          className={`flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-slate-700 ${isSettings ? 'text-amber-400' : ''} ${isChecked ? 'bg-slate-700/50' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const currentPages = editUser?.page_access || [];
                              let newPages = e.target.checked
                                ? [...currentPages, page]
                                : currentPages.filter(p => p !== page);
                              setEditUser({ ...editUser, page_access: newPages, role: 'custom' });
                            }}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-800 flex-shrink-0"
                          />
                          <span className="text-xs truncate">{pageDisplayNames[page] || page}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Linked Unit */}
              <div>
                <Label className="text-xs">Linked Unit</Label>
                <Select
                  value={editUser?.linked_unit || "none"}
                  onValueChange={(v) => setEditUser({ ...editUser, linked_unit: v === "none" ? null : v })}
                >
                  <SelectTrigger className="input-dark h-9">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Unit Linked</SelectItem>
                    {units.map((unit) => (
                      <SelectItem key={unit.unit_id} value={unit.name}>{unit.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Response Permission - only show if user has a linked unit */}
              {editUser?.linked_unit && (
                <label className="flex items-center justify-between p-3 rounded bg-slate-800 cursor-pointer hover:bg-slate-700">
                  <div>
                    <span className="text-sm text-slate-300">Allow self-respond to calls</span>
                    <p className="text-xs text-slate-500">Unit can respond/unrespond themselves on Response page</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={editUser?.can_self_respond !== false}
                    onChange={(e) => setEditUser({ ...editUser, can_self_respond: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-800"
                  />
                </label>
              )}

              {/* Disable patrol unlock */}
              <label className="flex items-center justify-between p-3 rounded bg-slate-800 cursor-pointer hover:bg-slate-700">
                <div>
                  <span className="text-sm text-slate-300">Disable patrol unlock</span>
                  <p className="text-xs text-slate-500">When on, this user can't send Unlock to DroneMobile-linked patrol cars, even when logged into the car. Default: allowed.</p>
                </div>
                <input
                  type="checkbox"
                  checked={editUser?.patrol_unlock_disabled === true}
                  onChange={(e) => setEditUser({ ...editUser, patrol_unlock_disabled: e.target.checked })}
                  className="w-5 h-5 rounded border-slate-600 bg-slate-800"
                />
              </label>

              {/* Event Ops access */}
              <label className="flex items-center justify-between p-3 rounded bg-slate-800 cursor-pointer hover:bg-slate-700">
                <div>
                  <span className="text-sm text-slate-300">Event Ops — Dispatch access</span>
                  <p className="text-xs text-slate-500">Full Event Ops platform (posts, cars, map, audit)</p>
                </div>
                <input
                  type="checkbox"
                  checked={editUser?.event_ops_dispatch_access || false}
                  onChange={(e) => setEditUser({ ...editUser, event_ops_dispatch_access: e.target.checked })}
                  className="w-5 h-5 rounded border-slate-600 bg-slate-800"
                />
              </label>
              <label className="flex items-center justify-between p-3 rounded bg-slate-800 cursor-pointer hover:bg-slate-700">
                <div>
                  <span className="text-sm text-slate-300">Event Ops — Unit access</span>
                  <p className="text-xs text-slate-500">Unit-only live view (cars filtered to their linked unit)</p>
                </div>
                <input
                  type="checkbox"
                  checked={editUser?.event_ops_unit_access || false}
                  onChange={(e) => setEditUser({ ...editUser, event_ops_unit_access: e.target.checked })}
                  className="w-5 h-5 rounded border-slate-600 bg-slate-800"
                />
              </label>
              {editUser?.event_ops_unit_access && (
                <label className="flex items-center justify-between p-3 rounded bg-slate-800 cursor-pointer hover:bg-slate-700 ml-4">
                  <div>
                    <span className="text-sm text-slate-300">Unit can mark own car status</span>
                    <p className="text-xs text-slate-500">Allow en-route / check-in / open / clear from the unit view</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={editUser?.event_ops_unit_can_edit || false}
                    onChange={(e) => setEditUser({ ...editUser, event_ops_unit_can_edit: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-800"
                  />
                </label>
              )}

              {/* Notification Settings */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="w-4 h-4 text-blue-400" />
                  <Label className="text-xs font-semibold">Notification Preferences</Label>
                </div>
                <p className="text-xs text-slate-500 mb-3">Set which notifications this user receives. These settings sync with the user's phone.</p>
                
                {/* Master switch over the per-type toggles below. Separate from the
                    "Notifications Enabled" switch above, which is an absolute off. */}
                <div className={`flex items-center justify-between p-3 rounded mb-3 ${editUser?.notif_enabled === false ? 'bg-red-950/30 border border-red-800/50' : 'bg-green-950/30 border border-green-800/50'}`}>
                  <div className="flex items-center gap-2">
                    <Bell className={`w-4 h-4 ${editUser?.notif_enabled === false ? 'text-red-400' : 'text-green-400'}`} />
                    <span className={`text-sm font-medium ${editUser?.notif_enabled === false ? 'text-red-400' : 'text-green-400'}`}>
                      Master Switch{editUser?.notif_enabled === false ? ' — Off' : ' — On'}
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={editUser?.notif_enabled !== false}
                    onChange={(e) => setEditUser({ ...editUser, notif_enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-800"
                  />
                </div>

                {/* Delivery switch — the same field the Notifications Enabled panel
                    above sets, and the same toggle the user sees on their own
                    settings page. Mirrored here so it reads alongside the rest. */}
                <div className={`flex items-center justify-between p-3 rounded mb-3 ${editUser?.notif_all_enabled === false ? 'bg-red-950/30 border border-red-800/50' : 'bg-green-950/30 border border-green-800/50'}`}>
                  <div className="flex items-center gap-2">
                    <Bell className={`w-4 h-4 ${editUser?.notif_all_enabled === false ? 'text-red-400' : 'text-green-400'}`} />
                    <div>
                      <span className={`text-sm font-medium ${editUser?.notif_all_enabled === false ? 'text-red-400' : 'text-green-400'}`}>
                        Notifications Enabled{editUser?.notif_all_enabled === false ? ' — Off' : ' — On'}
                      </span>
                      <p className="text-[10px] text-slate-400">
                        Off silences everything, 10-85 and 10-13 included, without unsubscribing them.
                      </p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={editUser?.notif_all_enabled !== false}
                    onChange={(e) => setEditUser({ ...editUser, notif_all_enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-800"
                  />
                </div>

                {/* Allow user to edit their own notifications */}
                <label className="flex items-center justify-between p-2 rounded mb-3 bg-slate-800 cursor-pointer hover:bg-slate-700">
                  <span className="text-xs text-slate-300">Allow user to edit their notifications</span>
                  <input
                    type="checkbox"
                    checked={editUser?.notif_can_edit !== false}
                    onChange={(e) => setEditUser({ ...editUser, notif_can_edit: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                  />
                </label>
                
                {/* Individual toggles - only show if master is enabled */}
                {editUser?.notif_enabled !== false && (
                  <div className="space-y-1 p-2 bg-slate-800 rounded max-h-56 overflow-y-auto">
                    {[
                      { key: 'notif_normal_calls', label: 'Normal Calls' },
                      { key: 'notif_priority_calls', label: 'Priority Calls' },
                      { key: 'notif_priority_updates', label: 'Priority Call Updates' },
                      { key: 'notif_1085', label: '10-85 Calls', forced: true },
                      { key: 'notif_1013', label: '10-13 Calls', forced: true },
                      { key: 'notif_call_updates', label: 'Call Updates' },
                      { key: 'notif_patrol', label: 'Patrol Activity' },
                      { key: 'notif_perpetrator_updates', label: 'Perpetrator Updates' }
                    ].map(({ key, label, forced }) => (
                      <label key={key} className={`flex items-center space-x-2 p-1 rounded ${forced ? '' : 'cursor-pointer hover:bg-slate-700'}`}>
                        <input
                          type="checkbox"
                          checked={forced ? true : editUser?.[key] !== false}
                          onChange={(e) => { if (!forced) setEditUser({ ...editUser, [key]: e.target.checked }); }}
                          disabled={forced}
                          className={`w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 ${forced ? 'opacity-40 cursor-not-allowed' : ''}`}
                        />
                        <span className="text-xs">{label}</span>
                        {forced && (
                          <span className="text-[10px] text-slate-500 italic">
                            {editUser?.notif_all_enabled === false ? '(silenced above)' : '(always on)'}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Location Sharing Section */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <Label className="text-xs font-semibold">Location Sharing</Label>
                </div>
                <label className="flex items-center justify-between p-2 rounded bg-slate-800 cursor-pointer hover:bg-slate-700">
                  <div>
                    <span className="text-xs text-slate-300">Allow Share Location button</span>
                    <p className="text-[10px] text-slate-500">Show/hide the Share Location button on Response page</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={editUser?.allow_location_sharing !== false}
                    onChange={(e) => setEditUser({ ...editUser, allow_location_sharing: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                  />
                </label>
                <label className="flex items-center justify-between p-2 rounded bg-slate-800 cursor-pointer hover:bg-slate-700 mt-2">
                  <div>
                    <span className="text-xs text-slate-300">Allow Text Size Toggle</span>
                    <p className="text-[10px] text-slate-500">Show/hide the A (S/M/L/XL) text size button in sidebar</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={editUser?.allow_text_size_toggle !== false}
                    onChange={(e) => setEditUser({ ...editUser, allow_text_size_toggle: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                  />
                </label>
                <div className="p-2 rounded bg-slate-800 mt-2">
                  <span className="text-xs text-slate-300">Allowed Themes</span>
                  <p className="text-[10px] text-slate-500 mb-1">Which theme modes this user can access</p>
                  <div className="flex gap-3">
                    {["light", "dark", "tactical"].map((t) => (
                      <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(editUser?.allowed_themes || ["light", "tactical"]).includes(t)}
                          onChange={(e) => {
                            const current = editUser?.allowed_themes || ["light", "tactical"];
                            const updated = e.target.checked ? [...current, t] : current.filter(x => x !== t);
                            setEditUser({ ...editUser, allowed_themes: updated.length > 0 ? updated : ["light"] });
                          }}
                          className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800"
                        />
                        <span className="text-xs text-slate-300 capitalize">{t === 'tactical' ? 'Blue' : t}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <label className="flex items-center justify-between p-2 rounded bg-slate-800 cursor-pointer hover:bg-slate-700 mt-2">
                  <div>
                    <span className="text-xs text-slate-300">Enable Phone Widget</span>
                    <p className="text-[10px] text-slate-500">Show floating SIP phone dialer for outbound calls</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={editUser?.phone_widget_enabled || false}
                    onChange={(e) => setEditUser({ ...editUser, phone_widget_enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                  />
                </label>
                <label className="flex items-center justify-between p-2 rounded bg-slate-800 cursor-pointer hover:bg-slate-700 mt-2">
                  <div>
                    <span className="text-xs text-slate-300">Enable Callback Choice</span>
                    <p className="text-[10px] text-slate-500">Prompt to choose native dialer or dispatch phone when clicking callbacks</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={editUser?.phone_callback_enabled || false}
                    onChange={(e) => setEditUser({ ...editUser, phone_callback_enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                  />
                </label>
                <div className="mt-2 p-2 rounded bg-slate-800">
                  <span className="text-xs text-slate-300">Personal Phone Number</span>
                  <p className="text-[10px] text-slate-500 mb-1">For "Call My Phone" — rings this number then bridges to destination</p>
                  <input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={editUser?.phone_number || ""}
                    onChange={(e) => setEditUser({ ...editUser, phone_number: e.target.value })}
                    className="w-full mt-1 px-2 py-1.5 text-xs rounded bg-slate-900 border border-slate-700 text-slate-200 placeholder-slate-600"
                  />
                </div>

                {/* Every device ID this user has been seen on (audit). */}
                <UserDevicesSection userId={editUser?.user_id} />

                {/* WhatsApp Notifications */}
                <div className="border-t border-slate-700 pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-green-400" />
                    <Label className="text-xs font-semibold">WhatsApp Notifications</Label>
                  </div>
                  <label className="flex items-center justify-between p-2 rounded bg-slate-800 cursor-pointer hover:bg-slate-700">
                    <div>
                      <span className="text-xs text-slate-300">Enable WhatsApp Alerts</span>
                      <p className="text-[10px] text-slate-500">Receive live call alerts via WhatsApp</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={editUser?.whatsapp_enabled || false}
                      onChange={(e) => setEditUser({ ...editUser, whatsapp_enabled: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                    />
                  </label>
                  <label className="flex items-center justify-between p-2 rounded bg-slate-800 cursor-pointer hover:bg-slate-700 mt-1">
                    <div>
                      <span className="text-xs text-slate-300">85/13 Only</span>
                      <p className="text-[10px] text-slate-500">Receive WhatsApp only for All 85/13 alerts (even if above is off)</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={editUser?.whatsapp_8513_only || false}
                      onChange={(e) => setEditUser({ ...editUser, whatsapp_8513_only: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                    />
                  </label>
                  <div className="mt-2 p-2 rounded bg-slate-800">
                    <span className="text-xs text-slate-300">WhatsApp Phone Number</span>
                    <p className="text-[10px] text-slate-500 mb-1">E.164 format (e.g. +1234567890)</p>
                    <input
                      type="tel"
                      placeholder="+1234567890"
                      value={editUser?.whatsapp_number || ""}
                      onChange={(e) => setEditUser({ ...editUser, whatsapp_number: e.target.value })}
                      className="w-full mt-1 px-2 py-1.5 text-xs rounded bg-slate-900 border border-slate-700 text-slate-200 placeholder-slate-600"
                    />
                  </div>
                </div>
              </div>

              {/* Admin PIN Code Section */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <Label className="text-xs font-semibold">Security PIN</Label>
                </div>
                <p className="text-xs text-slate-500 mb-3">Require this user to enter a PIN code after signing in with Google. You control the PIN.</p>
                
                {/* Enable PIN Toggle */}
                <label className="flex items-center justify-between p-2 rounded mb-3 bg-slate-800 cursor-pointer hover:bg-slate-700">
                  <span className="text-xs text-slate-300">Require PIN after Google login</span>
                  <input
                    type="checkbox"
                    checked={editUser?.pin_required || false}
                    onChange={(e) => setEditUser({ ...editUser, pin_required: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                  />
                </label>
                
                {/* PIN Code Input */}
                {editUser?.pin_required && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">PIN Code (4-8 digits)</Label>
                      <Input
                        type="password"
                        value={editUser?.pin_code || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                          setEditUser({ ...editUser, pin_code: val });
                        }}
                        placeholder={editUser?.has_pin ? "Enter new PIN to change..." : "Enter PIN code..."}
                        className="input-dark h-9 font-mono tracking-widest"
                        maxLength={8}
                      />
                      <p className="text-[10px] text-slate-500 mt-1">
                        {editUser?.pin_code?.length || 0}/8 digits. 
                        {editUser?.has_pin && !editUser?.pin_code 
                          ? " PIN already set - leave blank to keep existing." 
                          : editUser?.pin_code?.length >= 4 
                            ? " ✓ New PIN will be saved." 
                            : " Min 4 digits required."}
                      </p>
                      {editUser?.has_pin && (
                        <p className="text-[10px] text-green-500 mt-0.5">✓ User has a PIN configured</p>
                      )}
                    </div>
                    
                    {/* PIN Expiry */}
                    <div>
                      <Label className="text-xs">PIN Valid For</Label>
                      <Select 
                        value={editUser?.pin_expiry_hours !== undefined ? editUser.pin_expiry_hours.toString() : "24"} 
                        onValueChange={(v) => setEditUser({ ...editUser, pin_expiry_hours: parseInt(v) })}
                      >
                        <SelectTrigger className="input-dark h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 hour</SelectItem>
                          <SelectItem value="4">4 hours</SelectItem>
                          <SelectItem value="8">8 hours</SelectItem>
                          <SelectItem value="12">12 hours</SelectItem>
                          <SelectItem value="24">24 hours</SelectItem>
                          <SelectItem value="48">48 hours</SelectItem>
                          <SelectItem value="168">1 week</SelectItem>
                          <SelectItem value="720">30 days</SelectItem>
                          <SelectItem value="0">Every login</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-slate-500 mt-1">
                        How often the user needs to re-enter the PIN. "Every login" means PIN required on every browser session.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Admin-Controlled Force PIN Section */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-purple-400" />
                  <Label className="text-xs font-semibold">Admin-Controlled PIN</Label>
                </div>
                <p className="text-xs text-slate-500 mb-3">Require this user to enter a PIN anytime you want. Toggle this on to force a PIN popup regardless of Google login status.</p>
                
                {/* Enable Admin PIN Toggle */}
                <label className="flex items-center justify-between p-2 rounded mb-3 bg-slate-800 cursor-pointer hover:bg-slate-700">
                  <span className="text-xs text-slate-300">Force PIN popup (admin-controlled)</span>
                  <input
                    type="checkbox"
                    checked={editUser?.admin_pin_enabled || false}
                    onChange={(e) => setEditUser({ ...editUser, admin_pin_enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                  />
                </label>
                
                {/* Admin PIN Code Input */}
                {editUser?.admin_pin_enabled && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Admin PIN Code (4-8 digits)</Label>
                      <Input
                        type="password"
                        value={editUser?.admin_pin_code || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                          setEditUser({ ...editUser, admin_pin_code: val });
                        }}
                        placeholder={editUser?.has_admin_pin ? "Enter new PIN to change..." : "Enter PIN code..."}
                        className="input-dark h-9 font-mono tracking-widest"
                        maxLength={8}
                      />
                      <p className="text-[10px] text-slate-500 mt-1">
                        {editUser?.admin_pin_code?.length || 0}/8 digits. 
                        {editUser?.has_admin_pin && !editUser?.admin_pin_code 
                          ? " PIN already set - leave blank to keep existing." 
                          : editUser?.admin_pin_code?.length >= 4 
                            ? " ✓ New PIN will be saved." 
                            : " Min 4 digits required."}
                      </p>
                      {editUser?.has_admin_pin && (
                        <p className="text-[10px] text-purple-400 mt-0.5">✓ Admin PIN is configured</p>
                      )}
                    </div>
                    
                    {/* Admin PIN Message */}
                    <div>
                      <Label className="text-xs">Message to show user (optional)</Label>
                      <Input
                        type="text"
                        value={editUser?.admin_pin_message || ""}
                        onChange={(e) => setEditUser({ ...editUser, admin_pin_message: e.target.value })}
                        placeholder="e.g., Please contact supervisor for PIN"
                        className="input-dark h-9"
                      />
                      <p className="text-[10px] text-slate-500 mt-1">
                        Optional message displayed on the PIN popup.
                      </p>
                    </div>
                    
                    {/* Reset verification button */}
                    {editUser?.has_admin_pin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs bg-purple-900/30 border-purple-700 hover:bg-purple-900/50"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/users/${editUser.user_id}/reset-admin-pin-verification`, {}, { withCredentials: true });
                            toast.success("Admin PIN verification reset - user will see PIN popup");
                          } catch (error) {
                            toast.error("Failed to reset PIN verification");
                          }
                        }}
                      >
                        Force PIN Re-entry Now
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* App Lock Settings */}
              <div className="p-3 rounded-lg bg-amber-900/20 border border-amber-800/50 space-y-3">
                <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider">App Lock</h4>
                <p className="text-[10px] text-slate-400">
                  Lock the app after inactivity. User must enter PIN to continue using the app.
                </p>

                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-slate-300 flex-1">Enable App Lock</span>
                  <input
                    type="checkbox"
                    checked={editUser?.app_lock_enabled || false}
                    onChange={(e) => setEditUser({ ...editUser, app_lock_enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                  />
                </label>

                {editUser?.app_lock_enabled && (
                  <div className="space-y-3">
                    {/* App Lock PIN */}
                    <div>
                      <Label className="text-xs">App Lock PIN (4-8 digits)</Label>
                      <Input
                        type="password"
                        value={editUser?.app_lock_pin || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                          setEditUser({ ...editUser, app_lock_pin: val });
                        }}
                        placeholder={editUser?.has_app_lock_pin ? "Enter new PIN to change..." : "Enter PIN code..."}
                        className="input-dark h-9 font-mono tracking-widest"
                        maxLength={8}
                      />
                      <p className="text-[10px] text-slate-500 mt-1">
                        {editUser?.app_lock_pin?.length || 0}/8 digits.
                        {editUser?.has_app_lock_pin && !editUser?.app_lock_pin
                          ? " PIN already set - leave blank to keep existing."
                          : editUser?.app_lock_pin?.length >= 4
                            ? " New PIN will be saved."
                            : " Min 4 digits required."}
                      </p>
                      {editUser?.has_app_lock_pin && (
                        <p className="text-[10px] text-amber-400 mt-0.5">App Lock PIN is configured</p>
                      )}
                    </div>

                    {/* Timeout */}
                    <div>
                      <Label className="text-xs">Auto-lock after inactivity</Label>
                      <Select
                        value={editUser?.app_lock_timeout_minutes?.toString() || "5"}
                        onValueChange={(val) => setEditUser({ ...editUser, app_lock_timeout_minutes: parseInt(val) })}
                      >
                        <SelectTrigger className="input-dark h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="0">Never (manual only)</SelectItem>
                          <SelectItem value="1">1 minute</SelectItem>
                          <SelectItem value="5">5 minutes</SelectItem>
                          <SelectItem value="15">15 minutes</SelectItem>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">1 hour</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Scope */}
                    <div>
                      <Label className="text-xs">Lock Scope</Label>
                      <Select
                        value={editUser?.app_lock_scope || "app"}
                        onValueChange={(val) => setEditUser({ ...editUser, app_lock_scope: val })}
                      >
                        <SelectTrigger className="input-dark h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="app">Entire App</SelectItem>
                          <SelectItem value="pages">Specific Pages</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-slate-500 mt-1">
                        {editUser?.app_lock_scope === "pages"
                          ? "Only selected pages will require PIN after timeout"
                          : "Entire app will lock after timeout"}
                      </p>
                    </div>

                    {/* Pages selection (if scope is "pages") */}
                    {editUser?.app_lock_scope === "pages" && (
                      <div>
                        <Label className="text-xs">Pages Requiring Lock</Label>
                        <div className="grid grid-cols-2 gap-1 mt-2 max-h-48 overflow-y-auto">
                          {availablePages.map(page => (
                            <label key={page} className="flex items-center gap-2 text-xs cursor-pointer p-1 hover:bg-slate-800 rounded">
                              <input
                                type="checkbox"
                                checked={(editUser?.app_lock_pages || []).includes(page)}
                                onChange={(e) => {
                                  const currentPages = editUser?.app_lock_pages || [];
                                  if (e.target.checked) {
                                    setEditUser({ ...editUser, app_lock_pages: [...currentPages, page] });
                                  } else {
                                    setEditUser({ ...editUser, app_lock_pages: currentPages.filter(p => p !== page) });
                                  }
                                }}
                                className="w-3 h-3 rounded border-slate-600 bg-slate-800"
                              />
                              <span className="text-slate-300">{pageDisplayNames[page] || page}</span>
                            </label>
                          ))}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">
                          Selected: {(editUser?.app_lock_pages || []).length} pages
                        </p>
                      </div>
                    )}

                    {/* Reset app lock button */}
                    {editUser?.has_app_lock_pin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs bg-amber-900/30 border-amber-700 hover:bg-amber-900/50"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/users/${editUser.user_id}/reset-app-lock`, {}, { withCredentials: true });
                            toast.success("App lock reset - user will need to re-enter PIN");
                          } catch (error) {
                            toast.error("Failed to reset app lock");
                          }
                        }}
                      >
                        Force Re-entry (Reset Lock)
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Session Management */}
              <div className="p-3 rounded-lg bg-red-900/20 border border-red-800/50 space-y-3">
                <h4 className="text-xs font-semibold text-red-400 uppercase tracking-wider">Session Management</h4>
                <p className="text-[10px] text-slate-400">
                  Force the user to log out from all devices. They will need to sign in again with Google.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs bg-red-900/30 border-red-700 hover:bg-red-900/50 text-red-300"
                  onClick={async () => {
                    if (!confirm(`Force logout ${editUser?.name || editUser?.email}? They will need to sign in again with Google.`)) return;
                    try {
                      const response = await axios.post(`${API}/users/${editUser.user_id}/force-logout`, {}, { withCredentials: true });
                      toast.success(response.data.message);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to force logout");
                    }
                  }}
                >
                  Force Logout (Sign Out All Devices)
                </Button>

                <div className="border-t border-red-800/30 pt-3 mt-3">
                  <p className="text-[10px] text-slate-400 mb-2">
                    Clear push notification subscriptions. User will need to re-enable notifications in their settings.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs bg-orange-900/30 border-orange-700 hover:bg-orange-900/50 text-orange-300"
                    onClick={async () => {
                      if (!confirm(`Clear push subscriptions for ${editUser?.name || editUser?.email}? They will need to re-subscribe to notifications.`)) return;
                      try {
                        const response = await axios.post(`${API}/users/${editUser.user_id}/clear-push-subscriptions`, {}, { withCredentials: true });
                        toast.success(response.data.message);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to clear subscriptions");
                      }
                    }}
                  >
                    Clear Push Notifications
                  </Button>
                </div>
              </div>
            </div>
          </ScrollArea>

          {/* Fixed Footer */}
          <div className="flex gap-2 pt-4 border-t border-slate-700 flex-shrink-0">
            <Button variant="outline" className="flex-1" onClick={() => setShowEditUserDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser} className="flex-1 btn-primary">
              <Save className="w-4 h-4 mr-1" />Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Type Dialog */}
      <Dialog open={showEditTypeDialog} onOpenChange={setShowEditTypeDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider">Edit Incident Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Type Name</Label>
              <Input
                value={editType.name || ""}
                onChange={(e) => setEditType({ ...editType, name: e.target.value })}
                className="input-dark"
              />
            </div>
            <Button onClick={handleUpdateType} className="w-full btn-primary">
              <Save className="w-4 h-4 mr-1" />Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Car Dialog */}
      <Dialog open={showEditCarDialog} onOpenChange={setShowEditCarDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider">Edit Patrol Car</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Car Name</Label>
              <Input
                value={editCar?.name || ""}
                onChange={(e) => setEditCar({ ...editCar, name: e.target.value })}
                className="input-dark"
              />
            </div>
            <div>
              <Label>Linked DroneMobile Vehicle</Label>
              <Select
                value={editCar?.drone_device_key || "none"}
                onValueChange={(v) => {
                  if (v === "none") {
                    setEditCar({ ...editCar, drone_device_key: null, drone_vehicle_name: null });
                  } else {
                    const veh = linkDroneVehicles.find((x) => x.device_key === v);
                    setEditCar({ ...editCar, drone_device_key: v, drone_vehicle_name: veh?.name || null });
                  }
                }}
              >
                <SelectTrigger className="input-dark">
                  <SelectValue placeholder="Not linked" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not linked</SelectItem>
                  {linkDroneVehicles.map((v) => (
                    <SelectItem key={v.device_key} value={v.device_key}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editCar?.drone_device_key &&
                !linkDroneVehicles.some((x) => x.device_key === editCar.drone_device_key) && (
                <p className="text-xs text-amber-400 mt-1">
                  Currently linked: {editCar?.drone_vehicle_name || editCar?.drone_device_key}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Links this car to a DroneMobile vehicle so the unit logged in can remotely unlock it.
              </p>
            </div>
            <div>
              <Label>Linked Geotab Vehicle</Label>
              <Select
                value={editCar?.geotab_device_id || "none"}
                onValueChange={(v) => {
                  if (v === "none") {
                    setEditCar({ ...editCar, geotab_device_id: null, geotab_device_name: null });
                  } else {
                    const dev = linkGeotabDevices.find((x) => x.device_id === v);
                    setEditCar({ ...editCar, geotab_device_id: v, geotab_device_name: dev?.name || null });
                  }
                }}
              >
                <SelectTrigger className="input-dark">
                  <SelectValue placeholder="Not linked" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not linked</SelectItem>
                  {linkGeotabDevices.map((d) => (
                    <SelectItem key={d.device_id} value={d.device_id}>
                      {d.name}{d.license_plate ? ` · ${d.license_plate}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editCar?.geotab_device_id &&
                !linkGeotabDevices.some((x) => x.device_id === editCar.geotab_device_id) && (
                <p className="text-xs text-amber-400 mt-1">
                  Currently linked: {editCar?.geotab_device_name || editCar?.geotab_device_id}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Links this car to its MyGeotab vehicle for live location, fuel level and driver history on the Fleet tab.
              </p>
            </div>
            <Button onClick={handleUpdatePatrolCar} className="w-full btn-primary">
              <Save className="w-4 h-4 mr-1" />Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Group Dialog */}
      <Dialog open={showNewGroupDialog} onOpenChange={setShowNewGroupDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider">New Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Group Name</Label>
              <Input
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                className="input-dark h-9"
                placeholder="e.g. Night Shift"
              />
            </div>
            <div>
              <Label className="text-xs mb-2 block">Members</Label>
              <ScrollArea className="h-60 border border-slate-700 rounded p-2">
                {users.map((u) => (
                  <label key={u.user_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newGroup.members.includes(u.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewGroup({ ...newGroup, members: [...newGroup.members, u.user_id] });
                        } else {
                          setNewGroup({ ...newGroup, members: newGroup.members.filter(id => id !== u.user_id) });
                        }
                      }}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                    />
                    <span className="text-sm text-slate-300">{u.name}</span>
                    <span className="text-xs text-slate-500">({u.email})</span>
                  </label>
                ))}
              </ScrollArea>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewGroupDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateGroup} disabled={!newGroup.name.trim()} className="btn-primary">Create Group</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={showEditGroupDialog} onOpenChange={setShowEditGroupDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider">Edit Group</DialogTitle>
          </DialogHeader>
          {editGroup && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Group Name</Label>
                <Input
                  value={editGroup.name}
                  onChange={(e) => setEditGroup({ ...editGroup, name: e.target.value })}
                  className="input-dark h-9"
                />
              </div>
              <div>
                <Label className="text-xs mb-2 block">Members ({editGroup.members?.length || 0})</Label>
                <ScrollArea className="h-60 border border-slate-700 rounded p-2">
                  {users.map((u) => (
                    <label key={u.user_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(editGroup.members || []).includes(u.user_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditGroup({ ...editGroup, members: [...(editGroup.members || []), u.user_id] });
                          } else {
                            setEditGroup({ ...editGroup, members: (editGroup.members || []).filter(id => id !== u.user_id) });
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                      />
                      <span className="text-sm text-slate-300">{u.name}</span>
                      <span className="text-xs text-slate-500">({u.email})</span>
                    </label>
                  ))}
                </ScrollArea>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEditGroupDialog(false)}>Cancel</Button>
                <Button onClick={handleUpdateGroup} disabled={!editGroup.name.trim()} className="btn-primary">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CSV export viewer (Settings → Data → Exports) */}
      <ExportModal
        open={exportShow}
        onOpenChange={setExportShow}
        csvData={exportCsv}
        filename={exportFilename}
        downloadUrl={exportDownloadUrl}
      />
    </div>
  );
}
